// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
export type Account = {
  homePage: string;
  name: string;
};

export type ActionableActionbarProps = {
  actionableName: string;
  actionableSet: ActionableSetEntry[];
  bookmarked: boolean;
  canAccessCourse: boolean;
  contentMetadata: ContentMetadata_ACTIONABLE;
  freeActionableActive: boolean;
  language: Language;
  showCertificate: boolean;
  userCustomerId: number | null;
  userPortalCanSubscribe: boolean;
  userPortalCorporateId: number | null;
  userPortalNeedsPrivacyPolicy: boolean;
  userPortalSmb: boolean;
  userPortalUsername: string | null;
};

export type ActionableActivityReport = {
  actionable: ContentMetadata_ACTIONABLE;
  customer: CustomerReportView | null;
  timeStamp: DateTime<true>;
};

export type ActionableAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean;
  languages: Language[];
  query: string;
};

export type ActionableBulkLoadForm = {
  actionableIds: bigint[];
  corporateId: number | null;
  includeInactive: boolean;
};

export type ActionableEditForm = {
  active: boolean;
  channelIds: bigint[];
  coverFile: FormFile | null;
  ctaVideoUrl: string | null;
  dataIds: number[];
  description: string;
  language: Language;
  linkToActionableId: bigint | null;
  overwriteCourse: boolean;
  peerInsightFeedbackForm: ActionableEditFormPeerFeedbackAnswer[];
  proTalkAboutText: string | null;
  proTalkDateTime: DateTime<true> | null;
  proTalkExpertName: string | null;
  proTalkExpertPortraitFile: FormFile | null;
  proTalkExpertSkills: string[];
  proTalkTitle: string | null;
  proTalkUrl: string | null;
  proTalkVideoUrl: string | null;
  suggestedAnswers: SuggestedAnswerView[];
  title: string;
  unlinkActionable: boolean;
  xapiCourse: FormFile | null;
};

export type ActionableEditFormPeerFeedbackAnswer = {
  answer: string;
  question: PeerInsightQuestionView;
};

export type ActionableEditResult = { kind: "SUCCESS"; data: ActionableEditView } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export type ActionableEditView = {
  actionableId: bigint;
  actionableSet: ActionableSetEntry[];
  active: boolean;
  channels: ChannelMetadata[];
  courseUploaded: boolean;
  coverUri: string;
  ctaVideoUrl: string | null;
  description: string;
  language: Language;
  name: string;
  peerFeedbackDashboardUnlocked: boolean;
  peerFeedbackDummyQuestions: PeerInsightQuestionView[];
  proTalkAboutText: string | null;
  proTalkDateTime: DateTime<true> | null;
  proTalkExpertName: string | null;
  proTalkExpertPortraitUri: string | null;
  proTalkExpertSkills: string[];
  proTalkTitle: string | null;
  proTalkUrl: string | null;
  proTalkVideoUrl: string | null;
  suggestedAnswerQuestions: PeerInsightQuestionView[];
  suggestedAnswers: SuggestedAnswerView[];
  summaries: ContentMetadata_SUMMARY[];
  title: string;
};

export type ActionableFiltersForm = {
  languageFormFilter: Language[];
};

export type ContentId_ACTIONABLE = {
  contentType: "ACTIONABLE";
  id: bigint;
};

export type ActionableIdsList = {
  actionableIds: bigint[];
};

export type ActionableListPortletEditView = {
  actionables: ContentMetadata_ACTIONABLE[];
  portlet: PortletEditView;
};

export type ActionableListPortletForm = {
  actionableIds: bigint[];
  portletForm: PortletForm;
};

export type ActionableListPortletView = {
  items: ContentMetadata_ACTIONABLE[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type ContentMetadata_ACTIONABLE = {
  actionableId: bigint;
  actionableSetId: bigint | null;
  activatedAt: DateTime<true> | null;
  active: boolean;
  common: ContentMetadataCommon;
  contentId: ContentId_ACTIONABLE;
  contentType: "ACTIONABLE";
  coverUri: string;
  description: string;
  language: Language;
  modifiedAt: DateTime<true>;
  name: string;
  title: string;
};

export type ActionableMetadataArray = {
  items: ContentMetadata_ACTIONABLE[];
};

export type ActionableMiniView = {
  actionableId: bigint;
  coverUri: string;
  name: string;
  title: string;
};

export type ActionableReflectSectionProperties = {
  actionableId: bigint;
  actionableLanguage: Language;
  actionableTitle: string;
  certificateId: string;
  customerId: number;
  experienceFeedbackScore: number | null;
  formSteps: PeerInsightFormStep[];
  initialQuestionId: bigint;
  peerInsightProvidedAt: DateTime<true> | null;
  questions: PeerInsightQuestionView[];
  socialSharing: boolean;
  strictPrivacy: boolean;
};

export type ActionableReflectSectionProps = {
  actionableId: bigint;
  freeActionableActive: boolean;
  isMapp: boolean;
  language: Language;
};

export type ActionableSetEntry = {
  actionableId: bigint;
  active: boolean;
  language: Language;
  name: string;
  title: string;
};

export type ActionableView = {
  actionableId: bigint;
  actionableSet: ActionableSetEntry[];
  actionableSetId: bigint | null;
  activatedAt: DateTime<true> | null;
  active: boolean;
  coverUri: string;
  ctaVideoUrl: string | null;
  description: string;
  language: Language;
  modifiedAt: DateTime<true>;
  name: string;
  proTalkAboutText: string | null;
  proTalkDateTime: DateTime<true> | null;
  proTalkExpertName: string | null;
  proTalkExpertPortraitUri: string | null;
  proTalkExpertSkills: string[];
  proTalkTitle: string | null;
  proTalkUrl: string | null;
  proTalkVideoUrl: string | null;
  summaryIds: number[];
  tags: Tag[];
  title: string;
};

export type Activity = {
  definition: ActivityDefinition;
  id: string;
  objectType: string;
};

export type ActivityDefinition = {
  description: { additionalProperties: Record<string, string> };
  moreInfo: string;
  name: { additionalProperties: Record<string, string> };
  type: string;
};

export type Actor = {
  account: Account | null;
  mbox: string | null;
  name: string;
  objectType: string;
  openid: string | null;
};

export type Address = {
  address1: string;
  address2: string | null;
  country: string;
  fullname: string;
  state: string | null;
  town: string;
  zip: string;
};

export type AdminSidebarWidgetProps = {
  navigationItems: AdminToolLinkGroup[];
};

export type AdminToolLink = {
  description: string | null;
  hidden: boolean;
  ico: string | null;
  linkText: string | null;
  tabName: string | null;
  tabTitle: string | null;
  targetType: AdminToolLinkTargetType;
  url: string | null;
};

export type AdminToolLinkGroup = {
  ico: string | null;
  links: AdminToolLink[];
  name: string | null;
};

export namespace AdminToolLinkTargetType {
  const values = ["SELF", "BLANK", "ROUTER"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AdminToolLinkTargetType = AdminToolLinkTargetType.Impl;

export type AiBriefAiResponse = {
  markdown: string;
};

export type AiSolutionsFormProps = {
  ctaText: string;
};

export type AlipayPaymentView = {
  outTradeNo: string | null;
  tradeNo: string | null;
  transactionId: number;
};

export type AllAbstractsListWrapper = {
  activatedAt: { year: number; month: number; day: number } | null;
  audio: boolean;
  audioActivatedAt: { year: number; month: number; day: number } | null;
  dataId: number;
  deactivatedAt: { year: number; month: number; day: number } | null;
  enteredAt: { year: number; month: number; day: number } | null;
  epub: boolean;
  imprint: string;
  language: Language;
  managingEditor: string;
  modifiedAt: { year: number; month: number; day: number } | null;
  pdf: boolean;
  publisher: string;
  summaryDocumentType: string;
  summarySourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  title: string;
};

export type AllAbstractsListWrapperArray = {
  allAbstractsListWrapper: AllAbstractsListWrapper[];
};

export type AndroidPaymentView = {
  appStoreProductId: string | null;
  autoRenewing: boolean;
  countryCode: string | null;
  orderId: string | null;
  paymentState: number;
  purchaseState: number;
  testPurchase: boolean;
  transactionId: number;
};

export type AnnotationListPortletView = {
  items: AnnotationView[];
  metadata: PortletMetadata;
  page: number;
  pageSize: number;
  totalCount: number;
  viewAllUri: string | null;
};

export type AnnotationRequest = {
  endOffset: number;
  section: AnnotationSection;
  startOffset: number;
  textHighlighted: string;
};

export namespace AnnotationSection {
  const values = [
    "ABSTRACT_TEXT",
    "EMAIL_SENTENCE",
    "EMAIL_SENTENCE_WEEKLY",
    "RATING",
    "KEY_QUALITIES",
    "GENRE_AND_PERIOD",
    "TAKEAWAYS",
    "RECOMMENDATION",
    "REVIEW_TITLE",
    "REVIEW_LEAD",
    "REVIEW_TEXT",
    "ABOUT_TEXT",
    "HISTORICAL_BACKGROUND",
    "ABOUT_AUTHOR",
    "WHAT_YOU_WILL_LEARN",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AnnotationSection = AnnotationSection.Impl;

export type AnnotationView = {
  annotationId: bigint;
  customerId: number;
  dataId: number;
  endOffset: number;
  modifiedAt: DateTime<true>;
  modifiedAtPrettyFormatted: string;
  section: AnnotationSection;
  startOffset: number;
  textHighlighted: string;
  title: string;
};

export namespace ApprovalStatus {
  const values = ["APPROVED", "REJECTED", "IGNORED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ApprovalStatus = ApprovalStatus.Impl;

export namespace AreaOfExpertise {
  const values = [
    "ACCOUNTING_AND_FINANCE",
    "ADMINISTRATION",
    "BUSINESS_DEVELOPMENT",
    "COMMUNICATIONS",
    "CUSTOMER_SERVICE",
    "HUMAN_RESOURCES",
    "INFORMATION_TECHNOLOGY",
    "LEGAL",
    "MARKETING",
    "OPERATIONS",
    "PRODUCTION",
    "SALES",
    "SUPPLY_MANAGEMENT",
    "OTHER",
    "UNDEFINED",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AreaOfExpertise = AreaOfExpertise.Impl;

export namespace AskGetabstractAnalyticsEventVariant {
  const values = ["default_variant", "qa_triggered_from_search", "qa_demo"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AskGetabstractAnalyticsEventVariant = AskGetabstractAnalyticsEventVariant.Impl;

export type AskGetabstractAnalyticsFeedbackEventForm = {
  isPositive: boolean;
  questionUuid: string;
};

export type AskGetabstractAnswer = {
  answer: string;
  contextDocs: AskGetabstractAnswerContextDoc[];
  detectedLanguageLabel: string | null;
  previousQuestionUuid: string | null;
  question: string;
  questionUuid: string;
  relatedActionables: ActionableMiniView[];
  relatedQuestions: string[];
  status: AskGetabstractAnswerStatus;
};

export type AskGetabstractAnswerContextDoc = {
  bookmarked: boolean;
  dataId: number;
  rating: number | null;
  referenceNumber: number;
  snippet: string;
  sourceTypeString: string;
  title: string;
  urls: {
    cover: string;
    list: string;
    reference: string;
  };
};

export namespace AskGetabstractAnswerStatus {
  const values = ["PROCESSING", "FINISHED", "NO_DOCS_FROM_VECTOR_DB", "NO_DOCS_AFTER_FILTER", "NO_REFERENCE_IN_ANSWER", "GENERAL_ERROR", "TIMEOUT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AskGetabstractAnswerStatus = AskGetabstractAnswerStatus.Impl;

export type AskGetabstractPortletView = {
  items: string[];
  metadata: PortletMetadata;
};

export type AskGetabstractProperties = {
  isTrendingQuestion: boolean;
  language: Language;
  mappWebView: boolean;
  question: string | null;
  questionUuid: string | null;
  showAiDataProtectionDisclaimer: boolean;
};

export type AskGetabstractQuestion = {
  analyticsEventVariant: AskGetabstractAnalyticsEventVariant;
  blacklistQuestions: string[];
  previousQuestionUuid: string | null;
  question: string;
  questionUuid: string;
  relatedQuestionStep: number;
  trendingQuestion: boolean;
};

export type AskGetabstractQuestionAnswers = {
  current: AskGetabstractAnswer;
  history: AskGetabstractAnswer[];
};

export type AspCertificateListView = {
  expirationDate: { year: number; month: number; day: number } | null;
  signingKey: SigningKey;
  validFromDate: { year: number; month: number; day: number } | null;
};

export type AspCertificateListViewList = {
  aspCertificates: AspCertificateListView[];
};

export namespace AspCertificateType {
  const values = ["PRIMARY", "SECONDARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AspCertificateType = AspCertificateType.Impl;

export type AspCertificateView = {
  commonName: string | null;
  enabled: boolean;
  expirationDate: { year: number; month: number; day: number } | null;
  publicKey: string;
  serialNumber: string | null;
  type: AspCertificateType | null;
  validFromDate: { year: number; month: number; day: number } | null;
};

export type AssignedSkillForm = {
  channelId: bigint;
  competency: string | null;
  score: number;
  template: string | null;
  title: string;
  value: string;
};

export type AssignedSkillUnparsedForm = {
  channelId: string;
  competency: string;
  score: string;
  template: string;
  title: string;
  value: string;
};

export type AssignedSkillValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: AssignedSkillForm };

export type Assignee = {
  assigneeId: number | null;
  email: string;
};

export type AuthConnectorForm = {
  certificate: string;
  clientId: string;
  clientSecret: string;
  credentialProfile: string | null;
  grantType: "CLIENT_CREDENTIALS" | "PASSWORD";
  privateKey: string;
  scope: string;
  type: "BASIC" | "OAUTH2" | "JWT" | "GOOGLE_JWT";
};

export namespace AuthenticationType {
  const values = ["BASIC", "OAUTH2", "JWT", "GOOGLE_JWT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type AuthenticationType = AuthenticationType.Impl;

export type AuthorAdminListFilters = {
  authorId: string | null;
  channels: string | null;
  firstName: string | null;
  gender: string | null;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorAdminListView = {
  authorId: bigint;
  channels: string[];
  firstName: string;
  gender: Gender;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorContextInfo = {
  linkedinUrl: string | null;
  professionalBackground: string | null;
  twitterUrl: string | null;
};

export type AuthorEditView = {
  authorId: bigint | null;
  firstName: string;
  gender: Gender;
  ignoringWarning: boolean;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorExtractionResult = {
  authors: {
    firstName: string;
    lastName: string;
    middleName: string;
  }[];
};

export type AuthorForm = {
  firstName: string | null;
  isEditorialTeam?: boolean;
  lastName: string;
  middleName?: string;
};

export type AuthorListView = {
  authorId: number;
  firstName: string | null;
  gender: Gender | null;
  lastName: string | null;
  middleName: string | null;
};

export type AuthorListViewList = {
  authors: AuthorListView[];
};

export type AuthorRating = {
  evidence: string[];
  expertiseAreas: string[];
  infoFound: boolean;
  isVerifiedPerson: boolean;
  linkedinUrl: string | null;
  professionalBackground: string | null;
  profileUrl: string | null;
  redFlags: string[];
  scores: {
    domainAuthority: {
      reasoning: string;
      score: number;
      summary: string;
    };
    professionalBackground: {
      reasoning: string;
      score: number;
      summary: string;
    };
    publishedWork: {
      reasoning: string;
      score: number;
      summary: string;
    };
  };
  twitterUrl: string | null;
};

export type AuthorRatingDetailForm = {
  author: AuthorForm;
  contentProductionAuthorId?: bigint;
  domainAuthorityScore?: number;
  domainAuthoritySummary?: string;
  infoFound: boolean;
  isVerifiedPerson: boolean;
  linkedinUrl: string | null;
  professionalBackground: string;
  professionalBackgroundScore?: number;
  professionalBackgroundSummary?: string;
  profileUrl: string | null;
  publishedWorkScore?: number;
  publishedWorkSummary?: string;
  rating: bigint;
  twitterUrl: string | null;
};

export type AuthorRatingDetailUnparsedForm = {
  author: AuthorUnparsedForm;
  contentProductionAuthorId: string;
  domainAuthorityScore: string;
  domainAuthoritySummary: string;
  infoFound: boolean;
  isVerifiedPerson: boolean;
  linkedinUrl: string;
  professionalBackground: string;
  professionalBackgroundScore: string;
  professionalBackgroundSummary: string;
  profileUrl: string;
  publishedWorkScore: string;
  publishedWorkSummary: string;
  rating: string;
  twitterUrl: string;
};

export type AuthorRatingDetailValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: AuthorRatingDetailForm };

export type AuthorRatingOutput = {
  credibilityRating: number;
  evidence: string[];
  expertiseAreas: string[];
  infoFound: boolean;
  isVerifiedPerson: boolean;
  linkedinUrl: string | null;
  professionalBackground: string | null;
  profileUrl: string | null;
  redFlags: string[];
  scores: {
    domainAuthority: ScoreWithReasoning;
    professionalBackground: ScoreWithReasoning;
    publishedWork: ScoreWithReasoning;
  };
  totalScore: number | null;
  twitterUrl: string | null;
};

export type AuthorTranslationForm = {
  biography: string | null;
  firstName?: string;
  lastName?: string;
  middleName?: string;
};

export type AuthorTranslationUnparsedForm = {
  biography: string;
  firstName: string;
  lastName: string;
  middleName: string;
};

export type AuthorTranslationValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: AuthorTranslationForm };

export type AuthorUnparsedForm = {
  firstName: string;
  isEditorialTeam: boolean;
  lastName: string;
  middleName: string;
};

export type AuthorValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: AuthorForm };

export type B2BStatsReportPageProps = {
  b2bReportForm: NewB2BReportForm;
  corporateId: number;
  hasExtendedAccess: boolean;
  reportTypes: DropdownEntry[];
};

export type B2BStatsReportSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; reportId: number };

export type B2CRenewalEmails = {
  name: string;
  sendDate: DateTime<true>;
  type: string;
};

export type B2CSubscription = {
  actionStatus: string | null;
  customerId: number;
  editSubscriptionUrl: string;
  endDate: { year: number; month: number; day: number };
  giftSubscription: GiftSubscription | null;
  isCredited: boolean;
  isExpired: boolean;
  isLoss: boolean;
  isMobileSubscription: boolean;
  isPaid: boolean;
  isTeamSubscription: boolean;
  manualRenewalUrl: string;
  numberOfTransactions: number;
  product: string;
  productId: number;
  quantity: number;
  randomKey: string | null;
  startDate: { year: number; month: number; day: number };
  studentVerification: StudentVerificationView | null;
  subscriptionId: number;
  subscriptionPayment: SubscriptionPayment;
  subscriptionStatus: SubscriptionStatus;
  teamSubscription: TeamSubscription | null;
};

export type BankConnectionDetails = {
  abaNumber: string | null;
  accountNumber: string | null;
  bankConnectionType: "IBAN" | "ABA" | "SWIFTBIC" | "CHEQUE";
  bankname: string;
  iban: string | null;
  swiftBic: string | null;
};

export type BookChapterAtoms = {
  atoms: {
    claim: string;
    quote: string | null;
    rationale: string | null;
    support: string[];
  }[];
  coreProposition: string;
};

export type BookMetadata = {
  authors: {
    firstName: string;
    lastName: string;
    middleName: string;
  }[];
  publicationDate: string | null;
  sourceTitle: string | null;
  subtitle: string | null;
  title: string;
};

export type BookSynthesis = {
  coreThesis: string;
  themes: {
    chapterCount: number;
    prose: string;
    quotes: string[];
    title: string;
  }[];
};

export type BookmarkResponse = {
  bookmarked: boolean;
};

export type BreadCrumbItem = {
  pathName: string;
  url: string;
};

export type BuyBookProps = {
  buyBookTitle: string | null;
  customBuyBookUrl: string | null;
  mainTitle: string;
};

export type BuyBookUrlObject = {
  amazonDirect: string;
  type: BuyBookUrlType;
  url: string;
};

export namespace BuyBookUrlType {
  const values = ["POPUP", "NORMAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type BuyBookUrlType = BuyBookUrlType.Impl;

export type BytesData = {
  data: ReadonlyUint8Array;
};

export type CandidateOfferGroupView = {
  candidates: CandidateOfferListView[];
  convertedDisplayFee: string | null;
};

export type CandidateOfferListView = {
  customerId: number;
  fullName: string;
  offerState: OfferState;
};

export type CandidateQuery = {
  amount: number;
  currencyCode: string;
  nameFilter: string | null;
  productionIdentifier: ProductionIdentifier | null;
  userCustomerIds: number[];
};

export type CandidateRequest = {
  customerId: number;
  fee: CandidateTagViewFee | null;
};

export type CandidateSearchQuery = {
  amount: number | null;
  currencyCode: string | null;
  productionIdentifier: ProductionIdentifier | null;
};

export type CandidateSearchResultListView = {
  comments: string | null;
  customerId: number;
  fullName: string;
  openJobs: OpenJobOverviewList;
  state: UserState;
  tag: CandidateTagView;
  topics: string | null;
};

export type CandidateSearchResultListViews = {
  items: CandidateSearchResultListView[];
};

export type CandidateTagView = {
  customerId: number;
  fee: CandidateTagViewFee | null;
  fullName: string;
};

export type CandidateTagViewFee = {
  amount: number;
  currencyCode: string;
  displayValue: string;
};

export type CareerEditView = {
  careerId: bigint;
  form: CareerForm;
};

export type CareerForm = {
  active: boolean;
  careerLocalization: { additionalProperties: Record<string, CareerLocalizationForm> };
};

export namespace CareerLevel {
  const values = [
    "OWNER_EXECUTIVE_C_LEVEL",
    "SENIOR_MANAGEMENT",
    "MIDDLE_MANAGEMENT",
    "JUNIOR_MANAGEMENT",
    "ADMINISTRATIVE_STAFF",
    "EMPLOYEE_WITHOUT_MANAGEMENT_OR_LEADERSHIP_RESPONSIBILITY",
    "STUDENT_OR_TRAINEE",
    "TEACHER_OR_ACADEMIC",
    "NOT_EMPLOYED",
    "RETIRED",
    "OTHER",
    "UNDEFINED",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CareerLevel = CareerLevel.Impl;

export type CareerListView = {
  active: boolean;
  careerId: bigint;
  languages: string[];
  location: string;
  title: string;
};

export type CareerListViewList = {
  items: CareerListView[];
};

export type CareerLocalizationForm = {
  benefits: string;
  content: string;
  location: string;
  title: string;
};

export type CareerView = {
  active: boolean;
  benefits: string;
  careerId: bigint;
  content: string;
  location: string;
  title: string;
};

export type CertificateFileForm = {
  file: ReadonlyUint8Array;
  filename: string;
};

export type CertificateForm = {
  publicKey: string;
  type: AspCertificateType | null;
};

export type CertificatePublicKeyForm = {
  publicKey: string;
};

export type ChannelAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean | null;
  includeTemporary: boolean | null;
  query: string;
};

export type ChannelAdminTagBasedProps = {
  channelId: bigint;
  managerSelection: ChannelManager[];
};

export type ChannelCoverEditForm = { kind: "defaultForm" } | { kind: "update"; base64Image: string } | { kind: "existing"; url: string };

export type ChannelEditForm = {
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelLocalizationEditForm> };
  childChannels: ChannelRelationForm[];
  cover: ChannelCoverEditForm;
  definition: string | null;
  name: string;
  notes: string | null;
  primarySortOrder: number | null;
  tag: Tag;
};

export type ChannelEditView = {
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelLocalizationView> };
  childChannels: ChannelRelationView[];
  cover: string | null;
  definition: string | null;
  name: string;
  notes: string | null;
  primarySortOrder: number | null;
  tag: Tag;
};

export type ChannelFollowerStatus = {
  following: boolean;
};

export type ChannelIdsList = {
  channelIds: bigint[];
};

export type ChannelInsightListView = {
  channelId: bigint;
  summaryCount: number;
  title: string | null;
  topSummaries: ContentMetadata_SUMMARY[];
};

export type ChannelInsightListViews = {
  items: ChannelInsightListView[];
};

export type ChannelInsightQuery = {
  tags: string[];
};

export type ChannelList = {
  channels: ChannelMetadata[];
};

export type ChannelListPortletEditView = {
  channels: ChannelMetadata[];
  portlet: PortletEditView;
};

export type ChannelListPortletForm = {
  channelIds: bigint[];
  portletForm: PortletForm;
};

export type ChannelListPortletView = {
  items: ChannelMetadata[];
  metadata: PortletMetadata;
  page: number;
  pageSize: number;
  totalCount: number;
  viewAllUri: string;
};

export type ChannelLocalizationEditForm = {
  active: boolean;
  description: string | null;
  manager: number | null;
  synonym: string | null;
  title: string | null;
};

export type ChannelLocalizationView = {
  active: boolean;
  description: string | null;
  manager: number | null;
  synonym: string | null;
  title: string | null;
};

export type ChannelManager = {
  id: number;
  name: string;
};

export type ChannelMetadata = {
  activationTime: DateTime<true> | null;
  active: boolean;
  channelId: bigint;
  channelLocalization: { additionalProperties: Record<string, ChannelTextLocalization> };
  coverUri: string | null;
  definition: string | null;
  descriptionHtml: string | null;
  modificationTime: DateTime<true> | null;
  name: string;
  synonyms: string | null;
  tag: Tag;
  title: string | null;
  visibleLanguages: Language[];
};

export type ChannelMetadataArray = {
  channelMetadata: ChannelMetadata[];
};

export type ChannelPortletViewRequest = {
  isoCode: string;
  pageSize: number | null;
};

export type ChannelRelationForm = {
  channelId: bigint;
  sortOrder: number | null;
  title: string;
};

export type ChannelRelationView = {
  channelId: bigint;
  sortOrder: number | null;
  title: string;
};

export type ChannelSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; channelId: bigint };

export type ChannelTextLocalization = {
  description: string | null;
  synonym: string | null;
  title: string | null;
};

export type CommonConnectorForm = {
  active: boolean;
  baseUri: string;
  languages: Language[];
  name: string;
  products: ConnectorProduct[];
  syncCompletions: boolean;
  syncContent: boolean;
};

export type ConnectorCompletionExecutionLogListView = {
  completedAt: DateTime<true> | null;
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
  userId: string;
};

export type ConnectorCompletionFilters = {
  timeFrame: string;
  userId: string;
};

export namespace ConnectorCompletionTimeFrame {
  const values = ["LAST_24_HOURS", "LAST_WEEK", "LAST_2_WEEKS", "LAST_MONTH", "LAST_3_MONTHS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorCompletionTimeFrame = ConnectorCompletionTimeFrame.Impl;

export type ConnectorContentExecutionListView = {
  connectorExecutionId: bigint;
  connectorName: string;
  description: string;
  detail: ConnectorContentExecutionLogDetailView;
  provider: ConnectorProviderMetadata;
  syncedAt: DateTime<true> | null;
  type: "WEB" | "BATCH";
};

export type ConnectorContentExecutionLogDetailView = {
  failedExportedItems: number;
  successfullyExportedItems: number;
};

export type ConnectorContentExecutionLogListView = {
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
};

export type ConnectorCredentialProfileView = {
  authType: "BASIC" | "OAUTH2" | "JWT" | "GOOGLE_JWT";
  displayName: string;
  key: string;
};

export type ConnectorCredentialProfileViewArray = {
  items: ConnectorCredentialProfileView[];
};

export type ConnectorExecutionLogMetadata = {
  connectorExecutionLogId: bigint;
  contentId: string;
  method: string;
  request: string;
  response: string;
  statusCode: number;
  syncedAt: DateTime<true>;
};

export namespace ConnectorExecutionType {
  const values = ["WEB", "BATCH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorExecutionType = ConnectorExecutionType.Impl;

export type ConnectorExecutionView = {
  connectorExecutionId: bigint;
  executedAt: DateTime<true>;
  executionType: ConnectorExecutionType;
  logsCount: number;
  provider: ConnectorProviderMetadata;
};

export type ConnectorForm = {
  active: boolean;
  languages: Language[];
  name: string;
  products: ConnectorProduct[];
  settingsForm: ConnectorSettingsForm;
  syncCompletions: boolean;
  syncContent: boolean;
  type: ConnectorType;
};

export type ConnectorFormResult = { kind: "SUCCESS"; accessTokenValidated: boolean } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export namespace ConnectorGrantType {
  const values = ["CLIENT_CREDENTIALS", "PASSWORD"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorGrantType = ConnectorGrantType.Impl;

export type ConnectorListView = {
  active: boolean;
  connectorId: bigint;
  name: string;
  provider: ConnectorProviderMetadata;
  syncedAt: DateTime<true> | null;
};

export type ConnectorListViewArray = {
  items: ConnectorListView[];
};

export namespace ConnectorProduct {
  const values = ["BUSINESS_BOOKS", "ARTICLES_AND_REPORTS", "VIDEOS_AND_PODCASTS", "ACTIONABLES", "VERIFY", "VWD"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorProduct = ConnectorProduct.Impl;

export type ConnectorProviderMetadata = {
  imageUri: string;
  name: string;
  type: ConnectorType;
};

export type ConnectorRealTimeCompletionInfo = {
  completedAt: DateTime<true>;
  contentId: string;
  corporateId: number;
  customerId: number;
  duration: bigint;
  progress: number;
};

export type ConnectorSettingsForm = {
  accountId: string | null;
  apiKey: string | null;
  authenticationType: AuthenticationType;
  certificate: string | null;
  clientId: string;
  clientSecret: string;
  doceboCatalogCode: string | null;
  doceboCategoryCode: string | null;
  doceboChannelId: bigint | null;
  grantType: ("CLIENT_CREDENTIALS" | "PASSWORD") | null;
  lmsApiUri: string | null;
  lmsBaseUri: string;
  lmsTokenUri: string | null;
  locales: SuccessFactorsLocale[];
  password: string | null;
  privateKey: string | null;
  scope: string | null;
  sfCompanyId: string | null;
  userId: string | null;
  userIdentifier: ConnectorUserIdentifier;
  username: string | null;
  workdayCategoryFallback: string | null;
  xapiActorId: XApiActorId;
};

export type ConnectorSettingsView = {
  accountId: string | null;
  apiKey: string | null;
  authenticationType: AuthenticationType;
  certificate: string | null;
  clientId: string;
  clientSecret: string;
  credentialProfile: string | null;
  doceboCatalogCode: string | null;
  doceboCategoryCode: string | null;
  doceboChannelId: bigint | null;
  grantType: ConnectorGrantType;
  lmsApiUri: string | null;
  lmsBaseUri: string;
  lmsTokenUri: string | null;
  locales: SuccessFactorsLocale[];
  password: string | null;
  privateKey: string | null;
  region: string | null;
  scope: string | null;
  sfCompanyId: string | null;
  userId: string | null;
  userIdentifier: ConnectorUserIdentifier;
  username: string | null;
  workdayCategoryFallback: string | null;
  xapiActorId: XApiActorId;
};

export type ConnectorSyncEntry = {
  connectorId: bigint;
  corporateId: number;
  type: ConnectorType;
};

export type ConnectorSyncForm = {
  actionableIds: bigint[];
  dataIds: number[];
  verifyIds: bigint[];
  vwdIds: bigint[];
};

export namespace ConnectorType {
  const values = ["SUCCESS_FACTORS", "DEGREED", "DOCEBO", "DOCEBO_CATALOG", "CSOD", "SUM_TOTAL", "EDCAST", "EDCAST_V2", "WORKDAY", "VIVA_LEARNING", "MS_GRAPH", "ORACLE", "LRS_XAPI", "GEMINI_DATASTORE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorType = ConnectorType.Impl;

export namespace ConnectorUserIdentifier {
  const values = ["NAME_ID", "EMAIL", "EXTRA_FIELD_1", "EXTRA_FIELD_2", "EXTRA_FIELD_3"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ConnectorUserIdentifier = ConnectorUserIdentifier.Impl;

export type ConnectorView = {
  connectorId: bigint;
  languages: Language[];
  name: string;
  products: ConnectorProduct[];
  settings: ConnectorSettingsView;
  syncCompletions: boolean;
  syncContent: boolean;
  syncStartedCompletions: boolean;
  type: ConnectorType;
};

export type ContentCardDemoProps = {
  actionables: ContentMetadata[];
  miabs: ContentMetadata[];
  summaries: ContentMetadata[];
  verifais: ContentMetadata[];
};

export type ContentCurationDomainFilter = {
  contentCurationDomainFilterId: bigint | null;
  createdAt: DateTime<true>;
  domain: string;
  include: boolean;
  reason: string | null;
};

export type ContentCurationItem = {
  author: string | null;
  contentCurationItemId: bigint | null;
  createdAt: DateTime<true>;
  pipelineExists: boolean;
  publishedDate: { year: number; month: number; day: number } | null;
  searchTerm: string;
  summary: string | null;
  title: string;
  url: string;
};

export type ContentId =
  | { contentType: "SUMMARY"; id: bigint }
  | { contentType: "ACTIONABLE"; id: bigint }
  | { contentType: "MIAB"; id: bigint }
  | { contentType: "EXPERT_VIDEO"; id: bigint }
  | { contentType: "ONLINE_CONTENT"; id: bigint }
  | { contentType: "QUICKWIN"; id: bigint };

export type ContentIdList = {
  items: ContentId[];
};

export type ContentItemListPortletView = {
  items: ContentMetadata[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export namespace ContentItemType {
  const values = ["UNKNOWN", "SUMMARY", "CATEGORY", "CHANNEL", "ACTIONABLE", "MYLIST", "CUSTOMPAGE", "MIAB", "EXPERT_VIDEO", "CUSTOMER_STORY", "QUICKWIN", "VERIFY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentItemType = ContentItemType.Impl;

export type ContentIxStatus =
  | { contentType: "SUMMARY"; bookmarkedAt: DateTime<true> | null; consumedAt: DateTime<true> | null; contentId: ContentId_SUMMARY; customerId: number; likedAt: DateTime<true> | null; readingProgress: number }
  | { contentType: "MIAB"; bookmarkedAt: DateTime<true> | null; contentId: ContentId_MIAB; customerId: number; feedback: boolean | null; feedbackAt: DateTime<true> | null; modifiedAt: DateTime<true> }
  | {
      contentType: "ACTIONABLE";
      bookmarkedAt: DateTime<true> | null;
      contentId: ContentId_ACTIONABLE;
      customerId: number;
      experienceFeedbackScore: number | null;
      peerInsightProvidedAt: DateTime<true> | null;
      proTalkRequestedAt: DateTime<true> | null;
      proTalkVideoProgress: number | null;
      step2CompletedAt: DateTime<true> | null;
      step2Readiness: boolean;
      step2VideoProgress: number | null;
    }
  | { contentType: "EXPERT_VIDEO"; bookmarkedAt: DateTime<true> | null; contentId: ContentId_EXPERT_VIDEO; customerId: number; modifiedAt: DateTime<true>; startedAt: DateTime<true> | null }
  | {
      contentType: "QUICKWIN";
      bookmarkedAt: DateTime<true> | null;
      completedAt: DateTime<true> | null;
      contentId: ContentId_QUICKWIN;
      customerId: number;
      modifiedAt: DateTime<true>;
      reshuffledAt: DateTime<true> | null;
      selectedAnswerIndex: number | null;
      startedAt: DateTime<true> | null;
    }
  | { contentType: "VERIFAI"; bookmarkedAt: DateTime<true> | null; consumedAt: DateTime<true> | null; contentId: ContentId_ONLINE_CONTENT; customerId: number; modifiedAt: DateTime<true> };

export type ContentIxStatus_ACTIONABLE = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_ACTIONABLE;
  contentType: "ACTIONABLE";
  customerId: number;
  experienceFeedbackScore: number | null;
  peerInsightProvidedAt: DateTime<true> | null;
  proTalkRequestedAt: DateTime<true> | null;
  proTalkVideoProgress: number | null;
  step2CompletedAt: DateTime<true> | null;
  step2Readiness: boolean;
  step2VideoProgress: number | null;
};

export type ContentIxStatus_EXPERT_VIDEO = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_EXPERT_VIDEO;
  contentType: "EXPERT_VIDEO";
  customerId: number;
  modifiedAt: DateTime<true>;
  startedAt: DateTime<true> | null;
};

export type ContentIxStatusList = {
  items: ContentIxStatus[];
};

export type ContentIxStatus_MIAB = {
  bookmarkedAt: DateTime<true> | null;
  contentId: ContentId_MIAB;
  contentType: "MIAB";
  customerId: number;
  feedback: boolean | null;
  feedbackAt: DateTime<true> | null;
  modifiedAt: DateTime<true>;
};

export type ContentIxStatus_QUICKWIN = {
  bookmarkedAt: DateTime<true> | null;
  completedAt: DateTime<true> | null;
  contentId: ContentId_QUICKWIN;
  contentType: "QUICKWIN";
  customerId: number;
  modifiedAt: DateTime<true>;
  reshuffledAt: DateTime<true> | null;
  selectedAnswerIndex: number | null;
  startedAt: DateTime<true> | null;
};

export type ContentIxStatus_SUMMARY = {
  bookmarkedAt: DateTime<true> | null;
  consumedAt: DateTime<true> | null;
  contentId: ContentId_SUMMARY;
  contentType: "SUMMARY";
  customerId: number;
  likedAt: DateTime<true> | null;
  readingProgress: number;
};

export type ContentIxStatus_VERIFAI = {
  bookmarkedAt: DateTime<true> | null;
  consumedAt: DateTime<true> | null;
  contentId: ContentId_ONLINE_CONTENT;
  contentType: "VERIFAI";
  customerId: number;
  modifiedAt: DateTime<true>;
};

export namespace ContentLibrary {
  const values = ["BUSINESS_BOOKS", "CLASSICS_BOOKS", "ARTICLES_AND_REPORTS", "VIDEOS_AND_PODCASTS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentLibrary = ContentLibrary.Impl;

export type ContentMetadata =
  | {
      contentType: "SUMMARY";
      activatedAt: DateTime<true> | null;
      activationTime: DateTime<true> | null;
      active: boolean;
      audioActivatedAt: DateTime<true> | null;
      audioActive: boolean;
      audioDuration: number;
      authorInfo: string | null;
      authors: AuthorListView[];
      backgroundColor: string | null;
      bestseller: boolean;
      businessClassic: boolean;
      canonicalName: string;
      common: ContentMetadataCommon;
      contentId: ContentId_SUMMARY;
      contentItemId: bigint;
      contentItemType: ContentItemType;
      countriesExcluded: Country[];
      countriesIncluded: Country[];
      coverModifiedAt: DateTime<true> | null;
      coverUri: string;
      dataId: number;
      descriptionHtml: string | null;
      documentType: SummaryDocumentType;
      duration: number;
      imageBorder: boolean;
      language: Language;
      miabId: bigint | null;
      modifiedAt: DateTime<true> | null;
      originalTitle: string | null;
      previewAvailable: boolean;
      publicationDate: string | null;
      publisherInfo: string | null;
      publisherName: string | null;
      qualities: KeyQuality[];
      rating: number | null;
      sourceType: SummarySourceType;
      subtitle: string | null;
      summaryPages: number;
      summaryWords: number;
      teaser: string | null;
      title: string | null;
    }
  | {
      contentType: "ACTIONABLE";
      actionableId: bigint;
      actionableSetId: bigint | null;
      activatedAt: DateTime<true> | null;
      active: boolean;
      common: ContentMetadataCommon;
      contentId: ContentId_ACTIONABLE;
      coverUri: string;
      description: string;
      language: Language;
      modifiedAt: DateTime<true>;
      name: string;
      title: string;
    }
  | { contentType: "MIAB"; common: ContentMetadataCommon; contentId: ContentId_MIAB; customerId: number | null; dataId: number; modifiedAt: DateTime<true> | null }
  | {
      contentType: "ONLINE_CONTENT";
      authorsDetail: {
        editorialTeam: boolean;
        gender: OnlineContentCreatorGender;
        name: string;
        rating: number | null;
      }[];
      common: ContentMetadataCommon;
      contentId: ContentId_ONLINE_CONTENT;
      contentProductionResourceId: bigint | null;
      contentRating: number | null;
      deactivatedAt: DateTime<true> | null;
      formatType: OnlineContentFormatType;
      hashId: string;
      modifiedAt: DateTime<true> | null;
      overallRating: number | null;
      publicationDate: { year: number; month: number; day: number };
      readingTime: number;
      resourceUrl: string;
      sourceDetail: {
        logoUri: string | null;
        rating: number | null;
        title: string;
        url: string;
      };
      subtitle: string | null;
    }
  | { contentType: "EXPERT_VIDEO"; authors: AuthorListView[]; common: ContentMetadataCommon; contentId: ContentId_EXPERT_VIDEO; length: number | null; modifiedAt: DateTime<true>; whatYouWillLearn: string }
  | { contentType: "QUICKWIN"; common: ContentMetadataCommon; contentId: ContentId_QUICKWIN; dataId: number; modifiedAt: DateTime<true>; quickWinType: QuickWinType };

export type ContentMetadataCommon = {
  activatedAt: DateTime<true> | null;
  audioActive: boolean;
  coverUri: string;
  description: string | null;
  inactive: boolean;
  language: Language;
  leadIn: string | null;
  link: string | null;
  tags: Tag[];
  title: string;
};

export type ContentMetadata_EXPERT_VIDEO = {
  authors: AuthorListView[];
  common: ContentMetadataCommon;
  contentId: ContentId_EXPERT_VIDEO;
  contentType: "EXPERT_VIDEO";
  length: number | null;
  modifiedAt: DateTime<true>;
  whatYouWillLearn: string;
};

export type ContentMetadataFilterForm = {
  actionable: {
    filter: ActionableFiltersForm;
    pageInfo: PageInfo;
  };
  expertVideo: {
    filter: ExpertVideoFiltersForm;
    pageInfo: PageInfo;
  };
  fallbackLanguage: boolean;
  miab: {
    filter: MiabFiltersForm;
    pageInfo: PageInfo;
  };
  summary: {
    filter: SummaryFiltersForm;
    pageInfo: PageInfo;
    sorting: SummarySort;
  };
  tag: Tag;
  verifai: {
    filter: OnlineContentFiltersForm;
    pageInfo: PageInfo;
  };
};

export type ContentMetadataView = {
  actionable: {
    filter: FormFilter[];
    items: ContentMetadata_ACTIONABLE[];
    pageInfo: PageInfo;
  };
  expertVideo: {
    filter: FormFilter[];
    items: ContentMetadata_EXPERT_VIDEO[];
    pageInfo: PageInfo;
  };
  miab: {
    filter: FormFilter[];
    items: ContentMetadata_MIAB[];
    pageInfo: PageInfo;
  };
  summary: {
    filter: FormFilter[];
    items: ContentMetadata_SUMMARY[];
    pageInfo: PageInfo;
    sorting: FormSorting;
  };
  verifai: {
    filter: FormFilter[];
    items: ContentMetadata_ONLINE_CONTENT[];
    pageInfo: PageInfo;
  };
};

export type ContentProductionStepDataStepDataForm_AI_BRIEF_CONFIG = {
  kind: "AI_BRIEF_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_AI_BRIEF_CONFIG = {
  kind: "AI_BRIEF_CONFIG";
  prompt: string;
};

export type ContentProductStepAiBriefConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AI_BRIEF_CONFIG };

export type ContentProductionStepDataStepDataForm_AI_BRIEF_OUTPUT = {
  kind: "AI_BRIEF_OUTPUT";
  markdown: string | null;
};

export type ContentProductionStepDataStepDataUnparsedForm_AI_BRIEF_OUTPUT = {
  kind: "AI_BRIEF_OUTPUT";
  markdown: string;
};

export type ContentProductStepAiBriefOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AI_BRIEF_OUTPUT };

export type ContentProductionStepDataStepDataForm_AI_PROOFREAD_SUMMARY_CONFIG = {
  kind: "AI_PROOFREAD_SUMMARY_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_AI_PROOFREAD_SUMMARY_CONFIG = {
  kind: "AI_PROOFREAD_SUMMARY_CONFIG";
  prompt: string;
};

export type ContentProductStepAiProofreadSummaryConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AI_PROOFREAD_SUMMARY_CONFIG };

export type ContentProductionStepDataStepDataForm_AI_PROOFREAD_SUMMARY_OUTPUT = {
  kind: "AI_PROOFREAD_SUMMARY_OUTPUT";
  passed: boolean;
};

export type ContentProductionStepDataStepDataUnparsedForm_AI_PROOFREAD_SUMMARY_OUTPUT = {
  kind: "AI_PROOFREAD_SUMMARY_OUTPUT";
  passed: boolean;
};

export type ContentProductStepAiProofreadSummaryOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AI_PROOFREAD_SUMMARY_OUTPUT };

export type ContentProductionStepDataStepDataForm_APPLY_PARENTAL_SETTINGS_CONFIG = {
  kind: "APPLY_PARENTAL_SETTINGS_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_APPLY_PARENTAL_SETTINGS_CONFIG = {
  kind: "APPLY_PARENTAL_SETTINGS_CONFIG";
  prompt: string;
};

export type ContentProductStepApplyParentalSettingsConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_APPLY_PARENTAL_SETTINGS_CONFIG };

export type ContentProductionStepDataStepDataForm_APPLY_PARENTAL_SETTINGS_OUTPUT = {
  crudeLanguage: boolean;
  kind: "APPLY_PARENTAL_SETTINGS_OUTPUT";
  political: boolean;
  religious: boolean;
};

export type ContentProductionStepDataStepDataUnparsedForm_APPLY_PARENTAL_SETTINGS_OUTPUT = {
  crudeLanguage: boolean;
  kind: "APPLY_PARENTAL_SETTINGS_OUTPUT";
  political: boolean;
  religious: boolean;
};

export type ContentProductStepApplyParentalSettingsOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_APPLY_PARENTAL_SETTINGS_OUTPUT };

export type ContentProductionStepDataStepDataForm_ASSIGN_SKILLS_CONFIG = {
  kind: "ASSIGN_SKILLS_CONFIG";
  maxPerCompetency: bigint;
  maxSkills: bigint;
  maxTextChars: bigint;
  minProbability: number;
  model: string;
  window: number;
};

export type ContentProductionStepDataStepDataUnparsedForm_ASSIGN_SKILLS_CONFIG = {
  kind: "ASSIGN_SKILLS_CONFIG";
  maxPerCompetency: string;
  maxSkills: string;
  maxTextChars: string;
  minProbability: string;
  model: string;
  window: string;
};

export type ContentProductStepAssignSkillsConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_ASSIGN_SKILLS_CONFIG };

export type ContentProductionStepDataStepDataForm_ASSIGN_SKILLS_OUTPUT = {
  kind: "ASSIGN_SKILLS_OUTPUT";
  skills: AssignedSkillForm[];
  taxonomyGapCandidate: boolean;
};

export type ContentProductionStepDataStepDataUnparsedForm_ASSIGN_SKILLS_OUTPUT = {
  kind: "ASSIGN_SKILLS_OUTPUT";
  skills: AssignedSkillUnparsedForm[];
  taxonomyGapCandidate: boolean;
};

export type ContentProductStepAssignSkillsOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_ASSIGN_SKILLS_OUTPUT };

export type ContentProductionStepDataStepDataForm_AUTHOR_RATING_CONFIG = {
  kind: "AUTHOR_RATING_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_AUTHOR_RATING_CONFIG = {
  kind: "AUTHOR_RATING_CONFIG";
  prompt: string;
};

export type ContentProductStepAuthorRatingConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AUTHOR_RATING_CONFIG };

export type ContentProductionStepDataStepDataForm_AUTHOR_RATING_OUTPUT = {
  authors: AuthorRatingDetailForm[];
  averageRating?: bigint;
  kind: "AUTHOR_RATING_OUTPUT";
};

export type ContentProductionStepDataStepDataUnparsedForm_AUTHOR_RATING_OUTPUT = {
  authors: AuthorRatingDetailUnparsedForm[];
  averageRating: string;
  kind: "AUTHOR_RATING_OUTPUT";
};

export type ContentProductStepAuthorRatingOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AUTHOR_RATING_OUTPUT };

export type ContentProductionStepDataStepDataForm_BOOK_EXTRACTION_CONFIG = {
  chapterAtomizationPrompt: string;
  kind: "BOOK_EXTRACTION_CONFIG";
  languageDetectionPrompt?: string;
  metadataPrompt: string;
  synthesisPrompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_BOOK_EXTRACTION_CONFIG = {
  chapterAtomizationPrompt: string;
  kind: "BOOK_EXTRACTION_CONFIG";
  languageDetectionPrompt: string;
  metadataPrompt: string;
  synthesisPrompt: string;
};

export type ContentProductStepBookExtractionConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_BOOK_EXTRACTION_CONFIG };

export type ContentProductionStepDataStepDataForm_BOOK_INIT_INPUT = {
  fileData: FileDataForm;
  kind: "BOOK_INIT_INPUT";
  manuscript: ExtFormFile;
  uploaderCustomerId: bigint;
};

export type ContentProductionStepDataStepDataUnparsedForm_BOOK_INIT_INPUT = {
  fileData: FileDataUnparsedForm;
  kind: "BOOK_INIT_INPUT";
  manuscript: ExtFormFile;
  uploaderCustomerId: string;
};

export type ContentProductStepBookInitInputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_BOOK_INIT_INPUT };

export type ContentProductionStepDataStepDataForm_BOOK_INIT_OUTPUT = {
  fileData: FileDataForm;
  kind: "BOOK_INIT_OUTPUT";
  manuscript: ExtFormFile;
};

export type ContentProductionStepDataStepDataUnparsedForm_BOOK_INIT_OUTPUT = {
  fileData: FileDataUnparsedForm;
  kind: "BOOK_INIT_OUTPUT";
  manuscript: ExtFormFile;
};

export type ContentProductStepBookInitOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_BOOK_INIT_OUTPUT };

export type ContentProductionStepDataStepDataForm_CONTENT_RATING_CONFIG = {
  kind: "CONTENT_RATING_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_CONTENT_RATING_CONFIG = {
  kind: "CONTENT_RATING_CONFIG";
  prompt: string;
};

export type ContentProductStepContentRatingConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_CONTENT_RATING_CONFIG };

export type ContentProductionStepDataStepDataForm_CONTENT_RATING_OUTPUT = {
  actionabilityScore?: number;
  actionabilitySummary?: string;
  credibilityScore?: number;
  credibilitySummary?: string;
  expertiseOriginalityScore?: number;
  expertiseOriginalitySummary?: string;
  kind: "CONTENT_RATING_OUTPUT";
  qualitySummary?: string;
  rating: bigint;
  redFlagsScore?: number;
  redFlagsSummary?: string;
  substantivenessScore?: number;
  substantivenessSummary?: string;
  targetAudience: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_CONTENT_RATING_OUTPUT = {
  actionabilityScore: string;
  actionabilitySummary: string;
  credibilityScore: string;
  credibilitySummary: string;
  expertiseOriginalityScore: string;
  expertiseOriginalitySummary: string;
  kind: "CONTENT_RATING_OUTPUT";
  qualitySummary: string;
  rating: string;
  redFlagsScore: string;
  redFlagsSummary: string;
  substantivenessScore: string;
  substantivenessSummary: string;
  targetAudience: string;
};

export type ContentProductStepContentRatingOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_CONTENT_RATING_OUTPUT };

export type ContentProductionStepDataStepDataForm_EXTRACTED_OUTPUT = {
  kind: "EXTRACTED_OUTPUT";
  originalText: string;
  screenshot: ExtFormFile;
};

export type ContentProductionStepDataStepDataUnparsedForm_EXTRACTED_OUTPUT = {
  kind: "EXTRACTED_OUTPUT";
  originalText: string;
  screenshot: ExtFormFile;
};

export type ContentProductStepExtractedOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_EXTRACTED_OUTPUT };

export type ContentProductionStepDataStepDataForm_HTML_INIT_INPUT = {
  kind: "HTML_INIT_INPUT";
  url: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_HTML_INIT_INPUT = {
  kind: "HTML_INIT_INPUT";
  url: string;
};

export type ContentProductStepFetchHtmlPageInputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_HTML_INIT_INPUT };

export type ContentProductionStepDataStepDataForm_SUMMARY_INFO_CONFIG = {
  kind: "SUMMARY_INFO_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SUMMARY_INFO_CONFIG = {
  kind: "SUMMARY_INFO_CONFIG";
  prompt: string;
};

export type ContentProductStepGenerateInformativeSummaryContentConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SUMMARY_INFO_CONFIG };

export type ContentProductionStepDataStepDataForm_SUMMARY_INFO_OUTPUT = {
  aboutTheAuthor: string;
  kind: "SUMMARY_INFO_OUTPUT";
  recommendation: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SUMMARY_INFO_OUTPUT = {
  aboutTheAuthor: string;
  kind: "SUMMARY_INFO_OUTPUT";
  recommendation: string;
};

export type ContentProductStepGenerateInformativeSummaryContentOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SUMMARY_INFO_OUTPUT };

export type ContentProductionStepDataStepDataForm_WHAT_YOU_WILL_LEARN_CONFIG = {
  kind: "WHAT_YOU_WILL_LEARN_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_WHAT_YOU_WILL_LEARN_CONFIG = {
  kind: "WHAT_YOU_WILL_LEARN_CONFIG";
  prompt: string;
};

export type ContentProductStepGenerateWhatYouWillLearnConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_WHAT_YOU_WILL_LEARN_CONFIG };

export type ContentProductionStepDataStepDataForm_WHAT_YOU_WILL_LEARN_OUTPUT = {
  kind: "WHAT_YOU_WILL_LEARN_OUTPUT";
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_WHAT_YOU_WILL_LEARN_OUTPUT = {
  kind: "WHAT_YOU_WILL_LEARN_OUTPUT";
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepGenerateWhatYouWillLearnOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_WHAT_YOU_WILL_LEARN_OUTPUT };

export type ContentProductionStepDataStepDataForm_HTML_INIT_CONFIG = {
  authorPrompt: string;
  kind: "HTML_INIT_CONFIG";
  languageDetectionPrompt?: string;
  markdownCleanupPrompt: string;
  publicationDatePrompt: string;
  rewriteTitlePrompt?: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_HTML_INIT_CONFIG = {
  authorPrompt: string;
  kind: "HTML_INIT_CONFIG";
  languageDetectionPrompt: string;
  markdownCleanupPrompt: string;
  publicationDatePrompt: string;
  rewriteTitlePrompt: string;
};

export type ContentProductStepHtmlInitConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_HTML_INIT_CONFIG };

export type ContentProductionStepDataStepDataForm_MARKDOWN_OUTPUT = {
  kind: "MARKDOWN_OUTPUT";
  markdown: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_MARKDOWN_OUTPUT = {
  kind: "MARKDOWN_OUTPUT";
  markdown: string;
};

export type ContentProductStepMarkdownOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_MARKDOWN_OUTPUT };

export type ContentProductionStepDataStepDataForm_METADATA_OUTPUT_DEPRECATED = {
  authors: AuthorForm[];
  kind: "METADATA_OUTPUT_DEPRECATED";
};

export type ContentProductionStepDataStepDataUnparsedForm_METADATA_OUTPUT_DEPRECATED = {
  authors: AuthorUnparsedForm[];
  kind: "METADATA_OUTPUT_DEPRECATED";
};

export type ContentProductStepMetadataOutputDeprecatedValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_METADATA_OUTPUT_DEPRECATED };

export type ContentProductionStepDataStepDataForm_METADATA_OUTPUT = {
  contentLanguage?: string;
  description: string | null;
  formatType: ContentProductionFormatType;
  icon: ExtFormFile;
  kind: "METADATA_OUTPUT";
  publicationDate: { year: number; month: number; day: number };
  resourceUrl: string;
  sourceTitle: string | null;
  sourceUrl: string;
  subtitle: string | null;
  title: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_METADATA_OUTPUT = {
  contentLanguage: string;
  description: string;
  formatType: ContentProductionFormatType | null;
  icon: ExtFormFile;
  kind: "METADATA_OUTPUT";
  publicationDate: string;
  resourceUrl: string;
  sourceTitle: string;
  sourceUrl: string;
  subtitle: string;
  title: string;
};

export type ContentProductStepMetadataOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_METADATA_OUTPUT };

export type ContentProductionStepDataStepDataForm_NO_LONGER_SUPPORTED = {
  data: string;
  kind: "NO_LONGER_SUPPORTED";
};

export type ContentProductionStepDataStepDataUnparsedForm_NO_LONGER_SUPPORTED = {
  data: string;
  kind: "NO_LONGER_SUPPORTED";
};

export type ContentProductStepNoLongerSupportedValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_NO_LONGER_SUPPORTED };

export type ContentProductionStepDataStepDataForm_PDF_EXTRACTION_CONFIG = {
  kind: "PDF_EXTRACTION_CONFIG";
  languageDetectionPrompt?: string;
  metadataPrompt: string;
  prompt: string;
  rewriteTitlePrompt?: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_PDF_EXTRACTION_CONFIG = {
  kind: "PDF_EXTRACTION_CONFIG";
  languageDetectionPrompt: string;
  metadataPrompt: string;
  prompt: string;
  rewriteTitlePrompt: string;
};

export type ContentProductStepPdfExtractionConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_PDF_EXTRACTION_CONFIG };

export type ContentProductionStepDataStepDataForm_PDF_INIT_INPUT = {
  fileData: FileDataForm;
  kind: "PDF_INIT_INPUT";
  pdf: ExtFormFile;
  uploaderCustomerId: bigint;
};

export type ContentProductionStepDataStepDataUnparsedForm_PDF_INIT_INPUT = {
  fileData: FileDataUnparsedForm;
  kind: "PDF_INIT_INPUT";
  pdf: ExtFormFile;
  uploaderCustomerId: string;
};

export type ContentProductStepPdfInitInputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_PDF_INIT_INPUT };

export type ContentProductionStepDataStepDataForm_PDF_INIT_OUTPUT = {
  fileData: FileDataForm;
  kind: "PDF_INIT_OUTPUT";
  pdf: ExtFormFile;
};

export type ContentProductionStepDataStepDataUnparsedForm_PDF_INIT_OUTPUT = {
  fileData: FileDataUnparsedForm;
  kind: "PDF_INIT_OUTPUT";
  pdf: ExtFormFile;
};

export type ContentProductStepPdfInitOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_PDF_INIT_OUTPUT };

export type ContentProductionStepDataStepDataForm_PROOFREAD_SUMMARY_OUTPUT = {
  approved: boolean;
  evergreen: boolean;
  kind: "PROOFREAD_SUMMARY_OUTPUT";
};

export type ContentProductionStepDataStepDataUnparsedForm_PROOFREAD_SUMMARY_OUTPUT = {
  approved: boolean;
  evergreen: boolean;
  kind: "PROOFREAD_SUMMARY_OUTPUT";
};

export type ContentProductStepProofreadSummaryOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_PROOFREAD_SUMMARY_OUTPUT };

export type ContentProductionStepDataStepDataForm_PUBLISH_SUMMARY_OUTPUT = {
  kind: "PUBLISH_SUMMARY_OUTPUT";
  publishedAt?: bigint;
  resourceId?: bigint;
  resourceUuid?: string;
  summaryId: bigint;
};

export type ContentProductionStepDataStepDataUnparsedForm_PUBLISH_SUMMARY_OUTPUT = {
  kind: "PUBLISH_SUMMARY_OUTPUT";
  publishedAt: string;
  resourceId: string;
  resourceUuid: string;
  summaryId: string;
};

export type ContentProductStepPublishSummaryOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_PUBLISH_SUMMARY_OUTPUT };

export type ContentProductionStepDataStepDataForm_QUANTITATIVE_CHECKS_CONFIG = {
  kind: "QUANTITATIVE_CHECKS_CONFIG";
  maxQuoteCount: bigint;
  maxTakeawayWordCount: bigint;
  maxWordCount: bigint;
  minQuoteCount: bigint;
  minWordCount: bigint;
};

export type ContentProductionStepDataStepDataUnparsedForm_QUANTITATIVE_CHECKS_CONFIG = {
  kind: "QUANTITATIVE_CHECKS_CONFIG";
  maxQuoteCount: string;
  maxTakeawayWordCount: string;
  maxWordCount: string;
  minQuoteCount: string;
  minWordCount: string;
};

export type ContentProductStepQuantitativeChecksConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_QUANTITATIVE_CHECKS_CONFIG };

export type ContentProductionStepDataStepDataForm_QUANTITATIVE_CHECKS_OUTPUT = {
  kind: "QUANTITATIVE_CHECKS_OUTPUT";
  passed: boolean;
};

export type ContentProductionStepDataStepDataUnparsedForm_QUANTITATIVE_CHECKS_OUTPUT = {
  kind: "QUANTITATIVE_CHECKS_OUTPUT";
  passed: boolean;
};

export type ContentProductStepQuantitativeChecksOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_QUANTITATIVE_CHECKS_OUTPUT };

export type ContentProductionStepDataStepDataForm_RATING_RATIONALE_CONFIG = {
  kind: "RATING_RATIONALE_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_RATING_RATIONALE_CONFIG = {
  kind: "RATING_RATIONALE_CONFIG";
  prompt: string;
};

export type ContentProductStepRatingRationaleConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_RATING_RATIONALE_CONFIG };

export type ContentProductionStepDataStepDataForm_RATING_RATIONALE_OUTPUT = {
  kind: "RATING_RATIONALE_OUTPUT";
  rationale: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_RATING_RATIONALE_OUTPUT = {
  kind: "RATING_RATIONALE_OUTPUT";
  rationale: string;
};

export type ContentProductStepRatingRationaleOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_RATING_RATIONALE_OUTPUT };

export type ContentProductionStepDataStepDataForm_SKELETON_DATA = {
  data: string;
  kind: "SKELETON_DATA";
};

export type ContentProductionStepDataStepDataUnparsedForm_SKELETON_DATA = {
  data: string;
  kind: "SKELETON_DATA";
};

export type ContentProductStepSkeletonDataValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SKELETON_DATA };

export type ContentProductionStepDataStepDataForm_SOURCE_RATING_CONFIG = {
  kind: "SOURCE_RATING_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SOURCE_RATING_CONFIG = {
  kind: "SOURCE_RATING_CONFIG";
  prompt: string;
};

export type ContentProductStepSourceRatingConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SOURCE_RATING_CONFIG };

export type ContentProductionStepDataStepDataForm_SOURCE_RATING_OUTPUT = {
  contentProductionSourceId?: bigint;
  expertiseAreas: string | null;
  expertiseScore?: number;
  expertiseSummary?: string;
  independenceScore?: number;
  independenceSummary?: string;
  kind: "SOURCE_RATING_OUTPUT";
  logoUri?: string;
  primaryFocus: string | null;
  productionQualityScore?: number;
  productionQualitySummary?: string;
  rating: bigint;
  recommendation: string;
  reputationScore?: number;
  reputationSummary?: string;
  sourceCategory: string | null;
  sourceTitle?: string;
  sourceUrl?: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SOURCE_RATING_OUTPUT = {
  contentProductionSourceId: string;
  expertiseAreas: string;
  expertiseScore: string;
  expertiseSummary: string;
  independenceScore: string;
  independenceSummary: string;
  kind: "SOURCE_RATING_OUTPUT";
  logoUri: string;
  primaryFocus: string;
  productionQualityScore: string;
  productionQualitySummary: string;
  rating: string;
  recommendation: string;
  reputationScore: string;
  reputationSummary: string;
  sourceCategory: string;
  sourceTitle: string;
  sourceUrl: string;
};

export type ContentProductStepSourceRatingOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SOURCE_RATING_OUTPUT };

export type ContentProductionStepDataStepDataForm_RESOURCE_OUTPUT = {
  kind: "RESOURCE_OUTPUT";
  resourceId: bigint;
  resourceUuid?: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_RESOURCE_OUTPUT = {
  kind: "RESOURCE_OUTPUT";
  resourceId: string;
  resourceUuid: string;
};

export type ContentProductStepStoreResourceOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_RESOURCE_OUTPUT };

export type ContentProductionStepDataStepDataForm_SUMMARIZE_CONFIG = {
  articleInstruction: string;
  blogpostInstruction: string;
  bookInstruction: string;
  kind: "SUMMARIZE_CONFIG";
  podcastInstruction: string;
  prompt: string;
  reportInstruction: string;
  updatePrompt: string;
  videoInstruction: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SUMMARIZE_CONFIG = {
  articleInstruction: string;
  blogpostInstruction: string;
  bookInstruction: string;
  kind: "SUMMARIZE_CONFIG";
  podcastInstruction: string;
  prompt: string;
  reportInstruction: string;
  updatePrompt: string;
  videoInstruction: string;
};

export type ContentProductStepSummarizeContentConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SUMMARIZE_CONFIG };

export type ContentProductionStepDataStepDataForm_SUMMARIZE_OUTPUT = {
  kind: "SUMMARIZE_OUTPUT";
  language?: string;
  markdown: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_SUMMARIZE_OUTPUT = {
  kind: "SUMMARIZE_OUTPUT";
  language: string;
  markdown: string;
};

export type ContentProductStepSummarizeContentOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SUMMARIZE_OUTPUT };

export type ContentProductionStepDataStepDataForm_SUMMARY_GENERATING_OUTPUT = {
  approved: boolean;
  kind: "SUMMARY_GENERATING_OUTPUT";
};

export type ContentProductionStepDataStepDataUnparsedForm_SUMMARY_GENERATING_OUTPUT = {
  approved: boolean;
  kind: "SUMMARY_GENERATING_OUTPUT";
};

export type ContentProductStepSummaryGeneratingOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_SUMMARY_GENERATING_OUTPUT };

export type ContentProductionStepDataStepDataForm_TAKEAWAYS_CONFIG = {
  kind: "TAKEAWAYS_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_TAKEAWAYS_CONFIG = {
  kind: "TAKEAWAYS_CONFIG";
  prompt: string;
};

export type ContentProductStepTakeawaysConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TAKEAWAYS_CONFIG };

export type ContentProductionStepDataStepDataForm_TAKEAWAYS_OUTPUT = {
  kind: "TAKEAWAYS_OUTPUT";
  takeaways: TakeawaysForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TAKEAWAYS_OUTPUT = {
  kind: "TAKEAWAYS_OUTPUT";
  takeaways: TakeawaysUnparsedForm[];
};

export type ContentProductStepTakeawaysOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TAKEAWAYS_OUTPUT };

export type ContentProductionStepDataStepDataForm_TEE_PROOF_DATA = {
  kind: "TEE_PROOF_DATA";
  teeProofDataJson: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_TEE_PROOF_DATA = {
  kind: "TEE_PROOF_DATA";
  teeProofDataJson: string;
};

export type ContentProductStepTeeProofDataValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TEE_PROOF_DATA };

export type ContentProductionStepDataStepDataForm_TEE_VERIFICATION_OUTPUT = {
  kind: "TEE_VERIFICATION_OUTPUT";
  teeVerificationError: string | null;
  teeVerificationStatus: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_TEE_VERIFICATION_OUTPUT = {
  kind: "TEE_VERIFICATION_OUTPUT";
  teeVerificationError: string;
  teeVerificationStatus: string;
};

export type ContentProductStepTeeVerificationOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TEE_VERIFICATION_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_AR_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_AR_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_AR_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_AR_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateArOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_AR_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_CONFIG = {
  authorPrompt: string;
  kind: "TRANSLATE_CONFIG";
  summaryFieldsPrompt: string;
  summaryLanguageInstructions: string | null;
  summaryMarkdownPrompt: string;
  translationCheckPrompt: string;
  translationFixPrompt?: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_CONFIG = {
  authorPrompt: string;
  kind: "TRANSLATE_CONFIG";
  summaryFieldsPrompt: string;
  summaryLanguageInstructions: string;
  summaryMarkdownPrompt: string;
  translationCheckPrompt: string;
  translationFixPrompt: string;
};

export type ContentProductStepTranslateConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_CONFIG };

export type ContentProductionStepDataStepDataForm_TRANSLATE_DE_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_DE_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_DE_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_DE_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateDeOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_DE_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_EN_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_EN_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_EN_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_EN_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateEnOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_EN_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_ES_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_ES_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_ES_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_ES_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateEsOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_ES_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_FR_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_FR_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_FR_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_FR_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateFrOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_FR_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_PT_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_PT_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_PT_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_PT_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslatePtOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_PT_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_RU_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_RU_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_RU_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_RU_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateRuOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_RU_OUTPUT };

export type ContentProductionStepDataStepDataForm_TRANSLATE_ZH_OUTPUT = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  kind: "TRANSLATE_ZH_OUTPUT";
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type ContentProductionStepDataStepDataUnparsedForm_TRANSLATE_ZH_OUTPUT = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  kind: "TRANSLATE_ZH_OUTPUT";
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type ContentProductStepTranslateZhOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_TRANSLATE_ZH_OUTPUT };

export type ContentProductionStepDataStepDataForm_FIDELITY_CHECK_CONFIG = {
  kind: "FIDELITY_CHECK_CONFIG";
  prompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_FIDELITY_CHECK_CONFIG = {
  kind: "FIDELITY_CHECK_CONFIG";
  prompt: string;
};

export type ContentProductStepVerifySourceFidelityConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_FIDELITY_CHECK_CONFIG };

export type ContentProductionStepDataStepDataForm_FIDELITY_CHECK_OUTPUT = {
  kind: "FIDELITY_CHECK_OUTPUT";
  passed: boolean;
};

export type ContentProductionStepDataStepDataUnparsedForm_FIDELITY_CHECK_OUTPUT = {
  kind: "FIDELITY_CHECK_OUTPUT";
  passed: boolean;
};

export type ContentProductStepVerifySourceFidelityOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_FIDELITY_CHECK_OUTPUT };

export type ContentProductionStepDataStepDataForm_VIDEO_INIT_CONFIG = {
  authorPrompt: string;
  kind: "VIDEO_INIT_CONFIG";
  languageDetectionPrompt?: string;
  rewriteTitlePrompt?: string;
  speakerLabelingPrompt: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_VIDEO_INIT_CONFIG = {
  authorPrompt: string;
  kind: "VIDEO_INIT_CONFIG";
  languageDetectionPrompt: string;
  rewriteTitlePrompt: string;
  speakerLabelingPrompt: string;
};

export type ContentProductStepVideoInitConfigValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_VIDEO_INIT_CONFIG };

export type ContentProductionStepDataStepDataForm_VIDEO_INIT_INPUT = {
  kind: "VIDEO_INIT_INPUT";
  url: string;
};

export type ContentProductionStepDataStepDataUnparsedForm_VIDEO_INIT_INPUT = {
  kind: "VIDEO_INIT_INPUT";
  url: string;
};

export type ContentProductStepVideoInitInputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_VIDEO_INIT_INPUT };

export type ContentProductionApplyParentalSettingsOutput = {
  crudeLanguage: boolean;
  crudeLanguageReason: string | null;
  political: boolean;
  politicalReason: string | null;
  religious: boolean;
  religiousReason: string | null;
};

export type ContentProductionAuthorFormForm = {
  biography: string | null;
  contentProductionAuthorId: bigint;
  credibilityRating: bigint;
  editorialTeam?: boolean;
  firstName: string | null;
  lastName: string;
  middleName?: string;
};

export type ContentProductionAuthorFormSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: ContentProductionAuthorFormForm };

export type ContentProductionAuthorFormUnparsedForm = {
  biography: string;
  contentProductionAuthorId: bigint;
  credibilityRating: string;
  editorialTeam: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
};

export type ContentProductionAuthorFormValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionAuthorFormForm };

export type ContentProductionAuthorItem = {
  biography: string | null;
  contentProductionAuthorId: bigint;
  credibilityRating: number | null;
  editorialTeam: boolean;
  firstName: string | null;
  lastName: string;
  middleName: string | null;
};

export namespace ContentProductionErrorCategory {
  const values = ["SERVER_ERROR", "FORM_ERROR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionErrorCategory = ContentProductionErrorCategory.Impl;

export type ContentProductionFilter = {
  assignee: number | null;
  contentProductionSummaryId: bigint | null;
  costsGreaterThan: number | null;
  createdAtFrom: { year: number; month: number; day: number } | null;
  createdAtTo: { year: number; month: number; day: number } | null;
  editedNotPublished: boolean;
  errorCategory: ContentProductionErrorCategory | null;
  excludeWaitingAt: ContentProductionStepType | null;
  formatType: ContentProductionFormatType | null;
  includeRejected: boolean;
  minOverallRating: number | null;
  pipeline: ContentProductionPipeline | null;
  skillGroupChannelId: bigint | null;
  status: string | null;
  stepType: ContentProductionStepType | null;
};

export namespace ContentProductionFilterStatus {
  const values = ["PROOF_READING", "STUCK", "PUBLISHED", "RUNNING", "AWAITING_OUTPUT_FORMAT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionFilterStatus = ContentProductionFilterStatus.Impl;

export namespace ContentProductionFormatType {
  const values = ["ARTICLE", "BLOGPOST", "PODCAST", "VIDEO", "REPORT", "BOOK"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionFormatType = ContentProductionFormatType.Impl;

export type ContentProductionInformativeSummaryContent = {
  aboutTheAuthor: string;
  recommendation: string;
};

export type ContentProductionMarkdown = {
  languageOfMarkdown: Language;
  markdown: string;
};

export type ContentProductionMetadataTitleRewrite = {
  subtitle: string;
  title: string;
};

export namespace ContentProductionPipeline {
  const values = ["AUTO_V2", "MANUAL_OUTPUT_V2", "BOOK_V2", "AUTO_V1", "MANUAL_OUTPUT_V1", "BOOK_V1", "AUTO_V0", "MANUAL_OUTPUT_V0", "BOOK_V0"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionPipeline = ContentProductionPipeline.Impl;

export type ContentProductionProofreadSummaryOutput = {
  feedback: string;
  issues: string[];
  passed: boolean;
};

export type ContentProductionPublishSummaryOutput = {
  summaryId: string;
};

export type ContentProductionQuantitativeChecksOutput = {
  issues: string[];
  passed: boolean;
};

export type ContentProductionSourceFormForm = {
  blacklisted?: boolean;
  contentProductionSourceId: bigint;
  logo: ExtFormFile;
  sourceTitle: string;
  sourceUrl: string;
  trustworthinessRating: bigint;
};

export type ContentProductionSourceFormSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: ContentProductionSourceFormForm };

export type ContentProductionSourceFormUnparsedForm = {
  blacklisted: boolean;
  contentProductionSourceId: bigint;
  logo: ExtFormFile;
  sourceTitle: string;
  sourceUrl: string;
  trustworthinessRating: string;
};

export type ContentProductionSourceFormValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionSourceFormForm };

export type ContentProductionSourceItem = {
  blacklisted: boolean;
  contentProductionSourceId: bigint;
  logoUri: string | null;
  sourceTitle: string;
  sourceUrl: string;
  trustworthinessRating: number | null;
};

export namespace ContentProductionSourceType {
  const values = ["PDF", "VIDEO", "HTML", "PODCAST"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionSourceType = ContentProductionSourceType.Impl;

export type ContentProductionStats = {
  awaitingOutputFormatPipelineCount: number;
  awaitingProofreadPipelineCount: number;
  editedPipelineCount: number;
  publishedLast30DaysCount: number;
  publishedPipelineCount: number;
  runningPipelineCount: number;
  stepStats: ContentProductionStepStat[];
  stuckBreakdown: ContentProductionStuckBreakdownEntry[];
  stuckPipelineCount: number;
};

export namespace ContentProductionStatus {
  const values = ["IDLE", "SUCCESS", "FAILED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionStatus = ContentProductionStatus.Impl;

export type ContentProductionStep = {
  assignee: number | null;
  comment: string | null;
  contentProductionStepId: bigint;
  costs: number;
  createdAt: DateTime<true>;
  data: ContentProductionStepData;
  error: string | null;
  manualStep: boolean;
  pipeline: ContentProductionPipeline;
  rejected: boolean;
  status: ContentProductionStatus;
  stepType: ContentProductionStepType;
  title: string | null;
};

export type ContentProductionStepDataStepDataForm_AUTHOR_OUTPUT = {
  authors: AuthorForm[];
  kind: "AUTHOR_OUTPUT";
};

export type ContentProductionStepDataStepDataUnparsedForm_AUTHOR_OUTPUT = {
  authors: AuthorUnparsedForm[];
  kind: "AUTHOR_OUTPUT";
};

export type ContentProductionStepAuthorOutputValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm_AUTHOR_OUTPUT };

export type ContentProductionStepCommentForm = {
  comment: string | null;
};

export type ContentProductionStepData = {
  input: ContentProductionStepDataListUnparsedForm;
  output: ContentProductionStepDataListUnparsedForm;
  stepConfig: ContentProductionStepDataUnparsedForm | null;
};

export type ContentProductionStepDataForm = {
  actor: bigint | null;
  contentProductionStepId: bigint;
  createdAt: bigint | null;
  dataId: bigint | null;
  error: string | null;
  log: string | null;
  parentId: bigint | null;
  stepData: ContentProductionStepDataStepDataForm;
  type: string;
};

export type ContentProductionStepDataListForm = {
  data: ContentProductionStepDataForm[];
};

export type ContentProductionStepDataListSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: ContentProductionStepDataListForm };

export type ContentProductionStepDataListUnparsedForm = {
  data: ContentProductionStepDataUnparsedForm[];
};

export type ContentProductionStepDataListValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataListForm };

export type ContentProductionStepDataPreviousVersionResult = { kind: "NO_PREVIOUS_VERSION" } | { kind: "SUCCESS"; data: ContentProductionStepDataUnparsedForm };

export type ContentProductionStepDataStepDataForm =
  | { kind: "PDF_INIT_INPUT"; fileData: FileDataForm; pdf: ExtFormFile; uploaderCustomerId: bigint }
  | { kind: "PDF_INIT_OUTPUT"; fileData: FileDataForm; pdf: ExtFormFile }
  | { kind: "PDF_EXTRACTION_CONFIG"; languageDetectionPrompt?: string; metadataPrompt: string; prompt: string; rewriteTitlePrompt?: string }
  | { kind: "BOOK_INIT_INPUT"; fileData: FileDataForm; manuscript: ExtFormFile; uploaderCustomerId: bigint }
  | { kind: "BOOK_INIT_OUTPUT"; fileData: FileDataForm; manuscript: ExtFormFile }
  | { kind: "BOOK_EXTRACTION_CONFIG"; chapterAtomizationPrompt: string; languageDetectionPrompt?: string; metadataPrompt: string; synthesisPrompt: string }
  | { kind: "VIDEO_INIT_INPUT"; url: string }
  | { kind: "VIDEO_INIT_CONFIG"; authorPrompt: string; languageDetectionPrompt?: string; rewriteTitlePrompt?: string; speakerLabelingPrompt: string }
  | { kind: "HTML_INIT_INPUT"; url: string }
  | { kind: "HTML_INIT_CONFIG"; authorPrompt: string; languageDetectionPrompt?: string; markdownCleanupPrompt: string; publicationDatePrompt: string; rewriteTitlePrompt?: string }
  | { kind: "EXTRACTED_OUTPUT"; originalText: string; screenshot: ExtFormFile }
  | { kind: "MARKDOWN_OUTPUT"; markdown: string }
  | {
      kind: "METADATA_OUTPUT";
      contentLanguage?: string;
      description: string | null;
      formatType: ContentProductionFormatType;
      icon: ExtFormFile;
      publicationDate: { year: number; month: number; day: number };
      resourceUrl: string;
      sourceTitle: string | null;
      sourceUrl: string;
      subtitle: string | null;
      title: string;
    }
  | { kind: "METADATA_OUTPUT_DEPRECATED"; authors: AuthorForm[] }
  | { kind: "AUTHOR_OUTPUT"; authors: AuthorForm[] }
  | {
      kind: "CONTENT_RATING_OUTPUT";
      actionabilityScore?: number;
      actionabilitySummary?: string;
      credibilityScore?: number;
      credibilitySummary?: string;
      expertiseOriginalityScore?: number;
      expertiseOriginalitySummary?: string;
      qualitySummary?: string;
      rating: bigint;
      redFlagsScore?: number;
      redFlagsSummary?: string;
      substantivenessScore?: number;
      substantivenessSummary?: string;
      targetAudience: string;
    }
  | { kind: "CONTENT_RATING_CONFIG"; prompt: string }
  | { kind: "TEE_VERIFICATION_OUTPUT"; teeVerificationError: string | null; teeVerificationStatus: string }
  | { kind: "TEE_PROOF_DATA"; teeProofDataJson: string }
  | {
      kind: "SOURCE_RATING_OUTPUT";
      contentProductionSourceId?: bigint;
      expertiseAreas: string | null;
      expertiseScore?: number;
      expertiseSummary?: string;
      independenceScore?: number;
      independenceSummary?: string;
      logoUri?: string;
      primaryFocus: string | null;
      productionQualityScore?: number;
      productionQualitySummary?: string;
      rating: bigint;
      recommendation: string;
      reputationScore?: number;
      reputationSummary?: string;
      sourceCategory: string | null;
      sourceTitle?: string;
      sourceUrl?: string;
    }
  | { kind: "SOURCE_RATING_CONFIG"; prompt: string }
  | { kind: "AUTHOR_RATING_OUTPUT"; authors: AuthorRatingDetailForm[]; averageRating?: bigint }
  | { kind: "AUTHOR_RATING_CONFIG"; prompt: string }
  | { kind: "TAKEAWAYS_OUTPUT"; takeaways: TakeawaysForm[] }
  | { kind: "TAKEAWAYS_CONFIG"; prompt: string }
  | { kind: "SUMMARIZE_OUTPUT"; language?: string; markdown: string }
  | { kind: "SUMMARIZE_CONFIG"; articleInstruction: string; blogpostInstruction: string; bookInstruction: string; podcastInstruction: string; prompt: string; reportInstruction: string; updatePrompt: string; videoInstruction: string }
  | { kind: "SUMMARY_INFO_OUTPUT"; aboutTheAuthor: string; recommendation: string }
  | { kind: "SUMMARY_INFO_CONFIG"; prompt: string }
  | { kind: "QUANTITATIVE_CHECKS_OUTPUT"; passed: boolean }
  | { kind: "QUANTITATIVE_CHECKS_CONFIG"; maxQuoteCount: bigint; maxTakeawayWordCount: bigint; maxWordCount: bigint; minQuoteCount: bigint; minWordCount: bigint }
  | { kind: "PROOFREAD_SUMMARY_OUTPUT"; approved: boolean; evergreen: boolean }
  | { kind: "SUMMARY_GENERATING_OUTPUT"; approved: boolean }
  | { kind: "RATING_RATIONALE_OUTPUT"; rationale: string }
  | { kind: "RATING_RATIONALE_CONFIG"; prompt: string }
  | {
      kind: "TRANSLATE_EN_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_DE_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_ES_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_PT_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_FR_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_RU_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_ZH_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | {
      kind: "TRANSLATE_AR_OUTPUT";
      aboutTheAuthor: string | null;
      authorTranslations: AuthorTranslationForm[];
      languageCode: string;
      rationale: string | null;
      recommendation: string | null;
      subtitle: string | null;
      summaryMarkdown: string | null;
      takeaways: TakeawaysForm[];
      title: string | null;
      whatYouWillLearn: WhatYouWillLearnForm[];
    }
  | { kind: "TRANSLATE_CONFIG"; authorPrompt: string; summaryFieldsPrompt: string; summaryLanguageInstructions: string | null; summaryMarkdownPrompt: string; translationCheckPrompt: string; translationFixPrompt?: string }
  | { kind: "PUBLISH_SUMMARY_OUTPUT"; publishedAt?: bigint; resourceId?: bigint; resourceUuid?: string; summaryId: bigint }
  | { kind: "RESOURCE_OUTPUT"; resourceId: bigint; resourceUuid?: string }
  | { kind: "AI_BRIEF_OUTPUT"; markdown: string | null }
  | { kind: "AI_BRIEF_CONFIG"; prompt: string }
  | { kind: "AI_PROOFREAD_SUMMARY_OUTPUT"; passed: boolean }
  | { kind: "AI_PROOFREAD_SUMMARY_CONFIG"; prompt: string }
  | { kind: "FIDELITY_CHECK_OUTPUT"; passed: boolean }
  | { kind: "FIDELITY_CHECK_CONFIG"; prompt: string }
  | { kind: "WHAT_YOU_WILL_LEARN_OUTPUT"; whatYouWillLearn: WhatYouWillLearnForm[] }
  | { kind: "WHAT_YOU_WILL_LEARN_CONFIG"; prompt: string }
  | { kind: "ASSIGN_SKILLS_OUTPUT"; skills: AssignedSkillForm[]; taxonomyGapCandidate: boolean }
  | { kind: "ASSIGN_SKILLS_CONFIG"; maxPerCompetency: bigint; maxSkills: bigint; maxTextChars: bigint; minProbability: number; model: string; window: number }
  | { kind: "APPLY_PARENTAL_SETTINGS_OUTPUT"; crudeLanguage: boolean; political: boolean; religious: boolean }
  | { kind: "APPLY_PARENTAL_SETTINGS_CONFIG"; prompt: string }
  | { kind: "NO_LONGER_SUPPORTED"; data: string }
  | { kind: "SKELETON_DATA"; data: string };

export type ContentProductionStepDataStepDataUnparsedForm =
  | { kind: "PDF_INIT_INPUT"; fileData: FileDataUnparsedForm; pdf: ExtFormFile; uploaderCustomerId: string }
  | { kind: "PDF_INIT_OUTPUT"; fileData: FileDataUnparsedForm; pdf: ExtFormFile }
  | { kind: "PDF_EXTRACTION_CONFIG"; languageDetectionPrompt: string; metadataPrompt: string; prompt: string; rewriteTitlePrompt: string }
  | { kind: "BOOK_INIT_INPUT"; fileData: FileDataUnparsedForm; manuscript: ExtFormFile; uploaderCustomerId: string }
  | { kind: "BOOK_INIT_OUTPUT"; fileData: FileDataUnparsedForm; manuscript: ExtFormFile }
  | { kind: "BOOK_EXTRACTION_CONFIG"; chapterAtomizationPrompt: string; languageDetectionPrompt: string; metadataPrompt: string; synthesisPrompt: string }
  | { kind: "VIDEO_INIT_INPUT"; url: string }
  | { kind: "VIDEO_INIT_CONFIG"; authorPrompt: string; languageDetectionPrompt: string; rewriteTitlePrompt: string; speakerLabelingPrompt: string }
  | { kind: "HTML_INIT_INPUT"; url: string }
  | { kind: "HTML_INIT_CONFIG"; authorPrompt: string; languageDetectionPrompt: string; markdownCleanupPrompt: string; publicationDatePrompt: string; rewriteTitlePrompt: string }
  | { kind: "EXTRACTED_OUTPUT"; originalText: string; screenshot: ExtFormFile }
  | { kind: "MARKDOWN_OUTPUT"; markdown: string }
  | {
      kind: "METADATA_OUTPUT";
      contentLanguage: string;
      description: string;
      formatType: ContentProductionFormatType | null;
      icon: ExtFormFile;
      publicationDate: string;
      resourceUrl: string;
      sourceTitle: string;
      sourceUrl: string;
      subtitle: string;
      title: string;
    }
  | { kind: "METADATA_OUTPUT_DEPRECATED"; authors: AuthorUnparsedForm[] }
  | { kind: "AUTHOR_OUTPUT"; authors: AuthorUnparsedForm[] }
  | {
      kind: "CONTENT_RATING_OUTPUT";
      actionabilityScore: string;
      actionabilitySummary: string;
      credibilityScore: string;
      credibilitySummary: string;
      expertiseOriginalityScore: string;
      expertiseOriginalitySummary: string;
      qualitySummary: string;
      rating: string;
      redFlagsScore: string;
      redFlagsSummary: string;
      substantivenessScore: string;
      substantivenessSummary: string;
      targetAudience: string;
    }
  | { kind: "CONTENT_RATING_CONFIG"; prompt: string }
  | { kind: "TEE_VERIFICATION_OUTPUT"; teeVerificationError: string; teeVerificationStatus: string }
  | { kind: "TEE_PROOF_DATA"; teeProofDataJson: string }
  | {
      kind: "SOURCE_RATING_OUTPUT";
      contentProductionSourceId: string;
      expertiseAreas: string;
      expertiseScore: string;
      expertiseSummary: string;
      independenceScore: string;
      independenceSummary: string;
      logoUri: string;
      primaryFocus: string;
      productionQualityScore: string;
      productionQualitySummary: string;
      rating: string;
      recommendation: string;
      reputationScore: string;
      reputationSummary: string;
      sourceCategory: string;
      sourceTitle: string;
      sourceUrl: string;
    }
  | { kind: "SOURCE_RATING_CONFIG"; prompt: string }
  | { kind: "AUTHOR_RATING_OUTPUT"; authors: AuthorRatingDetailUnparsedForm[]; averageRating: string }
  | { kind: "AUTHOR_RATING_CONFIG"; prompt: string }
  | { kind: "TAKEAWAYS_OUTPUT"; takeaways: TakeawaysUnparsedForm[] }
  | { kind: "TAKEAWAYS_CONFIG"; prompt: string }
  | { kind: "SUMMARIZE_OUTPUT"; language: string; markdown: string }
  | { kind: "SUMMARIZE_CONFIG"; articleInstruction: string; blogpostInstruction: string; bookInstruction: string; podcastInstruction: string; prompt: string; reportInstruction: string; updatePrompt: string; videoInstruction: string }
  | { kind: "SUMMARY_INFO_OUTPUT"; aboutTheAuthor: string; recommendation: string }
  | { kind: "SUMMARY_INFO_CONFIG"; prompt: string }
  | { kind: "QUANTITATIVE_CHECKS_OUTPUT"; passed: boolean }
  | { kind: "QUANTITATIVE_CHECKS_CONFIG"; maxQuoteCount: string; maxTakeawayWordCount: string; maxWordCount: string; minQuoteCount: string; minWordCount: string }
  | { kind: "PROOFREAD_SUMMARY_OUTPUT"; approved: boolean; evergreen: boolean }
  | { kind: "SUMMARY_GENERATING_OUTPUT"; approved: boolean }
  | { kind: "RATING_RATIONALE_OUTPUT"; rationale: string }
  | { kind: "RATING_RATIONALE_CONFIG"; prompt: string }
  | {
      kind: "TRANSLATE_EN_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_DE_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_ES_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_PT_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_FR_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_RU_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_ZH_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | {
      kind: "TRANSLATE_AR_OUTPUT";
      aboutTheAuthor: string;
      authorTranslations: AuthorTranslationUnparsedForm[];
      languageCode: string;
      rationale: string;
      recommendation: string;
      subtitle: string;
      summaryMarkdown: string;
      takeaways: TakeawaysUnparsedForm[];
      title: string;
      whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
    }
  | { kind: "TRANSLATE_CONFIG"; authorPrompt: string; summaryFieldsPrompt: string; summaryLanguageInstructions: string; summaryMarkdownPrompt: string; translationCheckPrompt: string; translationFixPrompt: string }
  | { kind: "PUBLISH_SUMMARY_OUTPUT"; publishedAt: string; resourceId: string; resourceUuid: string; summaryId: string }
  | { kind: "RESOURCE_OUTPUT"; resourceId: string; resourceUuid: string }
  | { kind: "AI_BRIEF_OUTPUT"; markdown: string }
  | { kind: "AI_BRIEF_CONFIG"; prompt: string }
  | { kind: "AI_PROOFREAD_SUMMARY_OUTPUT"; passed: boolean }
  | { kind: "AI_PROOFREAD_SUMMARY_CONFIG"; prompt: string }
  | { kind: "FIDELITY_CHECK_OUTPUT"; passed: boolean }
  | { kind: "FIDELITY_CHECK_CONFIG"; prompt: string }
  | { kind: "WHAT_YOU_WILL_LEARN_OUTPUT"; whatYouWillLearn: WhatYouWillLearnUnparsedForm[] }
  | { kind: "WHAT_YOU_WILL_LEARN_CONFIG"; prompt: string }
  | { kind: "ASSIGN_SKILLS_OUTPUT"; skills: AssignedSkillUnparsedForm[]; taxonomyGapCandidate: boolean }
  | { kind: "ASSIGN_SKILLS_CONFIG"; maxPerCompetency: string; maxSkills: string; maxTextChars: string; minProbability: string; model: string; window: string }
  | { kind: "APPLY_PARENTAL_SETTINGS_OUTPUT"; crudeLanguage: boolean; political: boolean; religious: boolean }
  | { kind: "APPLY_PARENTAL_SETTINGS_CONFIG"; prompt: string }
  | { kind: "NO_LONGER_SUPPORTED"; data: string }
  | { kind: "SKELETON_DATA"; data: string };

export type ContentProductionStepDataStepDataValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataStepDataForm };

export type ContentProductionStepDataUnparsedForm = {
  actor: bigint | null;
  contentProductionStepId: bigint;
  createdAt: bigint | null;
  dataId: bigint | null;
  error: string | null;
  log: string | null;
  parentId: bigint | null;
  stepData: ContentProductionStepDataStepDataUnparsedForm;
  type: string;
};

export type ContentProductionStepDataValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ContentProductionStepDataForm };

export type ContentProductionStepId = {
  contentProductionStepId: bigint;
};

export type ContentProductionStepList = {
  assignees: Assignee[];
  historyIncluded: boolean;
  versions: ContentProductionVersionedStepEntry[];
};

export type ContentProductionStepRejectedForm = {
  rejected: boolean;
};

export type ContentProductionStepStat = {
  editedPipelineCount: number;
  stepType: ContentProductionStepType;
};

export namespace ContentProductionStepType {
  const values = [
    "AI_BRIEF",
    "AI_PROOFREAD_SUMMARY",
    "APPLY_PARENTAL_SETTINGS",
    "ASSIGN_SKILLS",
    "AUTHOR_RATING",
    "BOOK_EXTRACTION",
    "BOOK_INIT",
    "CONTENT_RATING",
    "DEFINE_OUTPUT_FORMAT",
    "FIDELITY_CHECK",
    "HTML_INIT",
    "PDF_EXTRACTION",
    "PDF_INIT",
    "PROOFREAD_SUMMARY",
    "PUBLISH_SUMMARY",
    "QUANTITATIVE_CHECKS",
    "RATING_RATIONALE",
    "RATING_RATIONALE_V2",
    "SOURCE_RATING",
    "STORE_RESOURCE",
    "SUMMARIZE",
    "SUMMARY_INFO",
    "TAKEAWAYS",
    "TRANSLATE_AR",
    "TRANSLATE_DE",
    "TRANSLATE_EN",
    "TRANSLATE_ES",
    "TRANSLATE_FR",
    "TRANSLATE_PT",
    "TRANSLATE_RU",
    "TRANSLATE_ZH",
    "VIDEO_INIT",
    "WHAT_YOU_WILL_LEARN",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContentProductionStepType = ContentProductionStepType.Impl;

export type ContentProductionStepView = {
  assignee: Assignee | null;
  comment: string | null;
  contentProductionStepId: bigint;
  costs: number;
  createdAt: DateTime<true>;
  currentSteps: ContentProductionStep[];
  data: string;
  pipeline: ContentProductionPipeline;
  published: boolean;
  rejected: boolean;
  stepType: ContentProductionStepType;
  summaryId: string | null;
  title: string | null;
};

export type ContentProductionStepViewList = {
  items: ContentProductionStepView[];
  possibleAssignees: Assignee[];
};

export type ContentProductionStuckBreakdownEntry = {
  errorCategory: ContentProductionErrorCategory;
  pipelineCount: number;
  stepType: ContentProductionStepType;
};

export type ContentProductionSummaryUpdate = {
  bigChanges: boolean;
  languageOfMarkdown: Language;
  markdown: string;
};

export type ContentProductionVerifySourceFidelityOutput = {
  guidance: string;
  inventedPhrases: string[];
  passed: boolean;
  unsupportedClaims: string[];
};

export type ContentProductionVersionedStepEntry = {
  items: ContentProductionStep[];
  version: bigint;
};

export type ContentRating = {
  evaluable: boolean;
  qualitySummary: string;
  scores: {
    actionability: {
      reasoning: string;
      score: number;
      summary: string;
    };
    credibility: {
      reasoning: string;
      score: number;
      summary: string;
    };
    expertiseOriginality: {
      reasoning: string;
      score: number;
      summary: string;
    };
    redFlags: {
      reasoning: string;
      score: number;
      summary: string;
    };
    substantiveness: {
      reasoning: string;
      score: number;
      summary: string;
    };
  };
  targetAudience: "BEGINNER" | "INTERMEDIATE" | "ADVANCED" | "EXPERT";
};

export type ContentRatingOutput = {
  qualityGrade: number;
  qualitySummary: string;
  scores: {
    actionability: ScoreWithReasoning;
    credibility: ScoreWithReasoning;
    expertiseOriginality: ScoreWithReasoning;
    redFlags: ScoreWithReasoning;
    substantiveness: ScoreWithReasoning;
  };
  targetAudience: "BEGINNER" | "INTERMEDIATE" | "ADVANCED" | "EXPERT";
  totalScore: number | null;
};

export namespace ContextFlag {
  const values = ["BETA", "LOGGED_IN", "LOGGED_IN_ANONYMOUS", "STRICT_FREE_TEXT_PRIVACY", "ALPHA"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ContextFlag = ContextFlag.Impl;

export type ContextFlagArr = {
  items: ContextFlag[];
};

export type CopilotSetupPageProps = {
  companyName: string | null;
  errorMessage: string | null;
  hasLicense: boolean;
  isLoggedIn: boolean;
  successMessage: string | null;
  urls: {
    consentUrl: string;
    contactUrl: string;
    learnMoreUrl: string;
  };
};

export type CorporateCommentForm = {
  comment: string;
};

export type CorporateCommentListView = {
  comment: string;
  corporateCommentId: number;
  createdAt: DateTime<true>;
  fullName: string;
};

export type CorporateContactForm = {
  email: string;
  firstName: string;
  functionStr: string | null;
  lastName: string;
  resource: CorporateContactResource | null;
  telephone: string | null;
};

export type CorporateContactListView = {
  corporateContactId: number;
  corporateId: number;
  email: string;
  firstName: string;
  functionStr: string | null;
  lastName: string;
  resource: CorporateContactResource;
  telephone: string | null;
};

export type CorporateContactListViewList = {
  contacts: CorporateContactListView[];
};

export namespace CorporateContactResource {
  const values = ["SSO", "LIBRARY_API"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CorporateContactResource = CorporateContactResource.Impl;

export type CorporateCustomerEditForm = {
  company: string | null;
  confirmEmail: string | null;
  corporateId: number;
  countryCode: string | null;
  customerId: number;
  duration: ("ONE_WEEK" | "TWO_WEEKS" | "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS" | "NINE_MONTHS" | "ONE_YEAR" | "TWO_YEARS" | "NO_END_DATE") | null;
  email: string | null;
  endDate: { year: number; month: number; day: number } | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string | null;
  language: Language;
  lastName: string | null;
  nameId: string | null;
  password: string | null;
  pushFrequency: "NONE" | "DAILY" | "ONCE_A_WEEK" | "TWICE_A_MONTH" | "ONCE_A_MONTH";
  repeatPassword: string | null;
  selectedUserGroupIds: number[];
  sendWelcomeEmail: boolean;
  startDate: { year: number; month: number; day: number } | null;
  title: number | null;
};

export type CorporateCustomerExportListView = {
  country: string | null;
  customerId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

export type CorporateCustomerExportParameters = {
  corporateCustomerFilters: CorporateCustomerFilters;
};

export type CorporateCustomerFilters = {
  corporateCustomerStatus: CorporateCustomerStatus | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  modifiedFrom: DateTime<true> | null;
};

export type CorporateCustomerHistoryInfo = {
  corporateId: number;
  customerId: number;
  deviceType: string | null;
  emailPushNotificationView: EmailPushNotificationView;
  emailPushNotificationsCount: number;
  fullName: string;
  lastMobileUsage: { year: number; month: number; day: number } | null;
  lastWebUsage: { year: number; month: number; day: number } | null;
  pullDownloadsCount: number;
  pushFrequency: PushFrequency | null;
  rolesAssigned: string[];
  signupSource: string | null;
  version: string | null;
  webUsagesCount: number;
};

export type CorporateCustomerListView = {
  active: boolean;
  company: string | null;
  country: string | null;
  customerId: number;
  email: string;
  endDate: { year: number; month: number; day: number };
  externalId: string | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string | null;
  insertDate: DateTime<true>;
  language: Language;
  lastMobileUsage: DateTime<true> | null;
  lastModifiedAt: DateTime<true>;
  lastName: string | null;
  lastWebUsageAt: DateTime<true> | null;
  middleName: string | null;
  nameId: string | null;
  randomCode: string | null;
  signupSource: string | null;
  startDate: { year: number; month: number; day: number };
  status: "NEW" | "EXPIRED" | "DELETED" | "NONE";
  title: number;
  userGroups: UserGroup[];
};

export type CorporateCustomerSaveResponse = { kind: "SUCCESS" } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "NOT_ALLOWED_ERROR"; message: string };

export namespace CorporateCustomerStatus {
  const values = ["ALL", "ACTIVE", "ACTIVE_AND_EXPIRED_LAST_30_DAYS", "ACTIVE_AND_EXPIRING_SOON", "EXPIRED", "DELETED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CorporateCustomerStatus = CorporateCustomerStatus.Impl;

export type CorporateIds = {
  corporateIds: number[];
};

export type CorporateLicences = {
  consumedLicences: number;
  limitedLicences: boolean;
  numberLicences: number;
  unidentifiedUsers: number;
};

export type CorporationLecacyForm = {
  info: string;
};

export type CorporationListView = {
  active: boolean;
  corporateId: number;
  corporation: string;
  username: string;
};

export type CorporationListViewList = {
  corporations: CorporationListView[];
};

export type CorporationNameMap = {
  corporations: { additionalProperties: Record<string, string> };
};

export type CorporationPortalAdminView = {
  active: boolean;
  corporateId: number;
  corporation: string;
  lcResponsible: CustomerView | null;
  logoUrl: string | null;
  portalUrl: string;
  signupUrl: string | null;
  username: string;
};

export type CorporationSamlLogListView = {
  authenticationRequest: string;
  corporationSamlLogId: number;
  exception: string;
  loggedAt: DateTime<true>;
  relayState: string;
  samlResponse: string;
  success: boolean;
};

export type CorporationSamlLogListViewArray = {
  arr: CorporationSamlLogListView[];
};

export type CorporationSettingView = {
  checkSso: boolean;
  corporateId: number;
  customFields: CustomFieldSettingView[];
  defaultPushFrequency: PushFrequency | null;
  features: FeatureType[];
  languages: Language[];
  librariesEnabled: ContentLibrary[];
  manualUserImportAuthorized: boolean;
  provisioning: boolean;
  pushFrequencies: PushFrequency[];
  quickSightDashboardId: number | null;
  showPushFrequencyBusiness: boolean;
  subscribeLink: boolean;
  type: CorporationType;
  userGroups: UserGroup[];
};

export namespace CorporationType {
  const values = ["ASP_SOLUTION", "LEAD_PORTAL_B2C", "AFFILIATE", "LEAD_PORTAL_B2B", "NOT_DEFINED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CorporationType = CorporationType.Impl;

export type Countries = {
  items: Country[];
};

export type Country = {
  countryCode: string;
  currency: "CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY";
  names: {
    ar: string;
    de: string;
    en: string;
    es: string;
    fr: string;
    pt: string;
    ru: string;
    zh: string;
  };
  phonePrefix: string;
  region: "SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA";
};

export type CountryRestrictedTag = {
  countryCodes: string | null;
  tag: Tag;
};

export type CountryRestrictedTagArray = {
  arr: CountryRestrictedTag[];
};

export namespace CountryRestrictionType {
  const values = ["INCLUDED", "EXCLUDED", "NO_RESTRICTION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CountryRestrictionType = CountryRestrictionType.Impl;

export type CreateProductBookForm = {
  manuscript: ExtFormFile;
};

export type CreateProductBookSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: CreateProductBookForm };

export type CreateProductBookUnparsedForm = {
  manuscript: ExtFormFile;
};

export type CreateProductBookValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: CreateProductBookForm };

export type CreateProductCSVForm = {
  csv: ExtFormFile;
  pipeline: ContentProductionPipeline;
};

export type CreateProductCSVSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: CreateProductCSVForm };

export type CreateProductCSVUnparsedForm = {
  csv: ExtFormFile;
  pipeline: ContentProductionPipeline | null;
};

export type CreateProductCSVValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: CreateProductCSVForm };

export type CreateProductPDFForm = {
  pdf: ExtFormFile;
  pipeline: ContentProductionPipeline;
};

export type CreateProductPDFSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: CreateProductPDFForm };

export type CreateProductPDFUnparsedForm = {
  pdf: ExtFormFile;
  pipeline: ContentProductionPipeline | null;
};

export type CreateProductPDFValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: CreateProductPDFForm };

export type CreateProductURLForm = {
  pipeline: ContentProductionPipeline;
  url: string;
};

export type CreateProductURLSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: CreateProductURLForm };

export type CreateProductURLUnparsedForm = {
  pipeline: ContentProductionPipeline | null;
  url: string;
};

export type CreateProductURLValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: CreateProductURLForm };

export type CreditCardPaymentView = {
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  batchRefNumber: string | null;
  creditCardAlias: string | null;
  creditCardNumber: string;
  creditCardType: CreditCardType;
  datatransMerchantId: string | null;
  expirationMonth: number;
  expirationYear: number;
  gatewayProvider: string;
  nameOnCard: string | null;
  refNumber: string | null;
  transactionDateTime: DateTime<true>;
  transactionId: number;
};

export namespace CreditCardType {
  const values = ["VISA", "MASTERCARD", "AMEXCO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CreditCardType = CreditCardType.Impl;

export type CroppingInfo = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export type CsodConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "CSOD";
  userId: string;
  userIdentifier: ConnectorUserIdentifier;
};

export namespace CurationContentType {
  const values = ["ARTICLE", "VIDEO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CurationContentType = CurationContentType.Impl;

export type CurationForm = {
  contentType: CurationContentType[];
  moduleApply: CurationModuleApply[];
  moduleExplore: CurationModuleExplore[];
  moduleLearn: CurationModuleLearn[];
  recency: CurationRecency;
  searchTerm: string;
};

export type CurationItemList = {
  items: ContentCurationItem[];
};

export namespace CurationModuleApply {
  const values = ["CASE_STUDY", "PLAYBOOK", "WALKTHROUGH", "TOOL_REVIEW", "LESSONS_LEARNED", "CAREE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CurationModuleApply = CurationModuleApply.Impl;

export namespace CurationModuleExplore {
  const values = ["REPORT", "TREND", "PREDICTION", "DEEP_DIVE", "PODCAST", "BENCHMARK"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CurationModuleExplore = CurationModuleExplore.Impl;

export namespace CurationModuleLearn {
  const values = ["EXPLAINER", "TUTORIAL", "RESEARCH", "FRAMEWORK"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CurationModuleLearn = CurationModuleLearn.Impl;

export namespace CurationRecency {
  const values = ["3MONTHS", "6MONTHS", "1YEAR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CurationRecency = CurationRecency.Impl;

export type CurationSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: CurationForm };

export type CurationUnparsedForm = {
  contentType: CurationContentType[];
  moduleApply: CurationModuleApply[];
  moduleExplore: CurationModuleExplore[];
  moduleLearn: CurationModuleLearn[];
  recency: CurationRecency | null;
  searchTerm: string;
};

export type CurationValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: CurationForm };

export type CustomFieldForm = {
  active: boolean;
  description: string | null;
  mandatory: boolean;
  optionsText: string | null;
  titles: { additionalProperties: Record<string, string> };
  type: CustomFieldType;
};

export type CustomFieldListView = {
  active: boolean;
  customFieldId: number;
  description: string | null;
  fieldName: string;
  mandatory: boolean;
  name: string;
  optionItems: string[];
  type: CustomFieldType;
};

export type CustomFieldOption = {
  code: string;
  customFieldId: number;
  value: string | null;
};

export type CustomFieldSettingView = {
  corporateId: number;
  customFieldId: number;
  customFieldType: "TEXT" | "OPTION";
  description: string | null;
  fieldName: string;
  mandatory: boolean;
  options: CustomFieldOption[];
  regexValidation: string | null;
  title: string | null;
};

export namespace CustomFieldType {
  const values = ["TEXT", "OPTION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CustomFieldType = CustomFieldType.Impl;

export type CustomPageAdminSearchForm = {
  includeInactive: boolean;
  onlyHighlighted: boolean;
  searchTerm: string;
};

export type CustomPageCopyRequest = {
  toCorporateId: number;
};

export type CustomPageEditForm = {
  active: boolean;
  customPageLocalization: { additionalProperties: Record<string, CustomPageLocalizationEditForm> };
  defaultCover: boolean;
  headerLayout: CustomPageHeaderLayout;
  portletIds: number[];
};

export type CustomPageEditFormMultipart = {
  cover: ReadonlyUint8Array | null;
  customPageEditForm: CustomPageEditForm;
};

export type CustomPageEditListView = {
  active: boolean;
  corporateId: number;
  corporation: string;
  customPageId: bigint;
  highlightPriority: number | null;
  modifiedAt: DateTime<true>;
  titles: { additionalProperties: Record<string, string> };
};

export type CustomPageEditListViewArray = {
  arr: CustomPageEditListView[];
};

export type CustomPageEditView = {
  active: boolean;
  corporateId: number;
  coverUrl: string;
  customPageId: bigint;
  customPageLocalization: { additionalProperties: Record<string, CustomPageLocalizationEditView> };
  headerLayout: CustomPageHeaderLayout;
  portlets: PortletMetadata[];
};

export type CustomPageFilters = {
  includeInactive: boolean;
  onlyHighlighted: boolean;
};

export namespace CustomPageHeaderLayout {
  const values = ["NONE", "DEFAULT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type CustomPageHeaderLayout = CustomPageHeaderLayout.Impl;

export type CustomPageHighlightsForm = {
  customPageIds: bigint[];
};

export type CustomPageListPortletEditView = {
  custompages: CustomPageListView[];
  portlet: PortletEditView;
};

export type CustomPageListPortletForm = {
  customPageIds: bigint[];
  portletForm: PortletForm;
};

export type CustomPageListPortletView = {
  data: PagedCustomPageListView;
  metadata: PortletMetadata;
  viewAllUri: string;
};

export type CustomPageListView = {
  corporateId: number;
  coverUri: string;
  customPageId: bigint;
  description: string | null;
  title: string;
};

export type CustomPageLocalizationEditForm = {
  description: string | null;
  title: string;
};

export type CustomPageLocalizationEditView = {
  description: string | null;
  title: string;
};

export type CustomPageView = {
  coverUrl: string;
  customPageId: bigint;
  description: string | null;
  headerLayout: CustomPageHeaderLayout;
  portlets: PortletMetadata[];
  title: string;
};

export type CustomerAdminAccountDetailForm = {
  acceptedTerms: DateTime<true> | null;
  customerId: number;
  isTestAccount: boolean;
  lastMobileUsage: DateTime<true> | null;
  lastWebUsage: DateTime<true> | null;
  lockedOutTime: bigint | null;
  loginAsCustomerUri: string;
  roles: CustomerAdminDetailRole[];
  totalWebUsages: number | null;
};

export type CustomerAdminB2BPortalsForm = {
  corporateId: number;
  corporation: string;
  corporationIsActive: boolean;
  customerIsActive: boolean;
  endDate: { year: number; month: number; day: number } | null;
  isExpired: boolean;
  responsibleCsm: string | null;
  responsibleSales: string | null;
  startDate: { year: number; month: number; day: number } | null;
  type: string | null;
};

export type CustomerAdminB2BPortalsForms = {
  forms: CustomerAdminB2BPortalsForm[];
};

export type CustomerAdminDetailAddress = {
  address1: string | null;
  address2: string | null;
  addressId: number;
  company: string | null;
  countryCode: string | null;
  department: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  position: string | null;
  state: string | null;
  title: number;
  town: string | null;
  vat: string | null;
  zipCode: string | null;
};

export type CustomerAdminDetailForm = {
  areaOfExpertise: AreaOfExpertise | null;
  batchEmails: boolean;
  billingAddress: CustomerAdminDetailAddress;
  careerLevel: CareerLevel | null;
  changePassword: boolean;
  commentNotification: boolean;
  customGender: string | null;
  customerId: number;
  education: boolean;
  email: string;
  gender: Gender | null;
  htmlEmail: boolean;
  invoiceByPost: boolean;
  language: Language;
  mailAddress: CustomerAdminDetailAddress;
  password: string;
  pushFrequency: PushFrequency;
  receiveNewsletters: boolean;
  unlimitedAccess: boolean;
  yearOfBirth: number | null;
};

export type CustomerAdminDetailRole = {
  corporation: string;
  customerEmail: string;
  isCorporationGroup: boolean;
  language: string;
  roleName: string;
};

export type CustomerAdminDetailWidgetProps = {
  customerId: number;
};

export type CustomerAdminListView = {
  active: boolean;
  address: string;
  b2bCustomer: boolean;
  country: string;
  customerId: number;
  email: string | null;
  firstName: string;
  lastName: string;
  portalStatuses: PortalStatusListView[];
  subscriptionStartDate: { year: number; month: number; day: number } | null;
  subscriptionStatus: string;
  subscriptionType: string;
  town: string;
  trialStartDate: { year: number; month: number; day: number } | null;
};

export type CustomerAdminListViewArray = {
  customerAdminListView: CustomerAdminListView[];
};

export type CustomerAdminMobileAppForm = {
  loginId: number;
  loginPreferences: MobileAppLoginPreferenceListView[];
  mobileDevices: MobileDevice[];
  oAuth2Tokens: OAuth2Token[];
  showLoginPreferences: boolean;
};

export type CustomerAdminQuickSearchResult = {
  count: number;
  uniqueId: number | null;
};

export type CustomerAdminSubscriptionsForm = {
  customerId: number;
  customerLanguageId: number;
  customerName: string;
  customerRandomCode: string;
  emailHistory: B2CRenewalEmails[];
  hasStudentSubscription: boolean;
  studentPreverification: StudentVerificationView | null;
  subscriptions: B2CSubscription[];
};

export type CustomerCommentForm = {
  comment: string;
  commentId: number;
  customerId: number;
  dateEntered: DateTime<true>;
  isMutable: boolean | null;
  operator: string | null;
};

export type CustomerCommentForms = {
  items: CustomerCommentForm[];
};

export type CustomerCommentSaveRequest = {
  comment: string;
};

export type CustomerIds = {
  customerIds: number[];
};

export type CustomerMassDeletionConfirmationView = {
  confirmationMessage: string;
};

export type CustomerMassDeletionEntryForm = {
  anonymize: boolean;
  customerId: number | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  nameId: string | null;
  status: "OK" | "ERROR";
  statusInfo: string | null;
};

export type CustomerMassDeletionForm = {
  items: CustomerMassDeletionEntryForm[];
};

export type CustomerMassDeletionUploadForm = {
  anonymizeAll: boolean;
  type: "DEFAULT" | "DIFFERENCE";
};

export type CustomerMassDeletionUploadFormMultipart = {
  customerMassDeletionUploadForm: CustomerMassDeletionUploadForm;
  file: ReadonlyUint8Array;
};

export type CustomerMassImportEmailSetting = {
  hideUserData: boolean;
  sendMail: boolean;
};

export type CustomerMassImportEntryForm = {
  company: string | null;
  countryCode: string | null;
  customerId: number | null;
  email: string;
  endDate: { year: number; month: number; day: number } | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string;
  functionStr: string | null;
  isNew: boolean;
  language: Language;
  lastName: string;
  nameId: string | null;
  password: string | null;
  state: string | null;
  title: number;
  town: string | null;
  userGroups: number[];
};

export type CustomerMassImportForm = {
  emailSetting: CustomerMassImportEmailSetting;
  items: CustomerMassImportEntryForm[];
  pushFrequencySetting: CustomerMassImportPushFrequencySetting;
};

export type CustomerMassImportProcessResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS" };

export type CustomerMassImportPushFrequencySetting = {
  digestFrequency: PushFrequency;
  updateExistingUserDigestFrequency: boolean;
};

export type CustomerMassImportUploadResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; items: CustomerMassImportView[] };

export type CustomerMassImportView = {
  form: CustomerMassImportEntryForm;
  status: "OK" | "WARNING" | "ERROR";
  statusInfo: string;
};

export type CustomerMyListCreateRequest = {
  dataId: number;
  name: string;
};

export type CustomerMyListSaveRequest = {
  dataId: number;
  op: "ADD" | "REMOVE";
};

export type CustomerReportView = {
  country: string | null;
  customerId: number;
  email: string | null;
  endDate: { year: number; month: number; day: number };
  externalId: string | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string | null;
  functionStr: string | null;
  language: Language;
  lastName: string | null;
  signupDate: { year: number; month: number; day: number };
  state: string | null;
  status: string;
  title: number | null;
  town: string | null;
  userGroups: string[];
};

export type CustomerSearchForm = {
  accountNumber: string;
  creditCardFirstDigits: string;
  creditCardLastDigits: string;
  customerId: number | null;
  email: string;
  firstName: string;
  lastName: string;
  paymentType: "ALL" | "PAYPAL" | "IOS" | "ANDROID" | "CC" | "LSV" | "INVOICE" | "MILES" | "ALIPAY";
  phoneNumber: string;
  refNo: string;
  subscriptionId: number | null;
  transactionEndDate: { year: number; month: number; day: number } | null;
  transactionId: number | null;
  transactionStartDate: { year: number; month: number; day: number } | null;
};

export type CustomerView = {
  customerId: number;
  email: string;
  fullName: string;
};

export type DataPatternDemoSingleRequest = {
  item: number;
};

export type DataPatternDemoUpdateRequest = {
  items: number[];
};

export type DateFormattingDemoProps = {
  values: {
    label: string;
    value: string;
  }[];
};

export type DateRange = {
  from: { year: number; month: number; day: number };
  to: { year: number; month: number; day: number };
};

export type DegreedConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "DEGREED";
  lmsApiUri: string;
  userIdentifier: ConnectorUserIdentifier;
};

export type DemoAddressForm = {
  city: string;
  street: string;
};

export type DemoAddressUnparsedForm = {
  city: string;
  street: string;
};

export type DemoAddressValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: DemoAddressForm };

export type GenerateDemoStructuralContactMethodForm_EMAIL = {
  address: string;
  kind: "EMAIL";
};

export type GenerateDemoStructuralContactMethodUnparsedForm_EMAIL = {
  address: string;
  kind: "EMAIL";
};

export type DemoContactEmailValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoStructuralContactMethodForm_EMAIL };

export type GenerateDemoStructuralContactMethodForm_PHONE = {
  kind: "PHONE";
  mobile: string;
};

export type GenerateDemoStructuralContactMethodUnparsedForm_PHONE = {
  kind: "PHONE";
  mobile: string;
};

export type DemoContactPhoneValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoStructuralContactMethodForm_PHONE };

export type DemoContentDataPatterns = {
  list: string[];
};

export type DemoFormCheckbox = {
  choose: string[];
  obligatoryAccept: boolean;
  option: boolean;
};

export type DemoFormDate = {
  obligatoryDate: { year: number; month: number; day: number };
  publishDate: { year: number; month: number; day: number } | null;
};

export type DemoFormFile = {
  data: ReadonlyUint8Array;
};

export type DemoFormNumber = {
  age: number;
  mayBeEmpty: number | null;
};

export type DemoFormPassword = {
  password: string;
  repeatPassword: string;
};

export type DemoFormRadio = {
  choose: "YES" | "MAYBE" | "NO";
  noDefaultChoose: ("THIS" | "THAT") | null;
};

export type DemoFormSelect = {
  noDefaultSelect: ("FIRST_ENTRY" | "SECOND_ENTRY" | "THIRD_ENTRY" | "FOURTH_ENTRY" | "FIFTH_ENTRY" | "SIXTH_ENTRY" | "SEVENTH_ENTRY" | "EIGHTH_ENTRY" | "NINTH_ENTRY" | "TENTH_ENTRY" | "ELEVENTH_ENTRY" | "TWELFTH_ENTRY") | null;
  select: "RED" | "BLUE" | "GREEN";
};

export type DemoFormText = {
  author: string;
  email: string;
  title: string | null;
};

export type DemoFormTextarea = {
  comment: string;
};

export type DetailPageProps = {
  expertVideoDetailPageProps: ExpertVideoDetailPageProps;
  relatedSummariesDetailPageProps: RelatedSummariesDetailPageProps;
};

export type DisplayMonetaryAmount = {
  amount: number;
  currencyCode: string;
  displayValue: string;
};

export type DoceboCatalogConnectorForm = {
  auth: AuthConnectorForm;
  catalogCode: string;
  categoryCode: string;
  common: CommonConnectorForm;
  connectorType: "DOCEBO_CATALOG";
  password: string;
  username: string;
};

export type DoceboConnectorForm = {
  auth: AuthConnectorForm;
  channelId: number;
  common: CommonConnectorForm;
  connectorType: "DOCEBO";
  password: string;
  username: string;
};

export type DomainProtectionForm = {
  checkDomains: boolean;
  domains: string[];
};

export type DomainProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: DomainProtectionForm };

export namespace DownloadType {
  const values = ["PUSH", "PULL", "BOTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type DownloadType = DownloadType.Impl;

export type DropdownEntry = {
  text: string;
  value: string | null;
};

export type DropdownEntryList = {
  dropDownEntryList: DropdownEntry[];
};

export type DynamicFields = {
  fields: { additionalProperties: Record<string, string> };
};

export type EdcastConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "EDCAST";
  oauthTokenUri: string;
  userIdentifier: ConnectorUserIdentifier;
  xapiActorId: XApiActorId;
};

export type EdcastV2ConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "EDCAST_V2";
  userIdentifier: ConnectorUserIdentifier;
};

export type EmailBatchJobEditForm = {
  active: boolean;
  emailBatchJobId: bigint;
  executeAt: DateTime<true>;
  priority: EmailBatchJobPriority;
};

export type EmailBatchJobForm = {
  emailBatchJobs: EmailBatchJobEditForm[];
  op: "UPDATE";
};

export type EmailBatchJobListView = {
  activatedAt: DateTime<true> | null;
  active: boolean;
  corporateIds: number[];
  countries: string[];
  customerEmail: string;
  customerId: number;
  emailBatchJobId: bigint;
  emailCategoryId: number;
  emailCategoryName: string;
  executeAt: DateTime<true>;
  finishedAt: DateTime<true> | null;
  modifiedAt: DateTime<true> | null;
  notifyEmail: string;
  priority: EmailBatchJobPriority;
  sendMatchingLanguageOnly: boolean;
  sentEmails: bigint;
  status: EmailBatchJobStatus;
  totalEmails: bigint;
  updated: boolean;
};

export namespace EmailBatchJobPriority {
  const values = ["HIGH", "MEDIUM", "LOW"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailBatchJobPriority = EmailBatchJobPriority.Impl;

export namespace EmailBatchJobStatus {
  const values = ["IDLE", "RUNNING", "DONE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailBatchJobStatus = EmailBatchJobStatus.Impl;

export type EmailCategoryFilters = {
  categoryId: number | null;
  categoryName: string | null;
  followUpType: EmailCategoryFollowUpType;
};

export namespace EmailCategoryFollowUpType {
  const values = ["STANDARD", "INTERVAL", "DATEBASED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryFollowUpType = EmailCategoryFollowUpType.Impl;

export type EmailCategoryForm = {
  description: string | null;
  details: EmailCategoryFormDetails;
  name: string;
  repositoryId: number;
  sendCountriesOnly: string[];
  sendMatchingLanguageOnly: boolean;
};

export type EmailCategoryFormDetails =
  | { repositoryType: "DEFAULT" }
  | { repositoryType: "RENEWAL"; active: boolean; maxDaysAfterExpiration: number; minDaysAfterExpiration: number; productIds: number[] }
  | { repositoryType: "EDUCATION"; active: boolean; productIds: number[]; welcomeEmail: boolean }
  | { repositoryType: "ONBOARDING"; active: boolean; maxDaysAfterSignup: number; minDaysAfterSignup: number }
  | {
      repositoryType: "FOLLOWUP";
      active: boolean;
      corporateIds: number[];
      emailingPools: string[];
      followupType: EmailCategoryFollowUpType;
      interval: number;
      maxDaysAfterExpiration: number;
      minDaysAfterExpiration: number;
      sendToAll: boolean;
      startDate: { year: number; month: number; day: number } | null;
    }
  | { repositoryType: "MANUAL"; corporateIds: number[] };

export type EmailCategoryManualSendForm = {
  executionDate: { year: number; month: number; day: number } | null;
  notifyEmail: string;
};

export namespace EmailCategoryTypeDefault {
  const values = ["DEFAULT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeDefault = EmailCategoryTypeDefault.Impl;

export namespace EmailCategoryTypeEducation {
  const values = ["EDUCATION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeEducation = EmailCategoryTypeEducation.Impl;

export namespace EmailCategoryTypeFollowUp {
  const values = ["FOLLOWUP"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeFollowUp = EmailCategoryTypeFollowUp.Impl;

export namespace EmailCategoryTypeManual {
  const values = ["MANUAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeManual = EmailCategoryTypeManual.Impl;

export namespace EmailCategoryTypeOnboarding {
  const values = ["ONBOARDING"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeOnboarding = EmailCategoryTypeOnboarding.Impl;

export namespace EmailCategoryTypeRenewal {
  const values = ["RENEWAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailCategoryTypeRenewal = EmailCategoryTypeRenewal.Impl;

export type EmailDefaultCategoryAdminView = {
  categoryId: number;
  name: string;
  repositoryId: number;
};

export type EmailEducationCategoryAdminView = {
  active: boolean;
  categoryId: number;
  name: string;
  productIds: number[];
  repositoryId: number;
};

export type EmailFollowUpCategoryAdminView = {
  active: boolean;
  categoryId: number;
  corporateIds: string[];
  interval: number;
  maxDaysAfterExpiration: number;
  minDaysAfterExpiration: number;
  name: string;
  repositoryId: number;
  startDate: { year: number; month: number; day: number } | null;
};

export type EmailManualCategoryAdminView = {
  categoryId: number;
  corporateIds: string[];
  name: string;
  repositoryId: number;
};

export type EmailOnboardingCategoryAdminView = {
  active: boolean;
  categoryId: number;
  maxDaysAfterSignup: number;
  minDaysAfterSignup: number;
  name: string;
  repositoryId: number;
};

export type EmailPoolAdminCreateResult = { kind: "DUPLICATE_ERROR" } | { kind: "SUCCESS"; data: EmailPoolAdminView };

export type EmailPoolAdminListView = {
  items: EmailPoolAdminView[];
};

export type EmailPoolAdminUpdateResult = { kind: "ITEM_NOT_FOUND_ERROR" } | { kind: "SUCCESS"; data: EmailPoolAdminView };

export type EmailPoolAdminView = {
  corporateIds: number[];
  name: string;
};

export type EmailPoolForm = {
  corporateIds: number[];
};

export type EmailPushNotificationView = {
  date: { year: number; month: number; day: number } | null;
  link: string | null;
  title: string | null;
};

export type EmailRenewalCategoryAdminView = {
  active: boolean;
  categoryId: number;
  maxDaysAfterExpiration: number;
  minDaysAfterExpiration: number;
  name: string;
  productIds: number[];
  repositoryId: number;
};

export type EmailRepositoryAdminListView = {
  items: EmailRepositoryAdminView[];
};

export type EmailRepositoryAdminView = {
  comment: string | null;
  name: string;
  repositoryId: number;
  type: EmailRepositoryType;
};

export namespace EmailRepositoryType {
  const values = ["DEFAULT", "FOLLOWUP", "RENEWAL", "EDUCATION", "ONBOARDING", "MANUAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailRepositoryType = EmailRepositoryType.Impl;

export type EmailTemplateCategoryInfo = {
  categoryName: string;
  defaultSender: string;
  footerOptions: DropdownEntry[];
  repositoryId: number;
  repositoryName: string;
  runningBatchProcess: boolean;
  templates: EmailTemplateEditView[];
  testParameters: EmailTemplateTestForm;
};

export type EmailTemplateEditView = {
  analyticsCampaign: string | null;
  attachmentPath: string | null;
  contentHtml: string | null;
  contentPlain: string | null;
  contentSidebar: string | null;
  emailTitle: string | null;
  footerSnippet: string | null;
  language: Language;
  linkTemplateCategoryId: number | null;
  linkTemplateId: number | null;
  sender: string | null;
  subject: string;
  templateId: number;
  templateLanguage: "UNSUPPORTED" | "VELOCITY";
};

export type EmailTemplateForm = {
  analyticsCampaign: string | null;
  attachmentPath: string | null;
  contentHtml: string | null;
  contentPlain: string | null;
  contentSidebar: string | null;
  emailTitle: string | null;
  footerSnippet: string | null;
  language: Language;
  linkTemplateId: number | null;
  sender: string | null;
  subject: string;
  templateLanguage: "UNSUPPORTED" | "VELOCITY";
};

export type EmailTemplateTestForm = {
  alternativeRecipient: string | null;
  corporateId: number | null;
  customerId: number;
  dataIds: number[];
  format: EmailTemplateTestFormat;
  method: EmailTemplateTestMethod;
  subscriptionId: number | null;
};

export namespace EmailTemplateTestFormat {
  const values = ["HTML", "PLAINTEXT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailTemplateTestFormat = EmailTemplateTestFormat.Impl;

export namespace EmailTemplateTestMethod {
  const values = ["BROWSER", "EMAIL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmailTemplateTestMethod = EmailTemplateTestMethod.Impl;

export type EmailTemplateTestResult = {
  format: EmailTemplateTestFormat;
  method: EmailTemplateTestMethod;
  resultBody: string | null;
  success: boolean;
};

export namespace EmbeddingModel {
  const values = ["TEXT_EMBEDDING_3_LARGE", "TEXT_EMBEDDING_3_SMALL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type EmbeddingModel = EmbeddingModel.Impl;

export type EvidenceForm = {
  text: string;
};

export type EvidenceUnparsedForm = {
  text: string;
};

export type EvidenceValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: EvidenceForm };

export type ExpenseEditView = {
  amount: number | null;
  currencyCode: string;
  currencyOptions: DropdownEntry[];
  expenseTypeOptions: DropdownEntry[];
  fileUrl: string | null;
  jobId: bigint | null;
  offerId: bigint;
  type: JobType;
};

export type ExpenseForm = {
  amount: number | null;
  currencyCode: string;
  jobId: bigint | null;
  offerId: bigint;
  type: JobType;
};

export type ExpenseFormMultipart = {
  expenseForm: ExpenseForm;
  file: FormFile;
};

export type ExpenseFormResult = { kind: "SUCCESS" } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export type ExpenseListView = {
  amount: number;
  currencyCode: string;
  fileUrl: string | null;
  jobId: bigint;
  state: JobState;
  type: JobType;
};

export type ExpenseListViewArray = {
  items: ExpenseListView[];
};

export type ExpenseToolData = {
  dataId: bigint;
  expenses: ExpenseListViewArray;
  offerId: bigint;
};

export type ExpertVideoAdminForm = {
  aboutTheHost: string;
  active: boolean;
  authorIds: bigint[];
  authorImage: FormFile | null;
  eventDateTime: ZonedDateTime;
  expertVideoId: bigint;
  language: Language;
  length: number;
  pdf: FormFile | null;
  registrationLink: string;
  tags: Tag[];
  teaser: string;
  title: string;
  transcript: string;
  vimeoLink: string;
  whatYouLearn: string;
  youtubeLink: string;
};

export type ExpertVideoAdminOverview = {
  items: ExpertVideoListView[];
};

export type ExpertVideoAnalytics = {
  expertVideoId: bigint;
};

export type ExpertVideoDetailPageProps = {
  expertVideo: ExpertVideoDetailView;
};

export type ExpertVideoDetailTags = {
  metadata: Tag[];
  names: string[];
};

export type ExpertVideoDetailView = {
  aboutTheHost: string;
  authorImage: FormFile | null;
  authors: AuthorListView[];
  eventDateTime: ZonedDateTime;
  expertVideoId: bigint;
  length: number;
  metadata: ContentMetadata;
  pdf: FormFile | null;
  registrationLink: string;
  tags: ExpertVideoDetailTags;
  transcript: string;
  vimeoLink: string;
  whatYouLearn: string;
  youtubeLink: string;
};

export type ExpertVideoEditPageProps = {
  availableTimeZones: DropdownEntry[];
  expertVideo: ExpertVideoAdminForm;
  selectedAuthors: AuthorListView[];
};

export type ExpertVideoFiltersForm = {
  languageFormFilter: Language[];
};

export type ContentId_EXPERT_VIDEO = {
  contentType: "EXPERT_VIDEO";
  id: bigint;
};

export type ExpertVideoListView = {
  active: boolean;
  authorIds: bigint[];
  eventDateTime: ZonedDateTime;
  expertVideoId: bigint;
  language: Language;
  title: string;
};

export type ExpertVideoOverviewPageProps = {
  channels: ChannelMetadata[];
};

export type ExpertVideoPagingInfo = {
  pageEnglish: number;
  pageNative: number;
  tagFilter: {
    tag: Tag;
  } | null;
};

export type ExpertVideoSaveResponse = { kind: "ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; expertVideoId: bigint };

export type ExpertVideoTaggedItems = {
  tags: Tag[];
};

export type ExpertVideosResult = {
  expertVideosEnglish: ContentMetadata_EXPERT_VIDEO[];
  expertVideosNative: ContentMetadata_EXPERT_VIDEO[];
  pagingEnglish: PageInfo;
  pagingNative: PageInfo;
};

export type ExpertiseAreasForm = {
  text: string;
};

export type ExpertiseAreasUnparsedForm = {
  text: string;
};

export type ExpertiseAreasValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ExpertiseAreasForm };

export type ExportReportListView = {
  downloadLink: string;
  exportDate: { year: number; month: number; day: number };
  exportTaskId: number;
  fileName: string;
  hash: string;
  reportName: string | null;
  reportType: ReportType | null;
};

export type ExtFormFile = { kind: "FILE"; file: FormFile | null } | { kind: "URL"; filename: string; url: string };

export namespace FeatureType {
  const values = ["ACTIONABLE", "GET_ABSTRACT_AI", "MIAB", "STUDIO", "COPILOT", "VERIFAI", "MCP"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FeatureType = FeatureType.Impl;

export type FeatureTypeArr = {
  items: FeatureType[];
};

export type FidelityCheck = {
  guidance: string;
  inventedPhrases: string[];
  passed: boolean;
  unsupportedClaims: string[];
};

export type FileDataForm = {
  minioBucket: string;
  minioKey: string;
};

export type FileDataUnparsedForm = {
  minioBucket: string;
  minioKey: string;
};

export type FileDataValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: FileDataForm };

export type FileUploadListView = {
  fileName: string;
  fileSize: bigint;
  fileUrl: string;
  height: number | null;
  owner: number | null;
  ownerName: string | null;
  uploadDate: DateTime<true>;
  uri: string;
  velocityCode: string | null;
  width: number | null;
};

export type FileUploadListViewArray = {
  arr: FileUploadListView[];
};

export type FilteredLearningItemAdminView = {
  items: LearningItemAdminView[];
  totalCount: number;
};

export type FlowMcpContentChunk = {
  authors: string[];
  content: string;
  contentKey: string;
  contentLabel: string;
  contentType: FlowMcpContentType;
  language: string;
  publicationDate: string | null;
  rating: number | null;
  releasedAt: bigint | null;
  score: number;
  title: string;
};

export namespace FlowMcpContentType {
  const values = ["SUMMARY", "VERIFY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FlowMcpContentType = FlowMcpContentType.Impl;

export type FlowMcpSearchContentResponse = {
  durationMs: bigint;
  items: FlowMcpContentChunk[];
};

export namespace FlowMcpSearchMode {
  const values = ["TOPICAL", "TITLE", "AUTHOR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FlowMcpSearchMode = FlowMcpSearchMode.Impl;

export type FlowWidgetProps = {
  dataId: bigint | null;
  productionStep: string | null;
  productionType: string | null;
};

export type FormFile = {
  bytes: ReadonlyUint8Array;
  filename: string;
  mimeType: string;
};

export type FormFilter = {
  activeValues: string[];
  identifier: FormFilterName;
  label: string;
  name: string;
  options: FormFilterOption[];
  priority: FormFilterPriority;
  type: FormFilterType;
};

export namespace FormFilterName {
  const values = ["CHANNEL", "SOURCE", "QUALITY", "RATING", "AUDIO", "LANGUAGE", "PUBLICATION_DATE", "MIAB", "EXPERT_PICK", "VERIFAI_FORMAT_TYPE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterName = FormFilterName.Impl;

export type FormFilterOption = {
  label: string;
  shortLabel: string | null;
  value: string;
};

export namespace FormFilterPriority {
  const values = ["PRIMARY", "SECONDARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterPriority = FormFilterPriority.Impl;

export namespace FormFilterType {
  const values = ["MULTICHECKBOX", "CHECKBOX", "MULTISELECT", "RANGE", "RANGE_MIN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FormFilterType = FormFilterType.Impl;

export type FormSorting = {
  activeValue: string;
  options: FormSortingOption[];
};

export type FormSortingOption = {
  label: string;
  value: string;
};

export namespace FreeDownloadRights {
  const values = ["OK", "NO", "UNKNOWN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FreeDownloadRights = FreeDownloadRights.Impl;

export type FreeSummaryPortletView = {
  metadata: PortletMetadata;
  summary: ContentMetadata_SUMMARY | null;
  viewAllUri: string | null;
};

export type FreelancerContactFilterForm = {
  languages: Language[];
  managingEditorCustomerId: number | null;
  writerStates: WriterState[];
};

export type FreelancerContactForm = {
  attachments: GaFileObject[];
  emailContent: string;
  emailSubject: string;
  managingEditorCustomerId: number | null;
  writerCustomerIds: number[];
};

export type FreelancerContactWriterListView = {
  writers: FreelancerContactWriterView[];
};

export type FreelancerContactWriterView = {
  languages: string;
  writer: UserListView;
  writerState: WriterState;
};

export namespace FrontendProject {
  const values = ["VUE", "DESIGN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type FrontendProject = FrontendProject.Impl;

export type FullSearchProps = {
  beta: boolean;
  hasCorporatePlus: boolean;
  initialActionableFormFilters: FormFilter[];
  initialSummaryFormFilters: FormFilter[];
  initialVerifaiFormFilters: FormFilter[];
  query: string | null;
};

export type FullSearchResults = {
  actionableFormFilters: FormFilter[];
  actionables: ContentMetadata[];
  channelPaging: {
    page: number;
    totalCount: bigint;
  };
  channels: ChannelMetadata[];
  customPages: CustomPageListView[];
  downloadSourceParams: { additionalProperties: Record<string, string> };
  summaries: ContentMetadata[];
  summariesPaging: {
    page: number;
    totalCount: bigint;
  };
  summaryFormFilters: FormFilter[];
  verifaiFormFilters: FormFilter[];
  verifaiPaging: {
    page: number;
    totalCount: bigint;
  };
  verifais: ContentMetadata[];
};

export type GaContextAsp = {
  corporateId: number;
  username: string;
};

export type GaContextCustomer = {
  customerId: number;
};

export type GaContextDataSync = {
  asp: GaContextAsp | null;
  currentLanguage: Language;
  customer: GaContextCustomer | null;
  flags: GaContextFlags;
};

export type GaContextFlags = {
  alpha: boolean;
  beta: boolean;
  loggedIn: boolean;
};

export type GaFileObject = {
  bytes: ReadonlyUint8Array;
  contentType: string;
  originalFileName: string;
};

export type GeminiDatastoreConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "GEMINI_DATASTORE";
  dataStoreId: string;
  location: string;
  projectId: string;
};

export namespace Gender {
  const values = ["FEMALE", "MALE", "UNKNOWN", "CUSTOM"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type Gender = Gender.Impl;

export type GenerateDemoDynamicForm = {
  dynamicFields: DynamicFields;
  title: string;
};

export type GenerateDemoDynamicSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: GenerateDemoDynamicForm };

export type GenerateDemoDynamicUnparsedForm = {
  dynamicFields: DynamicFields;
  title: string;
};

export type GenerateDemoDynamicValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoDynamicForm };

export type GenerateDemoForm = {
  age: bigint;
  agreeToTerms: boolean;
  bio: string | null;
  birthday: { year: number; month: number; day: number };
  countryCode: string;
  countryCodes: string[];
  email: string;
  firstName: string;
  interests: Language[];
  language: Language;
  password: string;
  pushFrequency: WeeklyEmailPushFrequency;
};

export type GenerateDemoItemForm = {
  name: string;
  quantity: bigint;
};

export type GenerateDemoItemUnparsedForm = {
  name: string;
  quantity: string;
};

export type GenerateDemoItemValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoItemForm };

export type GenerateDemoStructuralContactMethodForm = { kind: "EMAIL"; address: string } | { kind: "PHONE"; mobile: string };

export type GenerateDemoStructuralContactMethodUnparsedForm = { kind: "EMAIL"; address: string } | { kind: "PHONE"; mobile: string };

export type GenerateDemoStructuralContactMethodValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoStructuralContactMethodForm };

export type GenerateDemoStructuralForm = {
  billingAddress: DemoAddressForm | null;
  contactMethod: GenerateDemoStructuralContactMethodForm;
  corporateId: bigint;
  homeAddress: DemoAddressForm;
  items: GenerateDemoItemForm[];
};

export type GenerateDemoStructuralSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: GenerateDemoStructuralForm };

export type GenerateDemoStructuralUnparsedForm = {
  billingAddress: DemoAddressUnparsedForm;
  billingAddress_isAvailable: boolean;
  contactMethod: GenerateDemoStructuralContactMethodUnparsedForm;
  corporateId: bigint;
  homeAddress: DemoAddressUnparsedForm;
  items: GenerateDemoItemUnparsedForm[];
};

export type GenerateDemoStructuralValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoStructuralForm };

export type GenerateDemoSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: GenerateDemoForm };

export type GenerateDemoUnparsedForm = {
  age: string;
  agreeToTerms: boolean;
  bio: string;
  birthday: string;
  countryCode: string;
  countryCodes: string[];
  email: string;
  firstName: string;
  interests: Language[];
  language: Language | null;
  password: string;
  pushFrequency: WeeklyEmailPushFrequency | null;
};

export type GenerateDemoValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: GenerateDemoForm };

export type GenerateMiabAdminResponse = {
  content: MIABContent;
};

export type GenerateSummaryKeyInsightAdminResponse = {
  content: SummaryContentLegacy;
};

export type GetCorporateCustomersListViewRequest = {
  approximated: boolean;
  corporateCustomerFilters: CorporateCustomerFilters;
  pageRequest: PageRequest;
};

export type GetCustomFieldsResponse = {
  fields: CustomFieldListView[];
};

export type GetExportSummariesLibraryInMarcXmlFormatProps = {
  body: {
    fromDate: { year: number; month: number; day: number };
    marcLibraryFilters: MarcLibraryFilters;
    toDate: { year: number; month: number; day: number };
  };
};

export type GetSummaryPagePropsResponse = { kind: "REDIRECT"; redirectUrl: string } | { kind: "SUMMARY"; props: SummaryPageProps };

export namespace GiftCodeStatus {
  const values = ["AVAILABLE", "UNAVAILABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type GiftCodeStatus = GiftCodeStatus.Impl;

export type GiftDonorSubscription = {
  donorCustomerId: number;
  donorSubscriptionId: number;
};

export type GiftReceiverSubscription = {
  receiverCustomerId: number;
  receiverSubscriptionId: number;
};

export type GiftSubscription = {
  donorSubscription: GiftDonorSubscription | null;
  giftReceiverSubscription: GiftReceiverSubscription | null;
};

export namespace HybridSearchDocType {
  const values = ["SUMMARY", "ACTIONABLE", "EXPERTVIDEO", "VERIFAI", "VWD"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type HybridSearchDocType = HybridSearchDocType.Impl;

export type HybridSearchProps = {
  embeddingModel: EmbeddingModel;
  language: Language;
  llmRerank: boolean;
  llmRerankPrompt: string | null;
  pipelineRerank: boolean;
  recentDays: number;
  rewriteMerge: boolean;
  scoreThreshold: number;
  searchTerm: string;
  types: HybridSearchDocType[];
  weightRanker: {
    metadata: number;
    title: number;
    vector: number;
  };
};

export type HyphenationExceptions = {
  exceptions: string[];
};

export type ImageOutputInfo = {
  maxHeight: number;
  maxWidth: number;
  minHeight: number;
  minWidth: number;
  outputFormat: string;
};

export type ImageResizeObject = {
  croppingInfo: CroppingInfo;
  image: ReadonlyUint8Array;
  imageOutputInfo: ImageOutputInfo;
};

export type ImageTextPortletContent = {
  htmlContent: string | null;
  image: string | null;
  link: string | null;
};

export type ImageTextPortletEditView = {
  localizedContent: { additionalProperties: Record<string, ImageTextPortletContent> };
  portlet: PortletEditView;
};

export type ImageTextPortletForm = {
  localizedContent: { additionalProperties: Record<string, ImageTextPortletContent> };
  portletForm: PortletForm;
};

export type ImageTextPortletView = {
  content: ImageTextPortletContent;
  metadata: PortletMetadata;
};

export type InstantSearchResult = {
  ACTIONABLE: InstantSearchResultListView[] | null;
  CHANNEL: InstantSearchResultListView[] | null;
  CUSTOMPAGE: InstantSearchResultListView[] | null;
  SUMMARY: InstantSearchResultListView[] | null;
  VERIFAI: InstantSearchResultListView[] | null;
};

export type InstantSearchResultListView = {
  activationTime: DateTime<true> | null;
  authorInfo: string | null;
  contentItemType: string;
  id: bigint;
  thumbnail: string;
  title: string;
  url: string;
};

export type Invoice = {
  generatedAt: DateTime<true>;
  invoiceId: number;
  invoiceLevel: number;
  invoiceUri: string | null;
  pdfCounter: number;
  transactionId: number;
};

export type InvoiceJobListView = {
  amount: MonetaryAmount;
  invoiceId: bigint;
  jobId: bigint;
  summarySourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  summaryTitle: string;
};

export type InvoiceListView = {
  invoiceId: bigint;
  issuedAt: DateTime<true>;
  paidAt: DateTime<true> | null;
  total: string;
  workPeriodEnd: DateTime<true>;
  workPeriodStart: DateTime<true>;
};

export type InvoiceListViewList = {
  invoices: InvoiceListView[];
};

export type InvoicePaymentCardView = {
  additionalInformationBottom: string | null;
  additionalInformationTop: string | null;
  bankConnectionDetails: BankConnectionDetails;
  invoiceIssuerId: number;
  issuerAddress: Address;
  total: MonetaryAmount;
};

export type InvoicePaymentCardViewList = {
  invoices: InvoicePaymentCardView[];
};

export type InvoicePaymentDetailCardView = {
  groups: InvoicePaymentJobGroupListView[];
  total: MonetaryAmount;
};

export type InvoicePaymentJobGroupListView = {
  accountNumber: number | null;
  amount: MonetaryAmount;
  jobType: JobType;
  jobs: InvoiceJobListView[];
  productionType: ProductionType;
  summaryLength: SummaryLength;
};

export type InvoicePaymentParameters = {
  invoiceIds: bigint[];
  paidAt: { year: number; month: number; day: number };
};

export type InvoicePaymentView = {
  paidAt: DateTime<true> | null;
  reference: string | null;
  transactionId: number;
};

export type IosPaymentView = {
  appStoreProductId: string;
  autoRenewProductId: string | null;
  autoRenewing: boolean;
  expirationIntent: number;
  itunesTransactionId: string;
  originalItunesTransactionId: string | null;
  testPurchase: boolean;
  transactionId: number;
  webOrderLineItemId: bigint;
};

export type IpProtectionForm = {
  checkIp: boolean;
  ips: string[];
};

export type IpProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: IpProtectionForm };

export type IssuesForm = {
  text: string;
};

export type IssuesUnparsedForm = {
  text: string;
};

export type IssuesValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: IssuesForm };

export namespace ItemLayout {
  const values = ["MINI", "REGULAR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ItemLayout = ItemLayout.Impl;

export namespace JobState {
  const values = ["ACCEPTED", "SUBMITTED", "SUBMITTED_BUT_REJECTED", "DONE", "UNPAID", "PAID"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type JobState = JobState.Impl;

export namespace JobType {
  const values = [
    "WRITING",
    "REVISING",
    "BUYING",
    "EDITING",
    "CONTENT_EDITING",
    "COPY_EDITING",
    "PROOFREADING",
    "ACTIVATING",
    "SWITCH_TO_ABSTRACT",
    "SWITCH_TO_REVIEW",
    "CONSIDER_CREATING_REVIEW",
    "REQUESTING_RIGHTS",
    "REQUESTING_AUDIO_RIGHTS",
    "REQUESTING_PUBLISHER_APPROVAL",
    "CONTACT_RIGHTS_HOLDER",
    "SEARCH_FOR_LICENSES",
    "CREATING_EXTRA_EDITION",
    "ADDING_SUMMARY_SOURCE",
    "APPROVING",
    "DEFINING_FLOW",
    "DEFINING_FURTHER_FLOW",
    "DEACTIVATING",
    "AUDIO_EDITING",
    "AUDIO_PRODUCING",
    "AUDIO_PROOFING",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type JobType = JobType.Impl;

export type KeyInsightAdminProps = {
  dataId: number;
  keyInsightItems: KeyInsightItem[];
  title: string;
};

export type KeyInsightItem = {
  dataId: number;
  keyInsight: string;
  sortOrder: number;
  sumKeyInsightId: bigint;
};

export type KeyInsightUpdateForm = {
  keyInsightItems: KeyInsightItem[];
};

export type KeyQuality = {
  key: string;
  keyQualityId: number;
  type: "INTRINSIC" | "EXTRINSIC";
};

export namespace KindChannel {
  const values = ["channel"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindChannel = KindChannel.Impl;

export namespace KindClassification {
  const values = ["classification"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindClassification = KindClassification.Impl;

export namespace KindDynamicBoolean {
  const values = ["dynamicBoolean"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicBoolean = KindDynamicBoolean.Impl;

export namespace KindDynamicNumber {
  const values = ["dynamicNumber"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicNumber = KindDynamicNumber.Impl;

export namespace KindDynamicString {
  const values = ["dynamicString"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicString = KindDynamicString.Impl;

export namespace KindDynamicVoid {
  const values = ["dynamicVoid"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindDynamicVoid = KindDynamicVoid.Impl;

export namespace KindKeyQuality {
  const values = ["key-quality"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindKeyQuality = KindKeyQuality.Impl;

export namespace KindParentalSetting {
  const values = ["parental-setting"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindParentalSetting = KindParentalSetting.Impl;

export namespace KindPrimarySkill {
  const values = ["primary-skill"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindPrimarySkill = KindPrimarySkill.Impl;

export namespace KindSkill {
  const values = ["skill"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type KindSkill = KindSkill.Impl;

export namespace Language {
  const values = ["en", "de", "es", "ru", "zh", "pt", "fr", "ar"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type Language = Language.Impl;

export type LanguageDetectionResult = {
  language: Language;
};

export type LanguageTranslationForm = {
  aboutTheAuthor: string | null;
  authorTranslations: AuthorTranslationForm[];
  languageCode: string;
  rationale: string | null;
  recommendation: string | null;
  subtitle: string | null;
  summaryMarkdown: string | null;
  takeaways: TakeawaysForm[];
  title: string | null;
  whatYouWillLearn: WhatYouWillLearnForm[];
};

export type LanguageTranslationUnparsedForm = {
  aboutTheAuthor: string;
  authorTranslations: AuthorTranslationUnparsedForm[];
  languageCode: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  summaryMarkdown: string;
  takeaways: TakeawaysUnparsedForm[];
  title: string;
  whatYouWillLearn: WhatYouWillLearnUnparsedForm[];
};

export type LanguageTranslationValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: LanguageTranslationForm };

export type LeadCreationResponse = { kind: "ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "MEETING"; calendlyUrl: string } | { kind: "SUCCESS" };

export type LeadForm = {
  comment: string;
  company: string;
  companySize: LeadFormCompanySize;
  country: string;
  email: string;
  firstName: string;
  function: string;
  gRecaptchaResponse: string;
  howDidYouFindUs: string;
  inquiry: LeadFormInquiry;
  lastName: string;
  leadSource: LeadSource;
  phone: string;
  utmCampaign: string;
  utmContent: string;
  utmId: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
};

export namespace LeadFormCompanySize {
  const values = ["PLEASE_CHOOSE", "1_10", "11_100", "101_500", "501_1000", "1001_5000", "5001_10000", "10000_PLUS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadFormCompanySize = LeadFormCompanySize.Impl;

export namespace LeadFormInquiry {
  const values = ["PLEASE_CHOOSE", "FOR_ENTERPRISE", "FOR_INDIVIDUALS", "FOR_TEAMS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadFormInquiry = LeadFormInquiry.Impl;

export type LeadFormProps = {
  countries: Country[];
  country: Country;
  ctaText: string | null;
  fullWidth: boolean;
  lead: string | null;
  leadSource: LeadSource;
  title: string | null;
};

export namespace LeadSource {
  const values = [
    "RENEWAL_FAILED",
    "CANCELATION",
    "CONTACTFORM",
    "MANUAL",
    "TRIAL_REQUEST",
    "WHITE_PAPER_DOWNLOAD",
    "FREE_ABSTRACTS",
    "PURCHASE_FAILED",
    "PURCHASE_FAILED_OLD",
    "ACTIONABLE_LANDING_PAGE",
    "CUSTOMER_STORIES_LANDING_PAGE",
    "SUPPORT_FORM",
    "LEADERSFUTURE_LANDING_PAGE",
    "TEAMDEVELOPMENT_LANDING_PAGE",
    "EMPLOYEEENGAGEMENT_LANDING_PAGE",
    "EMPLOYEETRAINING_LANDING_PAGE",
    "CORPORATELEARNING_LANDING_PAGE",
    "LEARNINGDEVELOPMENT_LANDING_PAGE",
    "MICROLEARNING_LANDING_PAGE",
    "SKILLSOFT_LANDING_PAGE",
    "STROER_LANDING_PAGE",
    "LMS_LANDINGPAGE",
    "MICROLEARNING_LANDINGPAGE",
    "ENTERPRISE_PRODUCTS",
    "SOLUTIONS_PAGE",
    "SOLUTIONS_UPSKILLING",
    "SOLUTIONS_DEI",
    "SOLUTIONS_HEALTH_WELLBEING",
    "SOLUTIONS_LEARNING_PLATFORM_STRATEGY",
    "SOLUTIONS_TALENT_DEVELOPMENT",
    "SOLUTIONS_COLLABORATIVE_LEARNING",
    "SOLUTIONS_LEARNING_RESOURCE_LIBRARY",
    "SOLUTIONS_EFFECTIVE_LEADERS",
    "WELCOME_PAGE",
    "BUSINESS_BOOK_SUMMARY_LANDINGPAGE",
    "GRAPH_CONNECTOR_LANDINGPAGE",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LeadSource = LeadSource.Impl;

export type LearningCalendarItem = {
  coverImage: string;
  coverUri: string;
  date: { year: number; month: number; day: number };
  dayOfMonth: number;
  icon: string;
  isFirstAvailableDoor: boolean;
  isWeekend: boolean;
  linkUri: string;
  status: "OPEN" | "CLOSED" | "WEEKEND" | "DISABLED";
};

export type LearningCalendarView = {
  bannerImgUri: string;
  doors: LearningCalendarItem[];
  endDate: { year: number; month: number; day: number } | null;
  expiresAt: { year: number; month: number; day: number } | null;
  learningCalendarId: bigint;
  name: string;
  startDate: { year: number; month: number; day: number };
};

export type LearningDashboardResponse = { kind: "UNCONFIGURED" } | { kind: "STATS"; stats: LearningDashboardStats };

export type LearningDashboardStats = {
  currentStreak: number;
  learningActivitiesCompleted: number;
  longestStreak: number;
  motivationalMessage: MotivationalMessage;
  quickWinsCollected: number;
  streakFrequency: StreakFrequency;
};

export type LearningHabitConfig = {
  emailNotification: boolean;
  streakFrequency: StreakFrequency | null;
  streakFrequencyModifiedAt: DateTime<true> | null;
  weekDay: LearningHabitWeekDay | null;
};

export namespace LearningHabitWeekDay {
  const values = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningHabitWeekDay = LearningHabitWeekDay.Impl;

export type LearningItemAdminActionableView = {
  description: string;
  learningItemType: LearningItemTypeActionable;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminCpsResourceView = {
  learningItemType: LearningItemTypeCpsResource;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminExpertVideoView = {
  learningItemType: LearningItemTypeExpertVideo;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminFilter = {
  activatedAtRange: DateRange | null;
  active: boolean | null;
  audio: boolean | null;
  createdAtRange: DateRange | null;
  documentTypes: SummaryDocumentType[];
  ids: number[];
  itemTypes: LearningItemType[];
  languages: Language[];
  manager: string | null;
  missingTagKinds: TagKind[];
  publisher: string | null;
  resultSize: number;
  sourceTypes: SummarySourceType[];
  tagCount: number | null;
  tagKinds: TagKind[];
  tags: Tag[];
  title: string | null;
};

export type LearningItemAdminMetadataView = {
  activatedAt: { year: number; month: number; day: number } | null;
  active: boolean;
  createdAt: { year: number; month: number; day: number };
  deactivatedAt: { year: number; month: number; day: number } | null;
  id: bigint;
  language: Language;
  manager: string | null;
  tags: Tag[];
  title: string;
};

export type LearningItemAdminProps = {
  admin: boolean;
  filter: LearningItemAdminFilter;
  views: "READONLY" | "LEARNING_ITEM_VIEW" | "CHANNEL_VIEW";
};

export type LearningItemAdminQuickWinView = {
  learningItemType: LearningItemTypeQuickWin;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminSummaryView = {
  audioActive: boolean;
  documentType: SummaryDocumentType;
  learningItemType: LearningItemTypeSummary;
  metadata: LearningItemAdminMetadataView;
  publisher: string | null;
  sourceType: SummarySourceType;
};

export type LearningItemAdminVerifaiView = {
  learningItemType: LearningItemTypeVerifai;
  metadata: LearningItemAdminMetadataView;
};

export type LearningItemAdminView =
  | { learningItemType: "ACTIONABLE"; description: string; metadata: LearningItemAdminMetadataView }
  | { learningItemType: "SUMMARY"; audioActive: boolean; documentType: SummaryDocumentType; metadata: LearningItemAdminMetadataView; publisher: string | null; sourceType: SummarySourceType }
  | { learningItemType: "EXPERT_VIDEO"; metadata: LearningItemAdminMetadataView }
  | { learningItemType: "VERIFAI"; metadata: LearningItemAdminMetadataView }
  | { learningItemType: "QUICK_WIN"; metadata: LearningItemAdminMetadataView }
  | { learningItemType: "CPS_RESOURCE"; metadata: LearningItemAdminMetadataView };

export namespace LearningItemType {
  const values = ["SUMMARY", "ACTIONABLE", "EXPERT_VIDEO", "VERIFAI", "QUICK_WIN", "CPS_RESOURCE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemType = LearningItemType.Impl;

export namespace LearningItemTypeActionable {
  const values = ["ACTIONABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeActionable = LearningItemTypeActionable.Impl;

export namespace LearningItemTypeCpsResource {
  const values = ["CPS_RESOURCE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeCpsResource = LearningItemTypeCpsResource.Impl;

export namespace LearningItemTypeExpertVideo {
  const values = ["EXPERT_VIDEO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeExpertVideo = LearningItemTypeExpertVideo.Impl;

export namespace LearningItemTypeQuickWin {
  const values = ["QUICK_WIN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeQuickWin = LearningItemTypeQuickWin.Impl;

export namespace LearningItemTypeSummary {
  const values = ["SUMMARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeSummary = LearningItemTypeSummary.Impl;

export namespace LearningItemTypeVerifai {
  const values = ["VERIFAI"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LearningItemTypeVerifai = LearningItemTypeVerifai.Impl;

export type LibraryConnectorListView = {
  active: boolean;
  connectorId: number;
  connectorName: string;
  connectorType: "OCN" | "DOCEBO" | "XAPI" | "CSOD" | "DEGREED";
  corporateId: number;
  corporation: string;
  createdAt: DateTime<true>;
  modifiedAt: DateTime<true> | null;
};

export type LibraryConnectorListViewArray = {
  libraryConnectorListView: LibraryConnectorListView[];
};

export type LibraryPulseComment = {
  comment: string;
  commentDate: { year: number; month: number; day: number };
  customerName: string;
  summaryTitle: string;
  summaryUrl: string;
};

export namespace LibraryPulseEngagementMetric {
  const values = ["ACTIVE_USERS", "CONTENT_USAGE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LibraryPulseEngagementMetric = LibraryPulseEngagementMetric.Impl;

export type LibraryPulseEngagementTile = {
  count: number;
  countPreviousPeriod: number;
  metric: LibraryPulseEngagementMetric;
};

export namespace LibraryPulseFormat {
  const values = ["SUMMARY", "ONLINE_CONTENT", "ACTIONABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LibraryPulseFormat = LibraryPulseFormat.Impl;

export type LibraryPulseFormatCount = {
  count: number;
  format: LibraryPulseFormat;
};

export namespace LibraryPulsePeriod {
  const values = ["WEEK", "MONTH", "QUARTER"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type LibraryPulsePeriod = LibraryPulsePeriod.Impl;

export type LibraryPulseSummary = {
  adminFirstName: string | null;
  byFormat: LibraryPulseFormatCount[];
  engagementTiles: LibraryPulseEngagementTile[];
  newItemsCount: number;
  newItemsCountPreviousPeriod: number;
  picks: ContentMetadata[];
  previousRangeEnd: { year: number; month: number; day: number };
  previousRangeStart: { year: number; month: number; day: number };
  rangeEnd: { year: number; month: number; day: number };
  rangeStart: { year: number; month: number; day: number };
  recentComments: LibraryPulseComment[];
  trend: LibraryPulseTrendPoint[];
};

export type LibraryPulseTrendPoint = {
  count: number;
  date: { year: number; month: number; day: number };
};

export type LoadSkillResponse =
  | { kind: "SKILL"; following: "FOLLOWING" | "NOT_FOLLOWING" | "UNAVAILABLE"; metadata: ChannelMetadata; relatedSkills: ChannelMetadata[]; shareOptions: ShareMedium[]; tag: Tag; topResults: ContentMetadataView; tracking: SkillTracking }
  | { kind: "REDIRECT"; path: string };

export type LoginTotpProps = {
  enrollmentInfo: {
    plainSecret: string;
    qrCodeImageUrl: string;
  } | null;
};

export type LoginTotpRequest = {
  rememberDevice: boolean;
  totpCode: string;
};

export type LoginTotpResult = {
  redirectUrl: string;
};

export type LogoForm = {
  logo: ReadonlyUint8Array;
};

export type LrsXapiConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "LRS_XAPI";
  oauthTokenUri: string;
  userIdentifier: ConnectorUserIdentifier;
  xapiActorId: XApiActorId;
};

export type LsvPaymentView = {
  accountNumber: string | null;
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  bankIban: string | null;
  bankName: string | null;
  bankTown: string | null;
  clearingNumber: string | null;
  firstName: string;
  lastName: string;
  merchantId: string | null;
  refNumber: string | null;
  transactionId: number;
};

export type MIABAnalyticsFeedbackEventForm = {
  isPositive: boolean;
  miabId: bigint;
};

export type MIABContent = {
  data: MIABContentData;
  miabId: bigint;
};

export type MIABContentData = {
  customerId: number | null;
  keyInsights: string[];
  modifiedAt: DateTime<true>;
  questions: MIABQuestionMetadata[];
  summaryId: number;
};

export type MIABContents = {
  items: ContentMetadata_MIAB[];
};

export type MIABCustomQuestionsForm = {
  dataId: number;
  meta: string;
  prompt: string;
  questions: MIABQuestionMetadata[];
};

export type MIABCustomQuestionsProps = {
  createdAt: DateTime<true>;
  createdBy: string;
  customQuestions: MIABQuestionMetadata[];
  customerId: number;
  hash: string;
};

export type MIABCustomQuestionsResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "CREATED"; hash: string; miabId: bigint } | { kind: "UPDATED"; miabId: bigint };

export type MIABOverviewPageProps = {
  channels: ChannelMetadata[];
};

export type MIABPageProps = {
  ixStatuses: ContentIxStatus[];
  linkedMiab: ContentMetadata_MIAB | null;
  miab: MIABView;
  openView: ("AI" | "EDIT") | null;
  regionalRestriction: {
    countriesByRegion: { additionalProperties: Record<string, string[]> };
    restrictionType: CountryRestrictionType;
  };
  similarMiabs: ContentMetadata[];
};

export type MIABPopularSummariesResult = {
  miabsEnglish: ContentMetadata[];
  miabsNative: ContentMetadata[];
  pagingEnglish: PageInfo;
  pagingNative: PageInfo;
};

export type MIABQuestionMetadata = {
  createdAt: DateTime<true> | null;
  question: string;
  title: string;
};

export type MIABQuestionsAiResponse = {
  question1: {
    question: string;
    title: string;
  };
  question2: {
    question: string;
    title: string;
  };
  question3: {
    question: string;
    title: string;
  };
  question4: {
    question: string;
    title: string;
  };
  question5: {
    question: string;
    title: string;
  };
};

export type MIABSummaryForm = {
  pageEnglish: number;
  pageNative: number;
  selectedChannel: bigint | null;
};

export type MIABUserContext = {
  customerId: number;
  hash: string;
  username: string;
};

export type MIABView = {
  content: MIABContent;
  interactions: ContentIxStatus_MIAB | null;
  metadata: ContentMetadata;
  userContext: MIABUserContext | null;
};

export type MainNavigationEntry = { kind: "SUBNAV"; links: NavigationLinkEntry[]; title: string } | { kind: "SUBNAV_GROUP"; groups: SubNavigationGroup[]; title: string } | { kind: "LINK"; link: string; title: string };

export namespace MarcLibraryFilterType {
  const values = ["ACTIVATED", "ACTIVATED_MODIFIED", "DEACTIVATED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type MarcLibraryFilterType = MarcLibraryFilterType.Impl;

export type MarcLibraryFilters = {
  filterBy: MarcLibraryFilterType;
  isFiltered: boolean;
  language: Language;
};

export namespace MarketingActivityPermission {
  const values = ["UNKNOWN", "ALLOWED", "FORBIDDEN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type MarketingActivityPermission = MarketingActivityPermission.Impl;

export type MarketingFileUploadFilters = {
  author: string;
  fileName: string;
  isQueryFromSearch: boolean;
};

export type MarketingFileUploadForm = {
  contentType: string;
  fileContent: string;
  fileName: string;
  overwrite: boolean;
};

export type MaybeSkillNavigation = {
  navigation: SkillNavigation | null;
};

export type MiabAdminProps = {
  dataId: number;
  miab: MiabAdminUpdateForm;
  title: string;
};

export type MiabAdminQuestionItem = {
  question: string;
  title: string;
};

export type MiabAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean;
  languages: Language[];
  query: string;
};

export type MiabAdminUpdateForm = {
  dataId: number;
  miabAdminQuestionItems: MiabAdminQuestionItem[];
  miabId: bigint;
};

export type MiabAiSuggestionRequest = {
  dataId: number;
  userPrompt: string;
};

export type MiabAiSuggestionResponse = {
  questions: MIABQuestionMetadata[];
};

export type MiabFiltersForm = {
  languageFormFilter: Language[];
};

export type ContentId_MIAB = {
  contentType: "MIAB";
  id: bigint;
};

export type MiabListCustomPortletEditView = {
  miabs: ContentMetadata_MIAB[];
  portlet: PortletEditView;
};

export type MiabListCustomPortletForm = {
  miabIds: bigint[];
  portletForm: PortletForm;
};

export type MiabListPortletView = {
  items: ContentMetadata_MIAB[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type ContentMetadata_MIAB = {
  common: ContentMetadataCommon;
  contentId: ContentId_MIAB;
  contentType: "MIAB";
  customerId: number | null;
  dataId: number;
  modifiedAt: DateTime<true> | null;
};

export type MilesPaymentView = {
  cardNumber: string | null;
  milesTransactionId: number | null;
  pin: string | null;
  transactionId: number;
};

export namespace MixedContentType {
  const values = ["SUMMARY", "ONLINE_CONTENT", "MIAB", "QUICKWIN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type MixedContentType = MixedContentType.Impl;

export type MixedListCustomPortletEditView = {
  items: ContentMetadata[];
  portlet: PortletEditView;
};

export type MixedListCustomPortletForm = {
  items: MixedListItemRef[];
  portletForm: PortletForm;
};

export type MixedListItemRef = {
  contentId: bigint;
  contentType: MixedContentType;
};

export type MixedListPortletView = {
  items: ContentMetadata[];
  metadata: PortletMetadata;
  viewAllUri: string | null;
};

export type MobileAppLoginPreferenceListView = {
  corporateId: number;
  corporationName: string;
};

export type MobileDevice = {
  corporateId: number;
  customerId: number;
  deviceId: string;
  deviceType: string;
  firstUsedAt: DateTime<true>;
  language: Language;
  lastPushAt: DateTime<true> | null;
  lastUsedAt: DateTime<true>;
  osType: string | null;
  pushFrequency: PushFrequency;
  pushToken: string | null;
  version: string;
};

export type MonetaryAmount = {
  amount: number;
  currencyCode: string;
  displayValue: string;
};

export type MotivationalMessage = {
  message: string;
  type: MotivationalMessageType;
};

export namespace MotivationalMessageType {
  const values = ["EMTPY", "IDLE", "NEW", "PROGRESS_NUDGE", "PROGRESS_ACKNOWLEDGEMENT", "ACTIVITY_COMPLETION", "OVERALL_ACHIEVEMENT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type MotivationalMessageType = MotivationalMessageType.Impl;

export type MsGraphConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectionId: string;
  connectorType: "MS_GRAPH";
  tenantId: string;
  userIdentifier: ConnectorUserIdentifier;
};

export type MyListPortletView = {
  items: MyListView[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type MyListView = {
  coverUri: string;
  dataIds: number[];
  key: string;
  myListId: bigint;
  name: string;
};

export type MyListViewArray = {
  items: MyListView[];
};

export type MyProductInformation = {
  corporatePlus: boolean;
  currency: string;
  currencyId: number;
  displayDiscount: string;
  displayPrice: string;
  extraMonths: number;
  gift: boolean;
  giftQuantity: number;
  isDiscount: boolean;
  lengthText: string;
  months: number;
  productGroupId: number;
  productName: string;
  productSummary: string;
  promotionCode: string | null;
  promotionCodeInvisible: boolean;
  promotionCodeRenew: boolean;
  quantity: string;
  seats: number;
  seatsText: string;
  subscriptionPeriodFormatted: string;
  teamProduct: boolean;
};

export type MyQuickWinRequest = {
  ixStatus: QuickWinFilterIxStatus;
  pageRequest: PageRequest;
};

export type MyQuickWinResponse = {
  items: ContentMetadata_QUICKWIN[];
  pageInfo: PageInfo;
};

export namespace NavigationDirection {
  const values = ["NEXT", "PREVIOUS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type NavigationDirection = NavigationDirection.Impl;

export type NavigationLinkEntry = {
  description: string | null;
  link: string;
  title: string;
};

export type NewB2BReportForm = {
  active: boolean;
  activeUsersOnly: boolean;
  b2bCustomReport: boolean;
  ccRecipients: string;
  corporateId: number;
  customerId: number;
  dayOfWeek: number;
  downloadType: DownloadType;
  emailAdministrator: string | null;
  frequency: ReportFrequency;
  fromDate: { year: number; month: number; day: number } | null;
  ftpFolder: string;
  ftpHost: string;
  ftpPassphrase: string;
  ftpPassword: string;
  ftpPort: number;
  ftpRemoteFileName: string;
  ftpUserName: string;
  prefixFileName: boolean;
  privateKey: string | null;
  privateKeyContent: string;
  privateKeyEnabled: boolean;
  reportId: number;
  reportName: string;
  reportType: ReportType;
  specialReport: boolean;
  targetExcel: boolean;
  toDate: { year: number; month: number; day: number } | null;
  transferProtocol: TransferProtocol;
  zipFile: boolean;
};

export type NewConnectorForm =
  | { connectorType: "WORKDAY"; accountId: string; apiKey: string; categoryFallback: string; common: CommonConnectorForm; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "ORACLE"; accountId: string; apiKey: string; common: CommonConnectorForm; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "SUCCESS_FACTORS"; auth: AuthConnectorForm; common: CommonConnectorForm; companyId: string; locales: SuccessFactorsLocale[]; userId: string; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "CSOD"; auth: AuthConnectorForm; common: CommonConnectorForm; userId: string; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "VIVA_LEARNING"; auth: AuthConnectorForm; common: CommonConnectorForm; learningProviderId: string; tenantId: string; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "DOCEBO"; auth: AuthConnectorForm; channelId: number; common: CommonConnectorForm; password: string; username: string }
  | { connectorType: "DOCEBO_CATALOG"; auth: AuthConnectorForm; catalogCode: string; categoryCode: string; common: CommonConnectorForm; password: string; username: string }
  | { connectorType: "DEGREED"; auth: AuthConnectorForm; common: CommonConnectorForm; lmsApiUri: string; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "EDCAST"; auth: AuthConnectorForm; common: CommonConnectorForm; oauthTokenUri: string; userIdentifier: ConnectorUserIdentifier; xapiActorId: XApiActorId }
  | { connectorType: "EDCAST_V2"; auth: AuthConnectorForm; common: CommonConnectorForm; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "LRS_XAPI"; auth: AuthConnectorForm; common: CommonConnectorForm; oauthTokenUri: string; userIdentifier: ConnectorUserIdentifier; xapiActorId: XApiActorId }
  | { connectorType: "MS_GRAPH"; auth: AuthConnectorForm; common: CommonConnectorForm; connectionId: string; tenantId: string; userIdentifier: ConnectorUserIdentifier }
  | { connectorType: "GEMINI_DATASTORE"; auth: AuthConnectorForm; common: CommonConnectorForm; dataStoreId: string; location: string; projectId: string }
  | { connectorType: "SUM_TOTAL"; auth: AuthConnectorForm; common: CommonConnectorForm; oauthTokenUri: string; userIdentifier: ConnectorUserIdentifier; xapiActorId: XApiActorId };

export type NotificationCheckResponse = {
  notifications: NotificationDisplay[];
};

export type NotificationDisplay = {
  customIcon: string | null;
  layout: string;
  message: string | null;
  notificationId: number;
  type: string;
  urls: string[] | null;
};

export namespace OAuth2AuthorizationGrantType {
  const values = ["REFRESH_TOKEN", "CLIENT_CREDENTIALS", "PASSWORD", "AUTHORIZATION_CODE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OAuth2AuthorizationGrantType = OAuth2AuthorizationGrantType.Impl;

export type OAuth2RegisteredClientEditView = {
  accessTokenValidityInSeconds: bigint;
  authorizationGrantTypes: OAuth2AuthorizationGrantType[];
  clientId: string;
  clientName: string;
  clientSecret: string;
  corporateId: number;
  publicClient: boolean;
  redirectUris: string;
  refreshTokenValidityInSeconds: bigint;
  requireAuthorizationConsent: boolean;
  requireProofKey: boolean;
  scope: OAuth2Scope[];
  utmSource: string | null;
};

export type OAuth2RegisteredClientView = {
  accessTokenValidityInSeconds: bigint;
  authorizationGrantTypes: OAuth2AuthorizationGrantType[];
  clientId: string;
  clientName: string;
  clientSecret: string;
  corporateId: number;
  publicClient: boolean;
  redirectUris: string[];
  refreshTokenValidityInSeconds: bigint;
  requireAuthorizationConsent: boolean;
  requireProofKey: boolean;
  scope: OAuth2Scope[];
  utmSource: string | null;
};

export namespace OAuth2Scope {
  const values = ["LIBRARY_V2", "LIBRARY_V2_TRUSTED", "USERS", "MOBILEAPP", "SCIM", "PLAY_HT", "CSOD_CATALOG", "WRITERS_V1", "VERIFAI_V1", "LIBRARY_V3", "LIBRARY_V3_TRUSTED", "MCP", "VIBE_V1"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OAuth2Scope = OAuth2Scope.Impl;

export type OAuth2Token = {
  accessTokenExpiredAt: DateTime<true> | null;
  accessTokenValue: string | null;
  corporateId: number;
  isExpired: boolean;
  refreshTokenExpiredAt: DateTime<true> | null;
  refreshTokenValue: string | null;
  tokenType: string | null;
};

export type OauthSettingsForm = {
  authorizationUri: string | null;
  clientId: string | null;
  clientSecret: string | null;
  hd: string | null;
  idpLogoutUri: string | null;
  requestTokenUri: string | null;
  scope: string | null;
  ssoAttributeEntry: SsoAttributeEntry;
  userInfoUri: string | null;
};

export type OfferEditForm = {
  amount: number | null;
  candidates: CandidateRequest[];
  currencyCode: string | null;
  deadline: { year: number; month: number; day: number } | null;
  informOnlyActiveCandidates: boolean;
  message: string | null;
  offerId: bigint | null;
  productionIdentifier: ProductionIdentifier | null;
};

export type OfferEditView = {
  amount: number | null;
  candidates: CandidateTagView[];
  currencyCode: string | null;
  currencyOptions: DropdownEntry[];
  deadline: { year: number; month: number; day: number } | null;
  message: string | null;
  offerId: bigint | null;
};

export type OfferFormWidgetProps = {
  productionIdentifier: ProductionIdentifier;
};

export namespace OfferState {
  const values = ["OFFERED", "DECLINED", "EXPIRED", "ACCEPTED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OfferState = OfferState.Impl;

export namespace OnboardingCardType {
  const values = ["SESSION_LENGTH", "PREFERRED_FORMATS", "CONTENT_PREFERENCE", "SCHEDULE", "PROFILE_CONFIRMATION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnboardingCardType = OnboardingCardType.Impl;

export type OnlineContentActivityReportView = {
  content: ContentMetadata_ONLINE_CONTENT;
  customer: CustomerReportView | null;
  timeStamp: DateTime<true>;
};

export type OnlineContentAdminSearchForm = {
  corporateId: number | null;
  includeInactive: boolean;
  language: Language;
  query: string;
};

export type OnlineContentAuthor = {
  biography: string;
  gender: OnlineContentCreatorGender;
  id: bigint;
};

export type OnlineContentCardView = {
  authorRating: OnlineContentRating;
  contentRating: OnlineContentRating;
  creator: string;
  duration: number;
  formatType: OnlineContentFormatType;
  keywords: string[];
  onlineContentId: bigint;
  site: string;
  sourceRating: OnlineContentRating;
  title: string;
};

export type OnlineContentCreator = {
  creatorId: bigint;
  firstName: string;
  lastName: string;
  linkedInUrl: string | null;
  middleName: string;
  rating: OnlineContentRating;
  trustMarkers: OnlineContentTrustMarker[];
};

export namespace OnlineContentCreatorGender {
  const values = ["MALE", "FEMALE", "UNKNOWN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentCreatorGender = OnlineContentCreatorGender.Impl;

export type OnlineContentCreatorMetadata = {
  creatorId: bigint;
  editorialTeam: boolean;
  firstName: string;
  gender: OnlineContentCreatorGender;
  lastName: string;
  middleName: string | null;
  rating: number | null;
};

export type OnlineContentDetail = {
  aboutTheAuthor: string | null;
  authorTrustMarkers: string[];
  authors: OnlineContentAuthor[];
  contentIsMarkdown: boolean;
  contentSummary: string;
  contentTrustMarkers: string[];
  jobProfiles: string[];
  keywords: string;
  language: Language;
  onlineContentId: bigint;
  ratingRationale: string | null;
  rewrittenText: string;
  sourceTrustMarkers: string[];
  subtitle: string;
  synopsis: string;
  takeaways: string;
  title: string;
  whatYouWillLearn: string;
};

export type OnlineContentDetailPageProps = {
  aboutTheAuthorTitle: string;
  availableLanguages: Language[];
  breadCrumbsPath: BreadCrumbItem[];
  canOpenSource: boolean;
  contentDetail: OnlineContentDetail;
  localizedSkills: string[];
  metadata: ContentMetadata_ONLINE_CONTENT;
  relatedContentItems: ContentMetadata[];
};

export type OnlineContentDetailPagePropsResponse = { kind: "NOTFOUND" } | { kind: "VERIFAI"; props: OnlineContentDetailPageProps };

export type OnlineContentDetailViewItem = {
  coverUri: string;
  creators: OnlineContentCreator[];
  evergreen: boolean;
  formatColorBase: string;
  formatColorDark: string;
  formatColorLight: string;
  formatType: OnlineContentFormatType;
  hashId: string;
  keywords: string[];
  onlineContentId: bigint | null;
  originalText: string | null;
  profiles: OnlineContentProfile[];
  publicationDate: { year: number; month: number; day: number };
  rating: OnlineContentRating;
  readingTime: number;
  resourceUrl: string;
  rewrittenText: string | null;
  site: OnlineContentSite | null;
  subtitle: string | null;
  summary: string;
  synopsis: string;
  title: string;
  trustMarkers: OnlineContentTrustMarker[];
  whatYouWillLearn: string[];
};

export type OnlineContentDetailViewTranslations = {
  actions: string;
  authorRating: string;
  authors: string;
  content: string;
  contentRating: string;
  cta: string;
  export: string;
  format: string;
  jobProfiles: string;
  languages: OnlineContentLanguageType[];
  minutes: string;
  notAvailable: string;
  ratingDetails: string;
  source: string;
  sourceRating: string;
  summary: string;
  synopsis: string;
  takeaways: string;
};

export namespace OnlineContentDownloadFormat {
  const values = ["WEB", "PDF"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentDownloadFormat = OnlineContentDownloadFormat.Impl;

export type OnlineContentFilterableListPortletView = {
  filters: FormFilter[];
  items: ContentMetadata_ONLINE_CONTENT[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type OnlineContentFiltersForm = {
  expertPickFormFilter: boolean | null;
  formatTypeFilter: OnlineContentFormatType[];
};

export namespace OnlineContentFormatType {
  const values = ["ARTICLE", "BLOGPOST", "PODCAST", "REPORT", "VIDEO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentFormatType = OnlineContentFormatType.Impl;

export type ContentId_ONLINE_CONTENT = {
  contentType: "ONLINE_CONTENT";
  id: bigint;
};

export type OnlineContentLanguageType = {
  isoCode: string;
  name: string;
};

export type OnlineContentListCustomPortletEditView = {
  onlineContentItems: ContentMetadata_ONLINE_CONTENT[];
  portlet: PortletEditView;
};

export type OnlineContentListCustomPortletForm = {
  onlineContentIds: bigint[];
  portletForm: PortletForm;
};

export type OnlineContentListPortletView = {
  items: ContentMetadata_ONLINE_CONTENT[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type ContentMetadata_ONLINE_CONTENT = {
  authorsDetail: {
    editorialTeam: boolean;
    gender: OnlineContentCreatorGender;
    name: string;
    rating: number | null;
  }[];
  common: ContentMetadataCommon;
  contentId: ContentId_ONLINE_CONTENT;
  contentProductionResourceId: bigint | null;
  contentRating: number | null;
  contentType: "ONLINE_CONTENT";
  deactivatedAt: DateTime<true> | null;
  formatType: OnlineContentFormatType;
  hashId: string;
  modifiedAt: DateTime<true> | null;
  overallRating: number | null;
  publicationDate: { year: number; month: number; day: number };
  readingTime: number;
  resourceUrl: string;
  sourceDetail: {
    logoUri: string | null;
    rating: number | null;
    title: string;
    url: string;
  };
  subtitle: string | null;
};

export type OnlineContentNavLink = {
  ico: string | null;
  name: string;
  url: string;
};

export type OnlineContentNavigationProps = {
  navLinks: OnlineContentNavLink[];
};

export type OnlineContentProfile = {
  profile: string;
  profileId: bigint;
};

export namespace OnlineContentRating {
  const values = ["A", "B", "C", "F"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentRating = OnlineContentRating.Impl;

export type OnlineContentRedirectItem = {
  onlineContentId: bigint | null;
  resourceUrl: string;
};

export type OnlineContentSite = {
  rating: OnlineContentRating;
  siteId: bigint | null;
  siteUrl: string | null;
  title: string | null;
  trustMarkers: OnlineContentTrustMarker[] | null;
};

export type OnlineContentSiteMetadata = {
  logoUri: string | null;
  rating: number | null;
  siteId: bigint;
  siteUrl: string;
  title: string;
};

export namespace OnlineContentStatus {
  const values = ["PREPROCESSING", "OPEN", "ASSIGNED", "SUBMITTED", "EDITED", "REJECTED", "ARCHIVED", "PUBLISHED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentStatus = OnlineContentStatus.Impl;

export type OnlineContentTrustMarker = {
  label: string;
  trustMarkerId: bigint;
};

export namespace OnlineContentTrustMarkerType {
  const values = ["CONTENT_RELIABILITY", "CONTENT_RELEVANCE", "CONTENT_INNOVATION", "CONTENT_STYLE", "SITE", "CREATOR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OnlineContentTrustMarkerType = OnlineContentTrustMarkerType.Impl;

export type OpenJobOverviewList = {
  proofreading: number;
  writing: number;
};

export type OracleConnectorForm = {
  accountId: string;
  apiKey: string;
  common: CommonConnectorForm;
  connectorType: "ORACLE";
  userIdentifier: ConnectorUserIdentifier;
};

export type OutboundLink = {
  id: bigint | null;
  meta: string;
  targetName: string | null;
  targetUrl: string;
  type: OutboundLinkType;
};

export namespace OutboundLinkType {
  const values = ["GENERIC", "BUYBOOK", "MIAB_SCHEDULE", "ACTIONABLE_SCHEDULE", "SUMMARY_SCHEDULE", "VERIFY_SCHEDULE", "VERIFY_SOURCE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type OutboundLinkType = OutboundLinkType.Impl;

export type PageInfo = {
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PageRequest = {
  page: number;
  psize: number;
  sortBy: string | null;
  sortDesc: boolean;
};

export type PageViewMeta = {
  ci: bigint | null;
  ct: ContentItemType;
  orr: string;
  p: string;
  r: string | null;
};

export type PageViewPayload = {
  iframe: boolean;
  meta: string;
  timezone: string;
};

export type PagedActionableMetadata = {
  items: ContentMetadata_ACTIONABLE[];
  pageInfo: PageInfo;
};

export type PagedAnnotationListView = {
  items: AnnotationView[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PagedAuthorAdminListView = {
  items: AuthorAdminListView[];
  pageInfo: PageInfo;
};

export type PagedChannelMetadata = {
  items: ChannelMetadata[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PagedConnectorCompletionExecutionLogListView = {
  items: ConnectorCompletionExecutionLogListView[];
  pageInfo: PageInfo;
};

export type PagedConnectorContentExecutionListView = {
  items: ConnectorContentExecutionListView[];
  pageInfo: PageInfo;
};

export type PagedConnectorContentExecutionLogListView = {
  items: ConnectorContentExecutionLogListView[];
  pageInfo: PageInfo;
};

export type PagedContentProductionAuthorList = {
  items: ContentProductionAuthorItem[];
  pageInfo: PageInfo;
};

export type PagedContentProductionSourceList = {
  items: ContentProductionSourceItem[];
  pageInfo: PageInfo;
};

export type PagedContentProductionStepViewList = {
  items: ContentProductionStepView[];
  pageInfo: PageInfo;
  possibleAssignees: Assignee[];
  possibleSkillGroups: SkillGroupOption[];
};

export type PagedCorporateCommentListView = {
  items: CorporateCommentListView[];
  pageInfo: PageInfo;
};

export type PagedCorporateCustomerListView = {
  items: CorporateCustomerListView[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PagedCustomPageEditListView = {
  items: CustomPageEditListView[];
  pageInfo: PageInfo;
};

export type PagedCustomPageListView = {
  items: CustomPageListView[];
  pageInfo: PageInfo;
};

export type PagedEmailBatchJobListView = {
  items: EmailBatchJobListView[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PagedEmailCategoryAdminView =
  | { kind: "FOLLOWUP"; items: EmailFollowUpCategoryAdminView[]; pageInfo: PageInfo }
  | { kind: "DEFAULT"; items: EmailDefaultCategoryAdminView[]; pageInfo: PageInfo }
  | { kind: "EDUCATION"; items: EmailEducationCategoryAdminView[]; pageInfo: PageInfo }
  | { kind: "MANUAL"; items: EmailManualCategoryAdminView[]; pageInfo: PageInfo }
  | { kind: "ONBOARDING"; items: EmailOnboardingCategoryAdminView[]; pageInfo: PageInfo }
  | { kind: "RENEWAL"; items: EmailRenewalCategoryAdminView[]; pageInfo: PageInfo };

export type PagedEmailDefaultCategoryAdminView = {
  items: EmailDefaultCategoryAdminView[];
  kind: "DEFAULT";
  pageInfo: PageInfo;
};

export type PagedEmailEducationCategoryAdminView = {
  items: EmailEducationCategoryAdminView[];
  kind: "EDUCATION";
  pageInfo: PageInfo;
};

export type PagedEmailFollowUpCategoryAdminView = {
  items: EmailFollowUpCategoryAdminView[];
  kind: "FOLLOWUP";
  pageInfo: PageInfo;
};

export type PagedEmailManualCategoryAdminView = {
  items: EmailManualCategoryAdminView[];
  kind: "MANUAL";
  pageInfo: PageInfo;
};

export type PagedEmailOnboardingCategoryAdminView = {
  items: EmailOnboardingCategoryAdminView[];
  kind: "ONBOARDING";
  pageInfo: PageInfo;
};

export type PagedEmailRenewalCategoryAdminView = {
  items: EmailRenewalCategoryAdminView[];
  kind: "RENEWAL";
  pageInfo: PageInfo;
};

export type PagedExportReportListView = {
  items: ExportReportListView[];
  pageInfo: PageInfo;
};

export type PagedMiabMetadata = {
  items: ContentMetadata_MIAB[];
  pageInfo: PageInfo;
};

export type PagedMyListView = {
  items: MyListView[];
  pageInfo: PageInfo;
};

export type PagedOnlineContentMetadata = {
  items: ContentMetadata_ONLINE_CONTENT[];
  pageInfo: PageInfo;
};

export type PagedPeerInsightFeedbackListView = {
  items: PeerInsightFeedbackListView[];
  page: PageInfo;
};

export type PagedReportListView = {
  items: ReportListView[];
  pageInfo: PageInfo;
};

export type PagedSkillBoostMetadata = {
  items: ContentMetadata_QUICKWIN[];
  pageInfo: PageInfo;
};

export type PagedSummaryListView = {
  items: SummaryListView[];
  pageInfo: PageInfo;
};

export type PagedSummaryMetadata = {
  items: ContentMetadata_SUMMARY[];
  pageInfo: PageInfo;
};

export type PagedSummarySearchResultListView = {
  items: SummarySearchResultListView[];
  pageInfo: PageInfo;
};

export type PagedUnidentifiedCorporateCustomerListView = {
  items: UnidentifiedCorporateCustomerListView[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export namespace ParentalSettingType {
  const values = ["CRUDE_LANGUAGE", "RELIGIOUS", "POLITICAL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ParentalSettingType = ParentalSettingType.Impl;

export type PayPalPaymentView = {
  acqAuthorizationCode: string | null;
  authorizationCode: string | null;
  merchantId: string | null;
  paypalBillingAgreementId: string | null;
  refNumber: string | null;
  transactionId: number;
};

export namespace PaymentType {
  const values = ["CC", "INVOICE", "PAYPAL", "LSV", "MILES", "ALIPAY", "IOS", "ANDROID", "STRIPE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PaymentType = PaymentType.Impl;

export type PdfMetadata = {
  authors: {
    firstName: string;
    lastName: string;
    middleName: string;
  }[];
  formatType: string | null;
  publicationDate: string | null;
  source: {
    title: string;
    url: string;
  } | null;
  subtitle: string | null;
  title: string;
};

export type PeerInsightFeedbackDashboardView = {
  areaOfExpertise: AreaOfExpertise;
  feedbackText: string;
  likeCount: number;
  liked: boolean;
  peerInsightFeedbackId: bigint;
  question: PeerInsightQuestionView;
};

export type PeerInsightFeedbackDashboardViewList = {
  items: PeerInsightFeedbackDashboardView[];
};

export type PeerInsightFeedbackForm = {
  actionableId: bigint;
  freeActionableActive: boolean;
  peerInsightFormSteps: PeerInsightFormStep[];
  strictPrivacyEnabled: boolean;
};

export type PeerInsightFeedbackListView = {
  actionableId: bigint;
  actionableTitle: string;
  areaOfExpertise: AreaOfExpertise;
  creationDate: DateTime<true>;
  feedbackIndex: number | null;
  feedbackScore: number | null;
  feedbackText: string | null;
  peerInsightFeedbackId: bigint;
  question: PeerInsightQuestionView;
  status: ApprovalStatus | null;
};

export type PeerInsightFeedbackUpdateRequest = {
  approvalStatus: ApprovalStatus;
  peerInsightFeedbackId: bigint;
};

export type PeerInsightFormNavigation = {
  navigationDirection: NavigationDirection;
  peerInsightFormSteps: PeerInsightFormStep[];
  peerInsightQuestionId: bigint;
};

export type PeerInsightFormStep = {
  hasNext: boolean;
  hasPrevious: boolean;
  question: PeerInsightQuestionView;
  selectedScore: number | null;
  selectedSuggestedAnswers: bigint[];
  suggestedAnswers: SuggestedAnswerView[];
  text: string;
};

export type PeerInsightFormSubmitResponse = {
  faultyFields: bigint[] | null;
};

export type PeerInsightQuestionKeysResponse = {
  items: PeerInsightQuestionView[];
};

export type PeerInsightQuestionView = {
  allowedInNonStrictPrivacy: boolean;
  allowedInStrictPrivacy: boolean;
  key: string;
  numeric: boolean;
  peerInsightQuestionId: bigint;
  showSuggestedAnswers: boolean;
};

export type PeerInsightTextFeedbackRequest = {
  language: Language | null;
  pageRequest: PageRequest;
  questionKey: bigint | null;
};

export type Person = {
  firstName: string;
  gender: Gender;
  lastName: string | null;
  middleName: string | null;
};

export type PortalStatusListView = {
  active: boolean;
  name: string;
};

export namespace PortalType {
  const values = ["b2bAsp", "leadPortals", "freeDownloads"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortalType = PortalType.Impl;

export type PortletContainer = {
  portletIds: number[];
};

export type PortletContainerAdminEditForm = {
  portletContainerId: number;
  portletIds: number[];
  showDefaults: boolean;
  status: PortletContainerStatus;
};

export type PortletContainerAdminEditView = {
  customPortlets: PortletMetadata[];
  customized: boolean;
  defaultPortlets: PortletMetadata[];
  language: Language;
  portletContainerId: number;
  showDefaults: boolean;
  status: PortletContainerStatus;
};

export namespace PortletContainerStatus {
  const values = ["DRAFT", "LIVE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletContainerStatus = PortletContainerStatus.Impl;

export type PortletEditView = {
  defaultFilterAllLanguages: boolean;
  embedSortingFiltering: boolean;
  itemCount: number;
  itemLayout: ItemLayout;
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  portletId: number;
  portletLayout: PortletLayout;
  showTitle: boolean;
  subtitles: { additionalProperties: Record<string, string> };
  titles: { additionalProperties: Record<string, string> };
  type: PortletType;
  userGroupIds: number[];
  web: boolean;
};

export type PortletForm = {
  defaultFilterAllLanguages: boolean;
  embedSortingFiltering: boolean;
  itemCount: number;
  itemLayout: ItemLayout;
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  portletLayout: PortletLayout;
  showTitle: boolean;
  subtitles: { additionalProperties: Record<string, string> };
  titles: { additionalProperties: Record<string, string> };
  userGroupIds: number[];
  web: boolean;
};

export namespace PortletLayout {
  const values = ["GRID", "CAROUSEL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletLayout = PortletLayout.Impl;

export type PortletLibraryView = {
  autoGeneratedPortletIds: number[];
  customPortlets: PortletMetadata[];
  defaultPortlets: PortletMetadata[];
  usedCustomPagePortletIds: number[];
  usedStartPageDraftPortletIds: number[];
  usedStartPageLivePortletIds: number[];
};

export type PortletMetadata = {
  custom: boolean;
  defaultFilterAllLanguages: boolean;
  embedSortingFiltering: boolean;
  hideTitle: boolean;
  itemCount: number;
  itemLayout: ItemLayout;
  name: string;
  portletId: number;
  portletLayout: PortletLayout;
  subType: string | null;
  subtitle: string;
  title: string;
  type: PortletType;
  virtual: boolean;
};

export namespace PortletType {
  const values = [
    "IMAGETEXT",
    "SUMMARYLIST",
    "TRENDINGCHANNEL",
    "FEATUREDCHANNEL",
    "FOLLOWINGCHANNEL",
    "MYLIST",
    "PRIMARYTOPICCHANNEL",
    "CHANNELLIST",
    "ANNOTATIONLIST",
    "ACTIONABLELIST",
    "ACTIONABLELISTCUSTOM",
    "MYACTIONABLES",
    "FREESUMMARY",
    "SUMMARYLISTCUSTOM",
    "CUSTOMPAGELIST",
    "PROMOBOXCONFIG",
    "MYMIABS",
    "ASKGETABSTRACT",
    "CUSTOMIZEDMIABS",
    "MIABLIST",
    "MIABLISTCUSTOM",
    "MYQUICKWINS",
    "SKILLBOOSTLISTCUSTOM",
    "GROWTHDASHBOARD",
    "VERIFAILISTNEW",
    "VERIFAILISTNEWPUBLISHED",
    "VERIFAIMOSTPOPULAR",
    "MYVERIFAIS",
    "ONLINECONTENTLISTCUSTOM",
    "MIXEDLISTCUSTOM",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PortletType = PortletType.Impl;

export type ProTip = {
  proTipId: bigint;
  summary: ContentMetadata_SUMMARY;
  tip: string;
};

export type ProTipPromtAnswer = {
  proTips: string[];
};

export type ProTipSpotlight = {
  proTip: ProTip;
  sourceUri: string;
};

export type ProductAdminViewList = {
  list: {
    code: string;
    productId: bigint;
  }[];
};

export type ProductionIdentifier = {
  dataId: number;
  productionStep: ProductionStep;
  productionType: ProductionType;
};

export namespace ProductionStep {
  const values = ["UNDEFINED", "WRITING", "CONTENT_EDITING", "COPY_EDITING", "PROOFREADING", "AUDIO_EDITING", "AUDIO_PRODUCING", "AUDIO_PROOFING"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ProductionStep = ProductionStep.Impl;

export type ProductionStepListView = {
  productionStepViews: ProductionStepView[];
};

export namespace ProductionStepState {
  const values = ["UNDEFINED", "OFFERED", "DECLINED", "IN_PROGRESS", "FINISHED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ProductionStepState = ProductionStepState.Impl;

export type ProductionStepView = {
  baseDisplayFee: string | null;
  offerGroups: CandidateOfferGroupView[];
  offerId: bigint | null;
  productionStep: ProductionStep;
  productionStepState: ProductionStepState;
};

export namespace ProductionType {
  const values = ["ABSTRACT", "REVIEW", "AUDIO"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ProductionType = ProductionType.Impl;

export type PromoBoxConfigEditView = {
  customBoxes: PromoBoxEditView[];
  defaultBoxes: PromoBoxEditView[];
  defaultBoxesHidden: number[];
  showDefaultBoxes: boolean;
};

export type PromoBoxConfigForm = {
  items: PromoBoxConfigItemForm[];
  itemsHidden: number[];
  showDefaults: boolean;
};

export type PromoBoxConfigItemForm = {
  endsAt: { year: number; month: number; day: number } | null;
  promoBoxId: number;
  startsAt: { year: number; month: number; day: number } | null;
};

export type PromoBoxConfigPreview = {
  boxes: PromoBoxView[];
};

export type PromoBoxConfigPreviewFilters = {
  language: Language;
  loggedIn: boolean;
  platform: string;
};

export type PromoBoxEditForm = {
  image: FormFile | null;
  localizedImages: { additionalProperties: Record<string, FormFile> };
  saveReq: PromoBoxSaveRequest;
};

export type PromoBoxEditProps = {
  corporateId: number;
  languages: Language[];
  promoBoxId: number;
};

export type PromoBoxEditView = {
  backgroundColor: string | null;
  brightTextColor: boolean;
  corporateId: number;
  designApproved: boolean;
  endsAt: { year: number; month: number; day: number } | null;
  excludedLanguages: Language[];
  imageUri: string;
  localizedDescription: { additionalProperties: Record<string, string> };
  localizedImageUris: { additionalProperties: Record<string, string> };
  localizedTitle: { additionalProperties: Record<string, string> };
  localizedWebLinks: { additionalProperties: Record<string, string> };
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  promoBoxId: number;
  requiredFeatures: string[];
  startsAt: { year: number; month: number; day: number } | null;
  tag: PromoBoxTag;
  web: boolean;
  webLink: string;
};

export type PromoBoxLibraryView = {
  customBoxes: PromoBoxEditView[];
  defaultBoxes: PromoBoxEditView[];
};

export type PromoBoxPortletView = {
  items: PromoBoxView[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type PromoBoxSaveRequest = {
  excludedLanguages: Language[];
  imageUri: string | null;
  localizedDescription: { additionalProperties: Record<string, string> };
  localizedImageUris: { additionalProperties: Record<string, string> };
  localizedTitle: { additionalProperties: Record<string, string> };
  localizedWebLinks: { additionalProperties: Record<string, string> };
  loggedIn: boolean;
  loggedOut: boolean;
  mobile: boolean;
  name: string;
  requiredFeatures: string[];
  tag: PromoBoxTag;
  web: boolean;
  webLink: string;
};

export namespace PromoBoxTag {
  const values = [
    "UNDEFINED",
    "EXPERT_WEBINAR",
    "PRO_TALK",
    "FEATURED",
    "FEATURED_SUMMARY",
    "FEATURED_ACTIONABLE",
    "FEATURED_TOPIC",
    "EMERGING_TREND",
    "INTERNATIONAL_DAY",
    "LEARNING_AT_WORK_WEEK",
    "Q_A",
    "VALUES_AND_CAPABILITIES",
    "RECOMMENDED_READS",
    "INTERNAL_THOUGHT_LEADER",
    "READING_CALENDAR",
    "NEW",
    "SPOTLIGHT",
    "PROMOTION",
    "DEAL",
    "NEWS",
    "CAMPAIGN",
    "BOOK_AWARD",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PromoBoxTag = PromoBoxTag.Impl;

export type PromoBoxView = {
  backgroundColor: string | null;
  brightTextColor: boolean;
  description: string | null;
  designApproved: boolean;
  imageUri: string;
  promoBoxId: number;
  tag: PromoBoxTag;
  title: string | null;
  webLink: string;
};

export type ProximityPromptImport = {
  data: {
    dataId: number;
    proximity: {
      primaryTag: ProximityScore;
      secondaryTags: ProximityScore[];
    };
  }[];
};

export type ProximityScore = {
  name: string;
  score: number;
};

export type PublicationDateExtractionResult = {
  date: string | null;
};

export type PublisherContactEditRequest = {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  publisherContactId: number;
  title: number;
};

export type PublisherEditViewForm = {
  aiAllowed: boolean;
  approvalNeeded: boolean;
  contractNeeded: boolean;
  countryCode: string | null;
  englishName: string | null;
  ignoringWarning: boolean;
  imprintName: string | null;
  initialContactEmail: string | null;
  marketingActivityPermission: MarketingActivityPermission;
  noAudioRightsNeeded: boolean;
  noRightsNeeded: boolean;
  paymentNeeded: boolean;
  publisherContacts: PublisherContactEditRequest[];
  publisherId: number;
  publisherName: string;
  publisherShortName: string | null;
  publisherType: PublisherType;
  publisherURL: string | null;
  remarks: string | null;
  requestAudioRights: boolean;
  selectedRightsManagerCustomerId: number;
  showLink: boolean;
  town: string | null;
  usedInProduction: boolean;
  visible: boolean;
};

export type PublisherStatsLinkView = {
  createdAt: DateTime<true> | null;
  expired: boolean;
  expiresAt: DateTime<true> | null;
  path: string | null;
};

export namespace PublisherType {
  const values = ["TRADITIONAL", "OPTOUT", "AGENCY", "SELFPUBLISHER"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PublisherType = PublisherType.Impl;

export namespace PushFrequency {
  const values = ["NONE", "DAILY", "ONCE_A_WEEK", "TWICE_A_MONTH", "ONCE_A_MONTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type PushFrequency = PushFrequency.Impl;

export type QuickWinContent = {
  content: QuickWinContentData;
  metadata: ContentMetadata_QUICKWIN;
  summary: ContentMetadata_SUMMARY;
};

export type QuickWinContentData =
  | { quickWinType: "INSIGHT"; mcqCorrectAnswer: string; mcqFeedback: string; mcqOptions: string[]; mcqQuestion: string; script: string; takeaway: string }
  | { quickWinType: "REFLECTION"; mcqFeedback: string; mcqOptions: string[]; mcqQuestion: string; script: string; takeaway: string };

export type QuickWinContentData_INSIGHT = {
  mcqCorrectAnswer: string;
  mcqFeedback: string;
  mcqOptions: string[];
  mcqQuestion: string;
  quickWinType: "INSIGHT";
  script: string;
  takeaway: string;
};

export type QuickWinContentData_REFLECTION = {
  mcqFeedback: string;
  mcqOptions: string[];
  mcqQuestion: string;
  quickWinType: "REFLECTION";
  script: string;
  takeaway: string;
};

export type QuickWinDemoProps = {
  quickwins: ContentMetadata_QUICKWIN[];
};

export type QuickWinFeedback = {
  isPositive: boolean;
};

export namespace QuickWinFilterIxStatus {
  const values = ["ALL", "STARTED", "COMPLETED"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type QuickWinFilterIxStatus = QuickWinFilterIxStatus.Impl;

export type ContentId_QUICKWIN = {
  contentType: "QUICKWIN";
  id: bigint;
};

export type QuickWinInsightGeneration = {
  audioScript: string | null;
  step1: QuickWinInsightGenerationStep1;
  step2: QuickWinInsightGenerationStep2;
};

export type QuickWinInsightGenerationStep1 = {
  description: string;
  script: string;
  title: string;
};

export type QuickWinInsightGenerationStep1Batch = {
  insights: QuickWinInsightGenerationStep1[];
};

export type QuickWinInsightGenerationStep2 = {
  mcqCorrectAnswer: string;
  mcqFeedback: string;
  mcqOptions: string[];
  mcqQuestion: string;
  takeaway: string;
};

export type QuickWinInsightProofread = {
  step1: QuickWinInsightGenerationStep1;
  step2: QuickWinInsightGenerationStep2;
};

export type QuickWinListPortletView = {
  items: ContentMetadata_QUICKWIN[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type ContentMetadata_QUICKWIN = {
  common: ContentMetadataCommon;
  contentId: ContentId_QUICKWIN;
  contentType: "QUICKWIN";
  dataId: number;
  modifiedAt: DateTime<true>;
  quickWinType: QuickWinType;
};

export type QuickWinPromptPlaygroundRequest = {
  dataId: number;
  generate: boolean;
  generateAudio: boolean;
};

export type QuickWinPromptPlaygroundResponse = {
  items: ContentMetadata_QUICKWIN[];
};

export type QuickWinReflectionGeneration = {
  audioScript: string | null;
  step1: QuickWinReflectionGenerationStep1;
  step2: QuickWinReflectionGenerationStep2;
};

export type QuickWinReflectionGenerationStep1 = {
  description: string;
  script: string;
  title: string;
};

export type QuickWinReflectionGenerationStep1Batch = {
  reflections: QuickWinReflectionGenerationStep1[];
};

export type QuickWinReflectionGenerationStep2 = {
  mcqFeedback: string;
  mcqOptions: string[];
  mcqQuestion: string;
  takeaway: string;
};

export type QuickWinReflectionProofread = {
  step1: QuickWinReflectionGenerationStep1;
  step2: QuickWinReflectionGenerationStep2;
};

export type QuickWinSelectedAnswerRequest = {
  selectedAnswerIndex: number;
};

export type QuickWinSpotlightProps = {
  metadata: ContentMetadata_QUICKWIN | null;
  topics: string[];
};

export type QuickWinSpotlightRequest = {
  forcedQuickWinId: bigint | null;
  reshuffledQuickWinId: bigint | null;
};

export type QuickWinSpotlightSkillsForm = {
  channelIds: bigint[];
};

export type QuickWinSpotlightSkillsResponse = {
  skillGroups: SkillGroup[];
};

export type QuickWinTopicGeneration = {
  frameworks: string[];
  lessons: string[];
  reflections: string[];
};

export namespace QuickWinType {
  const values = ["INSIGHT", "REFLECTION"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type QuickWinType = QuickWinType.Impl;

export type RatingRationaleAiResponse = {
  rationale: string;
};

export namespace ReadingListType {
  const values = [
    "LATEST",
    "TOP_DOWNLOADS",
    "EDITORS_PICKS",
    "BUSINESS_BOOKS",
    "VIDEOS",
    "PODCASTS",
    "ECONOMIC_REPORTS",
    "BUSINESS_ARTICLES",
    "CLASSIC_BOOKS",
    "SCIENCE",
    "RECOMMENDED_SUMMARIES",
    "BOOKMARKS",
    "LIKED",
    "HISTORY",
    "PUSH_HISTORY",
    "TOP_AUDIO_CONSUMPTION",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ReadingListType = ReadingListType.Impl;

export type RedFlagForm = {
  text: string;
};

export type RedFlagUnparsedForm = {
  text: string;
};

export type RedFlagValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: RedFlagForm };

export type ReferencedItemEdit = {
  refItem: ReferencedLearningItem;
  tags: Tag[];
};

export type ReferencedItemSaveRequest = {
  items: ReferencedItemEdit[];
};

export type ReferencedLearningItem =
  | { learningItemType: "SUMMARY"; id: number }
  | { learningItemType: "ACTIONABLE"; id: bigint }
  | { learningItemType: "VERIFAI"; id: bigint }
  | { learningItemType: "EXPERT_VIDEO"; id: bigint }
  | { learningItemType: "QUICK_WIN"; id: bigint }
  | { learningItemType: "CPS_RESOURCE"; id: bigint };

export type ReferencedLearningItemList = {
  items: ReferencedLearningItem[];
};

export type ReferrerProtectionForm = {
  checkReferrer: boolean;
  referrers: string[];
};

export type ReferrerProtectionFormSaveResponse = { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons } | { kind: "SUCCESS"; form: ReferrerProtectionForm };

export type RelatedSummariesDetailPageProps = {
  relatedSummaries: ContentMetadata_SUMMARY[];
};

export type RenewSubscriptionForm = {
  ccNumber: string | null;
  ccType: string | null;
  cvv: string | null;
  existing: boolean;
  expMonth: string | null;
  expYear: string | null;
  lsvAccountNumber: string | null;
  lsvBankName: string | null;
  lsvBankTown: string | null;
  lsvClearingNumber: string | null;
  lsvFirstName: string | null;
  lsvIban: string | null;
  lsvLastName: string | null;
  manualPrice: number | null;
  paymentType: string | null;
  productId: number | null;
  promotionCode: string | null;
  street: string | null;
  subscriptionId: number;
  zip: string | null;
};

export namespace ReportFrequency {
  const values = ["ONE_TIME", "DAILY", "WEEKLY", "MONTHLY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ReportFrequency = ReportFrequency.Impl;

export type ReportListView = {
  active: boolean;
  corporateId: number;
  customerFullName: string;
  frequency: ReportFrequency;
  isRunning: boolean | null;
  name: string;
  prefixFileName: boolean;
  reportId: number;
  reportType: ReportType;
  targetExcel: boolean;
  timeFrameFrom: { year: number; month: number; day: number };
  timeFrameTo: { year: number; month: number; day: number };
  zipFile: boolean;
};

export type ReportSaveRequest = {
  active: boolean;
  activeUsersOnly: boolean;
  ccRecipients: string;
  corporateId: number;
  customerId: number;
  dayOfWeek: number;
  downloadType: DownloadType;
  frequency: ReportFrequency;
  fromDate: { year: number; month: number; day: number } | null;
  ftpFolder: string;
  ftpHost: string;
  ftpPassphrase: string;
  ftpPassword: string;
  ftpPort: number;
  ftpRemoteFileName: string;
  ftpUsername: string;
  prefixFileName: boolean;
  privateKey: string | null;
  reportId: number;
  reportName: string;
  reportType: ReportType;
  specialReport: boolean;
  targetExcel: boolean;
  toDate: { year: number; month: number; day: number } | null;
  transferProtocol: TransferProtocol;
  zipFile: boolean;
};

export namespace ReportType {
  const values = ["USERS", "ACTIVITY", "EXTENDED_USERS_EXPORT", "ACTIONABLES_ACTIVITY", "ONLINE_CONTENT_ACTIVITY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ReportType = ReportType.Impl;

export type ResendConfirmationEmailForm = {
  customerName: string | null;
  subscriptionId: number;
};

export type ResizeImage = {
  content: ReadonlyUint8Array;
};

export type Result = {
  completion: boolean;
  duration: string;
};

export type RevenueReportSearchForm = {
  isCompanyBilling: boolean;
  isEUCountries: boolean;
  quarter: number | null;
  year: number | null;
};

export type RevenueView = {
  country: string;
  currency: string;
  revenueData: { additionalProperties: Record<string, number> };
  total: number;
};

export type RevenueViewList = {
  revenueViews: RevenueView[];
};

export type RightsInputForm = {
  active: boolean;
  aiAllowed: boolean;
  audioReceivedDate: { year: number; month: number; day: number } | null;
  audioRequestedDate: { year: number; month: number; day: number } | null;
  autoRenew: boolean;
  contractExecuted: { year: number; month: number; day: number } | null;
  contractRequested: { year: number; month: number; day: number } | null;
  contractRequired: boolean;
  countriesExcluded: string[];
  countriesIncluded: string[];
  currencyCode: string | null;
  dataId: number;
  excludedActive: boolean;
  expirationDate: { year: number; month: number; day: number } | null;
  freeDownloadRights: FreeDownloadRights;
  inputRemarks: string | null;
  marketingActivityPermission: MarketingActivityPermission;
  msGraphAbstractAllowed: boolean;
  msGraphAbstractAllowedDate: { year: number; month: number; day: number } | null;
  msGraphReviewAllowed: boolean;
  negotiationsFailed: boolean;
  noAudioRights: boolean;
  noOnlineRights: boolean;
  noReview: boolean;
  noRightsRequired: boolean;
  paymentAdvised: { year: number; month: number; day: number } | null;
  paymentRequired: boolean;
  price: string | null;
  publisherAiAllowed: boolean;
  receivedDate: { year: number; month: number; day: number } | null;
  requestedDate: { year: number; month: number; day: number } | null;
  rightsFormatAudio: boolean;
  rightsFormatOffline: boolean;
  rightsFormatOnline: boolean;
  rightsFormatVideo: boolean;
  rightsProbability: RightsProbability;
  specialExpirationComment: string | null;
  specialExpirationDate: { year: number; month: number; day: number } | null;
  translationRightsAR: boolean;
  translationRightsAll: boolean;
  translationRightsCH: boolean;
  translationRightsDE: boolean;
  translationRightsEN: boolean;
  translationRightsES: boolean;
  translationRightsFR: boolean;
  translationRightsMA: boolean;
  translationRightsPT: boolean;
  translationRightsRU: boolean;
};

export type RightsInputToolProps = {
  dataId: bigint;
};

export namespace RightsProbability {
  const values = ["UNPREDICTABLE", "LIKELY", "VERY_LIKELY", "UNLIKELY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RightsProbability = RightsProbability.Impl;

export namespace RightsState {
  const values = ["RIGHTS_NOT_REQUESTED", "PENDING", "PENDING_PUBLISHER_APPROVAL", "OK", "NO_RIGHTS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RightsState = RightsState.Impl;

export namespace RoleResource {
  const values = [
    "ROLE_ADMIN",
    "PPS_EDITORIAL",
    "PPS_RIGHTS",
    "PPS_BOOKMARKETING",
    "PPS_WRITERREPORT",
    "PPS_STANDARDREPORT",
    "PPS_ALLSUMMARIES_LIST",
    "PPS_PUBLISHERADMIN",
    "PPS_INTERNALSEARCH",
    "ASP_ACCOUNT_ADMIN",
    "PUBLISHER_STATS_ALL",
    "ASP_STATISTICS",
    "ASP_USER_ADMIN",
    "PPS_RIGHTSADMINLIST",
    "ASP_STATISTICS_INTERNAL",
    "ADMIN_TOOL",
    "CUSTOMER_ADMIN",
    "EMAILTOOL",
    "TRANSACTION_ADMIN",
    "AFFILIATE_CLV_STATISTICS",
    "MARKETING_FILE_UPLOAD",
    "CURRENCY_EXCHANGE_RATE",
    "PPS_EDITORREPORT",
    "ADMIN_RELOAD",
    "ASP_MASSIMPORT_TOOL",
    "ASP_USER_ADMIN_LASTACCESS",
    "ASP_CENTRAL_ADMIN",
    "TXT_BUNDLE_EDIT",
    "TXT_TEXT_SINGLEEDIT",
    "TXT_TEXT_MULTIEDIT",
    "TXT_TOBEREVIEWEDLIST",
    "TXT_TOBETRANSLATEDLIST",
    "TXT_INLINE_EDIT",
    "TXT_LATEST_CHANGES",
    "TXT_SEARCH",
    "TXT_MAJOR_CHANGE",
    "TXT_ONLINE_STATE",
    "CREDIT_CARD_SETTLEMENT",
    "TXT_BUNDLE_VIEW",
    "EMAIL_LIST",
    "SOCIAL_COMMENT_FLAG",
    "CUSTOMER_WRITER",
    "RECOMMENDER_EXPORT",
    "KREC_EDIT",
    "WEB_RIGHTS_REQUEST",
    "SCORM_PACKAGE",
    "PPS_VIEW",
    "AFFILIATE_ADMIN",
    "SUMMARY_QR_CODE",
    "MARC_XML",
    "REGENERATE_SUMMARY_FILES",
    "HR_ADMIN_TOOL",
    "SUMMARY_STATISTICS",
    "CF_SUBSCRIPTIONS",
    "ASP_SSO_DOMAIN",
    "CUSTOMER_ADMIN_SUBSCRIPTION",
    "CUSTOMER_ADMIN_FINANCE",
    "OAUTH2_CLIENT_ADMIN",
    "ASP_USER_GROUP_ADMIN",
    "TOP_DOWNLOADS_TOOL",
    "WRITER_PORTAL",
    "ASP_LOGO_UPLOAD",
    "PORTLET_ADMIN",
    "PORTLET_SUPER_ADMIN",
    "FREEABSTRACT_ADMIN_TOOL",
    "PROMOBOXES_ADMIN",
    "CORPORATE_CONTACT",
    "CORPORATE_COMMENTS",
    "PPS_NEW_CONTENT_INPUT",
    "PPS_NEW_CONTENT_INPUT_ADMIN",
    "ABSENCES_ADMIN",
    "CHANNEL_ADMIN",
    "PROMOBOXES",
    "SSO_DOMAINS",
    "PPS_PORTAL",
    "PPS_PORTAL_ADMIN",
    "CHANNEL_LIST",
    "B2B_STATISTICS",
    "LIBRARY_CONNECTOR",
    "CHANNEL_ADMIN_GLOBAL",
    "PPS_FREELANCER_CONTACT_TOOL",
    "PPS_API_PRODUCTION_POINTS",
    "PPS_API_PRODUCTION_POINTS_ADMIN",
    "PPS_API_WRITING_OFFER",
    "PPS_API_EDITING_OFFER",
    "PPS_FLOW",
    "PPS_FLOW_ADMIN",
    "PPS_API_JOB",
    "PPS_API_INVOICE",
    "SCIM_TOKEN_VIEWER",
    "PPS_API_OFFER",
    "PRODUCTION_REPORT",
    "PPS_RIGHTS_FLOW",
    "PPS_AUTHOR_TOOL",
    "GA_KNOWLEDGE_BASE",
    "PPS_KNOWLEDGE_BASE",
    "EDITORIAL_KNOWLEDGE_BASE",
    "PPS_RIGHTS_TEAM_REPORTS",
    "PPS_EDITORIAL_TEAM_REPORTS",
    "PPS_API_SUMMARY_CREATION",
    "PROMOTION_CODE_TOOL",
    "PPS_GLOBAL_MOST_WANTED_SUMMARY_REPORT",
    "ASP_LIST",
    "PPS_USER_API",
    "ASP_SENSITIVE_CONTENT",
    "CHANNEL_ADMIN_SEARCH",
    "PPS_TEAM_MOST_WANTED_EDITORIAL_SUMMARY_REPORT",
    "PPS_PERSONAL_MOST_WANTED_EDITORIAL_SUMMARY_REPORT",
    "PPS_TEAM_MOST_WANTED_RIGHTS_SUMMARY_REPORT",
    "PPS_PERSONAL_MOST_WANTED_RIGHTS_SUMMARY_REPORT",
    "ACTIONABLE_ADMIN",
    "PPS_COMPONENT_DEMO",
    "ASP_MASSIMPORT_TOOL_EXTENDED",
    "ASP_UNIDENTIFIED_USER_ADMIN",
    "PPS_PAYMENT_TOOL",
    "HR_ADMIN_FULL_ACCESS",
    "PPS_ORIGINAL_SUMMARY_FILE_ACCESS",
    "CUSTOMER_REPORTS",
    "PPS_ARCHIVING_SUMMARY",
    "PPS_USER_BETA_TEST",
    "ACTIONABLE_ADMIN_SEARCH",
    "CUSTOM_PAGE_ADMIN",
    "PPS_EXPENSE_TOOL",
    "VERIFY_INPUT_TOOL",
    "VERIFY_PUBLISH",
    "VERIFY_UPLOAD",
    "VERIFY_DASHBOARD",
    "CSM_ADVANCED_SEARCH",
    "VERIFY_VERIFICATION",
    "VERIFY_EDITING",
    "SUMMARIES_BULK_LOAD",
    "ACTIONABLES_BULK_LOAD",
    "VERIFY_BULK_LOAD",
    "TAG_ADMIN",
    "EXPERTVIDEO_ADMIN",
    "MIAB_ADMIN",
    "AUTHOR_READ",
    "TAG_SUPER_ADMIN",
    "SKILL_BOOST_ADMIN",
    "TXT_READ_ONLY",
  ] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type RoleResource = RoleResource.Impl;

export type RoleResourceConstraint = {
  corporateIds: number[];
  customerId: number;
  languages: Language[];
  resource: RoleResource;
};

export type RoleResourceConstraintResponse = {
  body: RoleResourceConstraint[];
};

export namespace SamlBinding {
  const values = ["REDIRECT", "POST"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SamlBinding = SamlBinding.Impl;

export type SamlMetadataUriForm = {
  uri: string;
};

export namespace SamlNameIdFormat {
  const values = ["NONE", "UNSPECIFIED", "PERSISTENT", "EMAIL"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SamlNameIdFormat = SamlNameIdFormat.Impl;

export type SamlSettingsEditView = {
  certificates: AspCertificateView[];
  enforceAssertionSignature: boolean;
  enforceResponseSignature: boolean;
  futureTTL: number;
  idpEntityId: string | null;
  idpLogoutUrl: string | null;
  internetFacingIdp: boolean;
  nameIdPolicyFormat: SamlNameIdFormat;
  provisioning: boolean;
  samlBinding: SamlBinding;
  samlMetadataUri: string | null;
  scimOauthKeys: ScimOauthKeyListView[];
  signAuthRequest: SignAuthenticationRequest;
  signingKey: SigningKey;
  spEntityId: string;
  ssoAttributeEntry: SsoAttributeEntry;
  ssoDomains: string[];
  ssoIdentityProvider: string | null;
  ssoMobileAppSupport: boolean;
  urlIdentityProvider: string | null;
};

export type SamlSettingsForm = {
  certificateForms: CertificateForm[];
  enforceAssertionSignature: boolean;
  enforceResponseSignature: boolean;
  futureTTL: number;
  idpEntityId: string | null;
  idpLogoutUrl: string | null;
  internetFacingIdp: boolean;
  nameIdPolicyFormat: SamlNameIdFormat;
  provisioning: boolean;
  samlBinding: SamlBinding;
  samlMetadataUri: string | null;
  signAuthRequest: SignAuthenticationRequest;
  signingKey: SigningKey;
  spEntityId: string;
  ssoAttributeEntry: SsoAttributeEntry;
  ssoDomains: string[];
  ssoIdentityProvider: string | null;
  ssoMobileAppSupport: boolean;
  urlIdentityProvider: string | null;
};

export type SamlSettingsFormResult = { kind: "SUCCESS"; form: SamlSettingsEditView } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export type SaveRightsInputFormResponse = { kind: "SUCCESS"; data: RightsInputForm } | { kind: "VALIDATION_ERROR"; data: RightsInputForm; reasons: ValidationErrorReasons };

export type ScimOauthKeyListView = {
  baseUrl: string;
  clientId: string;
  token: string | null;
  type: ScimProvider;
};

export namespace ScimProvider {
  const values = ["OKTA", "AZURE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ScimProvider = ScimProvider.Impl;

export type ScoreForm = {
  reasoning: string;
  score: number | null;
};

export type ScoreUnparsedForm = {
  reasoning: string;
  score: string;
};

export type ScoreValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: ScoreForm };

export type ScoreWithReasoning = {
  reasoning: string;
  score: number;
};

export type SearchFiltersForm = {
  actionableLanguageFormFilter: ActionableFiltersForm;
  channelPage: number;
  query: string;
  summariesPage: number;
  summarySearchFilters: SummaryFiltersForm;
  verifaiFiltersForm: OnlineContentFiltersForm;
  verifaiPage: number;
};

export type SensitiveContentListViewList = {
  list: {
    active: boolean;
    coverUri: string;
    id: bigint;
    title: string;
  }[];
};

export namespace SensitiveContentType {
  const values = ["SUMMARY", "CHANNEL", "ACTIONABLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SensitiveContentType = SensitiveContentType.Impl;

export type ShareContent = { shareType: "CONTENT"; metadata: ContentMetadata } | { shareType: "CHANNEL"; channel: ChannelMetadata };

export type ShareEvent = {
  contentItemType: ContentItemType;
  id: string;
  shareMedium: ShareMedium;
};

export namespace ShareMedium {
  const values = ["COPY_LINK", "FACEBOOK", "FACEBOOK_WORKSPACE", "TWITTER", "LINKEDIN", "EMAIL", "IFRAME", "SCORM", "TEAMS", "WECHAT", "XING", "QRCODE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ShareMedium = ShareMedium.Impl;

export type ShareProps = {
  content: ShareContent;
  shareOptions: ShareMedium[];
};

export namespace SignAuthenticationRequest {
  const values = ["NO", "YES"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SignAuthenticationRequest = SignAuthenticationRequest.Impl;

export namespace SigningKey {
  const values = ["PRIMARY", "SECONDARY", "TERTIARY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SigningKey = SigningKey.Impl;

export type Skill = {
  following: "FOLLOWING" | "NOT_FOLLOWING" | "UNAVAILABLE";
  kind: "SKILL";
  metadata: ChannelMetadata;
  relatedSkills: ChannelMetadata[];
  shareOptions: ShareMedium[];
  tag: Tag;
  topResults: ContentMetadataView;
  tracking: SkillTracking;
};

export type SkillBoostAdminContentForm =
  | {
      kind: "INSIGHT";
      description: string;
      mcqCorrectAnswer: string;
      mcqFeedback: string;
      mcqOption1: string;
      mcqOption2: string;
      mcqOption3: string;
      mcqOption4: string | null;
      mcqQuestion: string;
      script: string;
      takeaway: string;
      title: string;
    }
  | { kind: "REFLECTION"; description: string; mcqFeedback: string; mcqOption1: string; mcqOption2: string; mcqOption3: string; mcqOption4: string | null; mcqQuestion: string; script: string; takeaway: string; title: string };

export type SkillBoostAdminContentUnparsedForm =
  | {
      kind: "INSIGHT";
      description: string;
      mcqCorrectAnswer: string;
      mcqFeedback: string;
      mcqOption1: string;
      mcqOption2: string;
      mcqOption3: string;
      mcqOption4: string;
      mcqQuestion: string;
      script: string;
      takeaway: string;
      title: string;
    }
  | { kind: "REFLECTION"; description: string; mcqFeedback: string; mcqOption1: string; mcqOption2: string; mcqOption3: string; mcqOption4: string; mcqQuestion: string; script: string; takeaway: string; title: string };

export type SkillBoostAdminContentValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: SkillBoostAdminContentForm };

export type SkillBoostAdminForm = {
  confirmScriptOverride: boolean;
  content: SkillBoostAdminContentForm;
  quickWinId: bigint;
};

export type SkillBoostAdminSearchForm = {
  corporateId: number | null;
  languages: Language[];
  query: string;
};

export type SkillBoostAdminSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: SkillBoostAdminForm };

export type SkillBoostAdminUnparsedForm = {
  confirmScriptOverride: boolean;
  content: SkillBoostAdminContentUnparsedForm;
  quickWinId: bigint;
};

export type SkillBoostAdminValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: SkillBoostAdminForm };

export type SkillBoostAdminView = {
  approved: boolean;
  approvedBy: number | null;
  audioScript: string | null;
  content: QuickWinContentData;
  dataId: number;
  description: string;
  quickWinId: bigint;
  quickWinType: QuickWinType;
  title: string;
};

export type SkillBoostAudioPreviewData = {
  audioScript: string;
  previewUrl: string;
};

export type SkillBoostAudioPreviewRequest = {
  script: string;
};

export type SkillBoostAudioPreviewResult = { kind: "GENERATION_ERROR" } | { kind: "SUCCESS"; data: SkillBoostAudioPreviewData };

export type SkillBoostAdminContentForm_INSIGHT = {
  description: string;
  kind: "INSIGHT";
  mcqCorrectAnswer: string;
  mcqFeedback: string;
  mcqOption1: string;
  mcqOption2: string;
  mcqOption3: string;
  mcqOption4: string | null;
  mcqQuestion: string;
  script: string;
  takeaway: string;
  title: string;
};

export type SkillBoostAdminContentUnparsedForm_INSIGHT = {
  description: string;
  kind: "INSIGHT";
  mcqCorrectAnswer: string;
  mcqFeedback: string;
  mcqOption1: string;
  mcqOption2: string;
  mcqOption3: string;
  mcqOption4: string;
  mcqQuestion: string;
  script: string;
  takeaway: string;
  title: string;
};

export type SkillBoostInsightAdminValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: SkillBoostAdminContentForm_INSIGHT };

export type SkillBoostListCustomPortletEditView = {
  portlet: PortletEditView;
  skillBoosts: ContentMetadata_QUICKWIN[];
};

export type SkillBoostListCustomPortletForm = {
  portletForm: PortletForm;
  skillBoostIds: bigint[];
};

export type SkillBoostAdminContentForm_REFLECTION = {
  description: string;
  kind: "REFLECTION";
  mcqFeedback: string;
  mcqOption1: string;
  mcqOption2: string;
  mcqOption3: string;
  mcqOption4: string | null;
  mcqQuestion: string;
  script: string;
  takeaway: string;
  title: string;
};

export type SkillBoostAdminContentUnparsedForm_REFLECTION = {
  description: string;
  kind: "REFLECTION";
  mcqFeedback: string;
  mcqOption1: string;
  mcqOption2: string;
  mcqOption3: string;
  mcqOption4: string;
  mcqQuestion: string;
  script: string;
  takeaway: string;
  title: string;
};

export type SkillBoostReflectionAdminValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: SkillBoostAdminContentForm_REFLECTION };

export type SkillChange = {
  competency: SkillOverviewItem | null;
  kind: "ADD" | "REMOVE";
  skill: SkillOverviewItem | null;
  skillGroup: SkillOverviewItem;
};

export type SkillChangeApply = {
  changes: SkillChange[];
  file: FormFile;
};

export type SkillChangeSet = {
  changes: SkillChange[];
  currentJson: string;
  errors: string[];
  upcomingJson: string;
  warnings: string[];
};

export type SkillGroup = {
  competencies: {
    item: SkillOverviewItem;
    skills: {
      item: SkillOverviewItem;
    }[];
  }[];
  item: SkillOverviewItem;
};

export type SkillGroupOption = {
  channelId: bigint;
  title: string;
};

export type SkillGroups = {
  skillGroups: {
    competencies: {
      item: {
        channelId: bigint;
        title: string;
        url: string;
      };
      skills: {
        item: {
          channelId: bigint;
          title: string;
          url: string;
        };
      }[];
    }[];
    item: {
      channelId: bigint;
      title: string;
      url: string;
    };
  }[];
};

export type SkillNavigation = {
  item: SkillNavigationItem;
  subItems: {
    item: SkillNavigationItem;
    subItems: {
      item: SkillNavigationItem;
    }[];
  }[];
};

export type SkillNavigationItem = {
  channelId: bigint;
  coverUrl: string;
  name: string;
  selected: boolean;
  tag: Tag;
  title: string;
  url: string;
};

export type SkillOverviewItem = {
  channelId: bigint;
  coverUri: string;
  tag: Tag;
  title: string;
  url: string;
};

export type SkillPageProps = {
  channel: ChannelMetadata;
  content: ContentMetadataView;
  following: bigint[];
  hasActionableFeature: boolean;
  navigation: SkillNavigation | null;
  relatedChannels: ChannelMetadata[];
  relatedTrackingParams: string;
  shareOptions: ShareMedium[];
  tag: Tag;
  tracking: {
    actionableParams: string;
    expertVideoParams: string;
    miabParams: string;
    summaryParams: string;
    verifaiParams: string;
  };
};

export type SkillPageQuery = {
  iframe: boolean;
  primaryChannelId: bigint | null;
};

export type SkillPromptAnswer = {
  primaryTag: string;
  secondaryTags: string[];
};

export type SkillProximityPromptAnswer = {
  primaryTag: ProximityScore;
  secondaryTags: ProximityScore[];
};

export type SkillRedirect = {
  kind: "REDIRECT";
  path: string;
};

export type SkillTracking = {
  actionableParams: string;
  expertVideoParams: string;
  miabParams: string;
  relatedTrackingParams: string;
  summaryParams: string;
  verifaiParams: string;
};

export type SourceEntity = {
  canonicalEntityName: string;
  keyPeople: string[];
  parentOrganization: string | null;
  sourceType: "news_media" | "technical_practitioner" | "academic" | "corporate_vendor" | "reference";
};

export type SourceRating = {
  expertiseAreas: string[];
  infoFound: boolean;
  keyConcerns: string[];
  keyStrengths: string[];
  primaryFocus: string | null;
  recommendation: "APPROVED" | "REVIEW_REQUIRED" | "REJECTED";
  scores: {
    expertise: {
      reasoning: string;
      score: number;
      summary: string;
    };
    independence: {
      reasoning: string;
      score: number;
      summary: string;
    };
    productionQuality: {
      reasoning: string;
      score: number;
      summary: string;
    };
    reputation: {
      reasoning: string;
      score: number;
      summary: string;
    };
  };
  sourceCategory: "educational" | "news" | "corporate" | "independent" | "unknown";
};

export type SourceRatingOutput = {
  expertiseAreas: string[];
  keyConcerns: string[];
  keyStrengths: string[];
  primaryFocus: string | null;
  recommendation: "APPROVED" | "REVIEW_REQUIRED" | "REJECTED";
  scores: {
    expertise: ScoreWithReasoning;
    independence: ScoreWithReasoning;
    productionQuality: ScoreWithReasoning;
    reputation: ScoreWithReasoning;
  };
  sourceCategory: "educational" | "news" | "corporate" | "independent" | "unknown";
  totalScore: number | null;
  trustworthinessRating: number;
};

export type SsoAttributeEntry = {
  country: string | null;
  email: string | null;
  extraField1: string | null;
  extraField2: string | null;
  extraField3: string | null;
  firstName: string | null;
  functionStr: string | null;
  lastName: string | null;
  password: string | null;
  title: string | null;
  town: string | null;
  uid: string | null;
};

export namespace SsoProtocol {
  const values = ["NONE", "SAML", "OAUTH2"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SsoProtocol = SsoProtocol.Impl;

export type SsoSettingsForm = {
  checkSso: boolean;
  ssoProtocol: SsoProtocol;
};

export type SsoSettingsFormResult = { kind: "SUCCESS" } | { kind: "VALIDATION_ERROR"; validationErrorReasons: ValidationErrorReasons };

export namespace StreakFrequency {
  const values = ["DAILY", "WEEKLY"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type StreakFrequency = StreakFrequency.Impl;

export type StripePaymentView = {
  livemode: boolean;
  stripeCustomerId: string;
  stripeInvoiceId: string | null;
  stripePaymentIntentId: string | null;
  stripeSourceId: string;
  stripeSubscriptionId: string;
};

export type StripePlaygroundEvent = {
  attempts: number | null;
  billingEventId: number | null;
  createdAt: DateTime<true>;
  externalId: string;
  lastError: string | null;
  objectExternalId: string | null;
  processedAt: DateTime<true> | null;
  receivedAt: DateTime<true> | null;
  status: string | null;
  storedJson: string | null;
  stripeJson: string;
  type: string;
};

export type StripePlaygroundEventGroup = {
  customerExternalId: string | null;
  events: StripePlaygroundEvent[];
  storedAccessUntil: DateTime<true> | null;
  storedCustomerId: number | null;
  storedState: string | null;
  subscriptionExternalId: string | null;
};

export type StripePlaygroundEventOutcome = { kind: "ERROR"; message: string } | { kind: "NOT_CLAIMABLE" } | { kind: "FAILED"; error: string } | { kind: "PROCESSED"; changes: string[] };

export type StripePlaygroundEventsResult = { kind: "ERROR"; message: string } | { kind: "SUCCESS"; groups: StripePlaygroundEventGroup[]; sandbox: boolean };

export type StripePlaygroundFailure = {
  billingEventIds: number[];
  error: string;
};

export type StripePlaygroundRunRequest = {
  limit: number;
};

export type StripePlaygroundRunResult = { kind: "ERROR"; message: string } | { kind: "SUCCESS"; failed: StripePlaygroundFailure[]; processed: number; pullError: string | null; pulled: number; unprocessed: number };

export type StripePlaygroundSyncResult = { kind: "ERROR"; message: string } | { kind: "NOT_FOUND"; purchaseReference: string | null } | { kind: "SYNCED"; accessUntil: DateTime<true>; changes: string[]; state: string };

export namespace StudentIdVerificationProvider {
  const values = ["UNIDAYS", "UNIDAYS_PERK", "SHEERID", "MANUAL", "UNKNOWN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type StudentIdVerificationProvider = StudentIdVerificationProvider.Impl;

export namespace StudentVerificationStatus {
  const values = ["VERIFIED", "EXPIRED", "FAILED", "IN_PROGRESS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type StudentVerificationStatus = StudentVerificationStatus.Impl;

export type StudentVerificationView = {
  countryOfStudy: string;
  createdAt: DateTime<true>;
  fieldOfStudy: string | null;
  proof: string | null;
  provider: StudentIdVerificationProvider;
  status: StudentVerificationStatus;
  subscriptionId: number | null;
  verifiedAt: DateTime<true> | null;
};

export type StudioCustomerDetail = {
  corporation: {
    corporateId: number;
    corporation: string;
    features: string[];
    username: string;
  };
  customer: {
    admin: boolean;
    customerId: number;
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type SubNavigationGroup = {
  groupTitle: string;
  links: NavigationLinkEntry[];
};

export type SubscribeCheckoutCompletion = {
  loggedIn: boolean;
  productName: string;
};

export type SubscribeCheckoutForm = {
  checkoutUrl: string;
  company: string | null;
  companyBilling: boolean;
  email: string;
  gRecaptchaResponse: string;
  password: string | null;
  productId: bigint;
  promotionCode: string;
  termsAndConditions: boolean;
};

export type SubscribeCheckoutInitResult = { kind: "NOT_AVAILABLE_ERROR" } | { kind: "EXISTING_SUBSCRIPTION_ERROR" } | { kind: "SUCCESS"; data: SubscribeCheckoutOffer };

export type SubscribeCheckoutOffer = {
  email: string | null;
  passwordRequired: boolean;
  product: MyProductInformation;
  productId: number;
};

export type SubscribeCheckoutStatusResult = { kind: "UNKNOWN_SESSION_ERROR" } | { kind: "EXPIRED_ERROR" } | { kind: "PENDING" } | { kind: "SUCCESS"; data: SubscribeCheckoutCompletion };

export type SubscribeCheckoutSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: SubscribeCheckoutForm };

export type SubscribeCheckoutUnparsedForm = {
  checkoutUrl: string;
  company: string;
  companyBilling: boolean;
  email: string;
  gRecaptchaResponse: string;
  password: string;
  productId: bigint;
  promotionCode: string;
  termsAndConditions: boolean;
};

export type SubscribeCheckoutValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: SubscribeCheckoutForm };

export type SubscriptionAdminForm = {
  extraMonths: number | null;
  isPaid: boolean;
  productId: number;
  reference: string | null;
  showCommentModal: boolean;
  status: SubscriptionStatus;
  studentVerificationCountryOfStudy: string | null;
  studentVerificationFieldOfStudy: string | null;
  studentVerificationProof: string | null;
  studentVerificationProvider: string | null;
  studentVerificationStatus: string | null;
  studentVerificationVerifiedAt: DateTime<true> | null;
  subscriptionEndDate: { year: number; month: number; day: number };
  subscriptionId: number;
  subscriptionStartDate: { year: number; month: number; day: number };
  transactions: SubscriptionTransaction[];
};

export type SubscriptionAdminView = {
  affiliate: string | null;
  cancelLinkForCustomerUri: string;
  comment: string | null;
  customerId: number;
  emailAddressForCancelConfirmation: string;
  extraMonths: number | null;
  giftCertificateUri: string | null;
  giftCodesAvailable: GiftCodeStatus;
  invoicesAndReminders: Invoice[] | null;
  isGiftSubscription: boolean;
  isPaid: boolean;
  motivationToBuy: string | null;
  productId: number;
  productName: string;
  promotionCode: string | null;
  reference: string;
  referredBy: string | null;
  renewSource: SubscriptionRenewSource;
  status: SubscriptionStatus;
  studentVerification: StudentVerificationView | null;
  subscriptionEndDate: { year: number; month: number; day: number };
  subscriptionId: number;
  subscriptionRenewInfo: SubscriptionRenewInfoView | null;
  subscriptionStartDate: { year: number; month: number; day: number };
  transactions: SubscriptionTransaction[];
};

export type SubscriptionCreditForm = {
  amountToCredit: number;
  balance: number;
  comment: string | null;
  sendEmail: boolean;
  stopSubscription: boolean;
  subscriptionId: number;
  transactionTypeId: number;
};

export type SubscriptionCreditView = {
  amountToCredit: number;
  balance: number;
  currencyCode: string | null;
  customerId: number;
  emailSubject: string | null;
  emailText: string | null;
  isEmailHtml: boolean | null;
  isGiftSubscription: boolean;
  isSubscriptionStopped: boolean;
  subscriptionId: number;
  transaction: SubscriptionTransaction;
};

export type SubscriptionPayment = {
  balance: MonetaryAmount;
  isMobilePayment: boolean;
  paymentType: string | null;
};

export type SubscriptionRenewInfoView = {
  ccNumber: string | null;
  ccType: CreditCardType | null;
  cvv: string | null;
  expMonth: string | null;
  expYear: string | null;
  lsvAccountNumber: string | null;
  lsvBankIban: string | null;
  lsvBankName: string | null;
  lsvBankTown: string | null;
  lsvClearingNumber: string | null;
  lsvFirstName: string | null;
  lsvLastName: string | null;
  manualPrice: number | null;
  paymentType: string | null;
  productId: number | null;
  promotionCode: string | null;
  street: string | null;
  subscriptionId: number;
  zip: string | null;
};

export namespace SubscriptionRenewSource {
  const values = ["NEW", "AUTOMATIC", "MANUAL", "UPGRADE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SubscriptionRenewSource = SubscriptionRenewSource.Impl;

export namespace SubscriptionStatus {
  const values = ["ACTIVE", "STOP", "CANCEL", "RENEWED", "RENEWALFAILED", "PASTDUE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SubscriptionStatus = SubscriptionStatus.Impl;

export type SubscriptionTransaction = {
  amount: DisplayMonetaryAmount;
  comment: string | null;
  customerId: number;
  generatedAt: { year: number; month: number; day: number };
  paymentInfoAlipay: AlipayPaymentView | null;
  paymentInfoAndroid: AndroidPaymentView | null;
  paymentInfoCC: CreditCardPaymentView | null;
  paymentInfoIOS: IosPaymentView | null;
  paymentInfoInvoice: InvoicePaymentView | null;
  paymentInfoLSV: LsvPaymentView | null;
  paymentInfoMiles: MilesPaymentView | null;
  paymentInfoPaypal: PayPalPaymentView | null;
  paymentInfoStripe: StripePaymentView | null;
  paymentReceiptUri: string;
  paymentType: PaymentType;
  quantity: number;
  subscriptionId: number;
  transactionId: number;
  transactionType: "DEBIT" | "CREDIT";
};

export type SuccessFactorsConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  companyId: string;
  connectorType: "SUCCESS_FACTORS";
  locales: SuccessFactorsLocale[];
  userId: string;
  userIdentifier: ConnectorUserIdentifier;
};

export namespace SuccessFactorsLocale {
  const values = ["pt_BR", "zh", "zh_HK", "zh_TW", "en", "en_CA", "en_GB", "fr", "fr_CA", "de", "pt", "ru", "es", "es_MX"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SuccessFactorsLocale = SuccessFactorsLocale.Impl;

export type SuggestedAnswerView = {
  actionableId: bigint;
  description: string;
  peerInsightQuestionId: bigint;
  suggestedAnswerId: bigint | null;
  title: string;
};

export type SumTotalConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "SUM_TOTAL";
  oauthTokenUri: string;
  userIdentifier: ConnectorUserIdentifier;
  xapiActorId: XApiActorId;
};

export type SummaryActivityReportView = {
  customer: CustomerReportView | null;
  downloadCountry: string | null;
  downloadFormat: string | null;
  primarySkills: string[];
  skills: string[];
  summary: ContentMetadata_SUMMARY;
  timeStamp: DateTime<true>;
};

export type SummaryAddCommentRequest = {
  comment: string;
  dataId: number;
  parentCommentId: number | null;
};

export type SummaryBulkLoadForm = {
  corporateId: number | null;
  dataIds: number[];
  includeInactive: boolean;
};

export type SummaryCardDemoProps = {
  summaryList: ContentMetadata_SUMMARY[];
};

export type SummaryComment = {
  authorAvatar: string;
  commentText: string;
  commentatorName: string;
  createdAt: DateTime<true>;
  parentCommentId: number | null;
  summaryCommentId: number;
};

export type SummaryContent =
  | {
      summaryType: "DEFAULT";
      aboutAuthorHtml: string;
      aboutTextHtml: string | null;
      abstractTextHtml: string;
      dataId: number;
      introHtml: string;
      keyInsights: string[];
      language: Language;
      outputFormat: SummaryOutputFormat;
      source: SummarySource;
      sourceType: SummarySourceType;
      takeaways: string[];
    }
  | {
      summaryType: "CLASSIC";
      aboutAuthorHtml: string;
      aboutTextHtml: string | null;
      abstractTextHtml: string;
      dataId: number;
      genre: string;
      historicalBackgroundHtml: string;
      introHtml: string;
      language: Language;
      outputFormat: SummaryOutputFormat;
      period: string;
      source: SummarySource;
      sourceType: SummarySourceType;
      takeaways: string[];
    }
  | {
      summaryType: "REVIEW";
      dataId: number;
      language: Language;
      longReviewHtml: string | null;
      outputFormat: SummaryOutputFormat;
      reviewLeadHtml: string;
      reviewTitle: string;
      source: SummarySource;
      sourceType: SummarySourceType;
      writer: Writer | null;
    }
  | { summaryType: "UPCOMING"; aboutAuthorHtml: string; dataId: number; language: Language; outputFormat: SummaryOutputFormat; source: SummarySource; sourceType: SummarySourceType };

export type SummaryContent_CLASSIC = {
  aboutAuthorHtml: string;
  aboutTextHtml: string | null;
  abstractTextHtml: string;
  dataId: number;
  genre: string;
  historicalBackgroundHtml: string;
  introHtml: string;
  language: Language;
  outputFormat: SummaryOutputFormat;
  period: string;
  source: SummarySource;
  sourceType: SummarySourceType;
  summaryType: "CLASSIC";
  takeaways: string[];
};

export type SummaryContent_DEFAULT = {
  aboutAuthorHtml: string;
  aboutTextHtml: string | null;
  abstractTextHtml: string;
  dataId: number;
  introHtml: string;
  keyInsights: string[];
  language: Language;
  outputFormat: SummaryOutputFormat;
  source: SummarySource;
  sourceType: SummarySourceType;
  summaryType: "DEFAULT";
  takeaways: string[];
};

export type SummaryContentLegacy = {
  aboutAuthorHtml: string | null;
  aboutTextHtml: string | null;
  abstractTextHtml: string | null;
  bookId: number;
  dataId: number;
  historicalBackgroundHtml: string | null;
  introHtml: string | null;
  keyInsights: string[];
  language: Language;
  longReviewHtml: string | null;
  outputFormat: SummaryOutputFormat;
  reviewLeadHtml: string | null;
  reviewTitle: string | null;
  sourceIsbn: string | null;
  sourcePages: number;
  sourceUrl: string | null;
  takeaways: string[];
};

export type SummaryContent_REVIEW = {
  dataId: number;
  language: Language;
  longReviewHtml: string | null;
  outputFormat: SummaryOutputFormat;
  reviewLeadHtml: string;
  reviewTitle: string;
  source: SummarySource;
  sourceType: SummarySourceType;
  summaryType: "REVIEW";
  writer: Writer | null;
};

export type SummaryContent_UPCOMING = {
  aboutAuthorHtml: string;
  dataId: number;
  language: Language;
  outputFormat: SummaryOutputFormat;
  source: SummarySource;
  sourceType: SummarySourceType;
  summaryType: "UPCOMING";
};

export type SummaryCoverInformation = {
  imageUri: string;
  summarySourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  title: string | null;
};

export namespace SummaryDocumentType {
  const values = ["ABSTRACT", "REVIEW"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryDocumentType = SummaryDocumentType.Impl;

export namespace SummaryDownloadFormat {
  const values = ["PDF", "AUDIO", "KINDLE", "EPUB", "MOBILE_APP", "WEB"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryDownloadFormat = SummaryDownloadFormat.Impl;

export type SummaryExportFormat = {
  description: string;
  format: SummaryExportFormatType;
  link: string;
  restrictionReason: SummaryRestrictionReason;
  title: string;
};

export namespace SummaryExportFormatType {
  const values = ["PDF", "EPUB", "AUDIO", "KINDLE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryExportFormatType = SummaryExportFormatType.Impl;

export type SummaryFiltersForm = {
  audioFormFilter: boolean;
  languageFormFilter: Language[];
  miabFormFilter: boolean;
  publicationDateFormFilter: number[];
  qualityFormFilter: string[];
  ratingFormFilter: number[];
  sourceFormFilter: string[];
};

export type ContentId_SUMMARY = {
  contentType: "SUMMARY";
  id: bigint;
};

export type SummaryIdsList = {
  summaryIds: number[];
};

export type SummaryLanguageEdition = {
  language: Language;
  link: string;
};

export namespace SummaryLength {
  const values = ["SHORT", "LONG", "EXTRA_LONG"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryLength = SummaryLength.Impl;

export type SummaryListCustomPortletEditView = {
  channels: ChannelMetadata[];
  portlet: PortletEditView;
  summaries: ContentMetadata_SUMMARY[];
};

export type SummaryListCustomPortletForm = {
  channelIds: bigint[];
  portletForm: PortletForm;
  summaryIds: number[];
};

export type SummaryListPortletEditView = {
  listType: ReadingListType;
  portlet: PortletEditView;
};

export type SummaryListPortletForm = {
  listType: ReadingListType;
  portletForm: PortletForm;
};

export type SummaryListPortletView = {
  items: ContentMetadata_SUMMARY[];
  metadata: PortletMetadata;
  pageInfo: PageInfo;
  viewAllUri: string | null;
};

export type SummaryListView = {
  authors: string;
  dataId: number;
  language: Language;
  source: string;
  title: string;
};

export type ContentMetadata_SUMMARY = {
  activatedAt: DateTime<true> | null;
  activationTime: DateTime<true> | null;
  active: boolean;
  audioActivatedAt: DateTime<true> | null;
  audioActive: boolean;
  audioDuration: number;
  authorInfo: string | null;
  authors: AuthorListView[];
  backgroundColor: string | null;
  bestseller: boolean;
  businessClassic: boolean;
  canonicalName: string;
  common: ContentMetadataCommon;
  contentId: ContentId_SUMMARY;
  contentItemId: bigint;
  contentItemType: ContentItemType;
  contentType: "SUMMARY";
  countriesExcluded: Country[];
  countriesIncluded: Country[];
  coverModifiedAt: DateTime<true> | null;
  coverUri: string;
  dataId: number;
  descriptionHtml: string | null;
  documentType: SummaryDocumentType;
  duration: number;
  imageBorder: boolean;
  language: Language;
  miabId: bigint | null;
  modifiedAt: DateTime<true> | null;
  originalTitle: string | null;
  previewAvailable: boolean;
  publicationDate: string | null;
  publisherInfo: string | null;
  publisherName: string | null;
  qualities: KeyQuality[];
  rating: number | null;
  sourceType: SummarySourceType;
  subtitle: string | null;
  summaryPages: number;
  summaryWords: number;
  teaser: string | null;
  title: string | null;
};

export type SummaryMetadataArray = {
  items: ContentMetadata_SUMMARY[];
};

export namespace SummaryOutputFormat {
  const values = ["TWO_PAGES", "FIVE_PAGES", "EIGHT_PAGES"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryOutputFormat = SummaryOutputFormat.Impl;

export type SummaryOverview = {
  audioState: RightsState;
  authors: string[];
  dataId: number;
  enteredAt: { year: number; month: number; day: number } | null;
  language: Language;
  numberOfPages: number | null;
  outputFormat: SummaryOutputFormat;
  publisher: string | null;
  rightsProbability: RightsProbability;
  rightsState: RightsState;
  sourceType: "BOOK" | "VIDEO" | "ARTICLE" | "CHAPTER" | "REPORT" | "PODCAST";
  sourceUrl: string | null;
  subtitle: string | null;
  summaryCoverInformation: SummaryCoverInformation;
  title: string | null;
};

export namespace SummaryPageActionType {
  const values = ["LISTEN", "MIAB", "BOOKMARK", "MYLIST", "COMMENT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryPageActionType = SummaryPageActionType.Impl;

export type SummaryPageClassic = {
  aboutAuthorTitle: string;
  actionTypes: SummaryPageActionType[];
  annotations: AnnotationView[];
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  comments: SummaryComment[];
  content: SummaryContent_CLASSIC;
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  firstEditionText: string;
  genre: string;
  metadata: ContentMetadata_SUMMARY;
  otherLanguageEditions: SummaryLanguageEdition[];
  period: string;
  publisher: SummaryPagePublisherInfo;
  reader: boolean;
  regionalAvailability: string;
  relatedSummaries: ContentMetadata_SUMMARY[];
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "CLASSIC";
};

export type SummaryPageClassicRestricted = {
  aboutAuthorHtml: string | null;
  aboutAuthorTitle: string;
  annotations: AnnotationView[];
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  comments: SummaryComment[];
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  firstEditionText: string;
  genre: string;
  metadata: ContentMetadata_SUMMARY;
  otherLanguageEditions: SummaryLanguageEdition[];
  period: string;
  publisher: SummaryPagePublisherInfo;
  relatedSummaries: ContentMetadata_SUMMARY[];
  restrictionMessage: string | null;
  restrictionReason: SummaryRestrictionReason;
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "CLASSIC_RESTRICTED";
};

export type SummaryPageCustomerProps = {
  avatarUrl: string;
  kindleEmailAddress: string | null;
};

export type SummaryPageDeactivated = {
  breadCrumbsPath: BreadCrumbItem[];
  customerProps: SummaryPageCustomerProps;
  metadata: ContentMetadata_SUMMARY;
  relatedSummaries: ContentMetadata_SUMMARY[];
  summaryType: "DEACTIVATED";
};

export type SummaryPageDefault = {
  aboutAuthorTitle: string;
  actionTypes: SummaryPageActionType[];
  annotations: AnnotationView[];
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  comments: SummaryComment[];
  content: SummaryContent_DEFAULT;
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  keySkills: string[];
  metadata: ContentMetadata_SUMMARY;
  otherLanguageEditions: SummaryLanguageEdition[];
  publisher: SummaryPagePublisherInfo;
  qualities: string[];
  reader: boolean;
  regionalAvailability: string;
  relatedSummaries: ContentMetadata_SUMMARY[];
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "DEFAULT";
};

export type SummaryPageDefaultRestricted = {
  aboutAuthorHtml: string | null;
  aboutAuthorTitle: string;
  annotations: AnnotationView[];
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  comments: SummaryComment[];
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  keyInsights: string[];
  keySkills: string[];
  metadata: ContentMetadata_SUMMARY;
  otherLanguageEditions: SummaryLanguageEdition[];
  publisher: SummaryPagePublisherInfo;
  qualities: string[];
  regionalAvailability: string;
  relatedSummaries: ContentMetadata_SUMMARY[];
  restrictionMessage: string | null;
  restrictionReason: SummaryRestrictionReason;
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "DEFAULT_RESTRICTED";
};

export type SummaryPageEditionDetails = {
  duration: string | null;
  isbn: string | null;
  numberOfPages: number;
  specialCopyright: string | null;
};

export type SummaryPageProps =
  | {
      summaryType: "DEFAULT";
      aboutAuthorTitle: string;
      actionTypes: SummaryPageActionType[];
      annotations: AnnotationView[];
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      comments: SummaryComment[];
      content: SummaryContent_DEFAULT;
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      keySkills: string[];
      metadata: ContentMetadata_SUMMARY;
      otherLanguageEditions: SummaryLanguageEdition[];
      publisher: SummaryPagePublisherInfo;
      qualities: string[];
      reader: boolean;
      regionalAvailability: string;
      relatedSummaries: ContentMetadata_SUMMARY[];
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    }
  | {
      summaryType: "REVIEW";
      aboutAuthorTitle: string;
      actionTypes: SummaryPageActionType[];
      annotations: AnnotationView[];
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      comments: SummaryComment[];
      content: SummaryContent_REVIEW;
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      keySkills: string[];
      metadata: ContentMetadata_SUMMARY;
      publisher: SummaryPagePublisherInfo;
      regionalAvailability: string;
      relatedSummaries: ContentMetadata_SUMMARY[];
      reviewInfoText: string;
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    }
  | {
      summaryType: "UPCOMING";
      aboutAuthorTitle: string;
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      content: SummaryContent_UPCOMING;
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      metadata: ContentMetadata_SUMMARY;
      publisher: SummaryPagePublisherInfo;
      qualities: string[];
      relatedSummaries: ContentMetadata_SUMMARY[];
      willBeNotified: boolean;
    }
  | { summaryType: "DEACTIVATED"; breadCrumbsPath: BreadCrumbItem[]; customerProps: SummaryPageCustomerProps; metadata: ContentMetadata_SUMMARY; relatedSummaries: ContentMetadata_SUMMARY[] }
  | {
      summaryType: "CLASSIC";
      aboutAuthorTitle: string;
      actionTypes: SummaryPageActionType[];
      annotations: AnnotationView[];
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      comments: SummaryComment[];
      content: SummaryContent_CLASSIC;
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      firstEditionText: string;
      genre: string;
      metadata: ContentMetadata_SUMMARY;
      otherLanguageEditions: SummaryLanguageEdition[];
      period: string;
      publisher: SummaryPagePublisherInfo;
      reader: boolean;
      regionalAvailability: string;
      relatedSummaries: ContentMetadata_SUMMARY[];
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    }
  | {
      summaryType: "REVIEW_RESTRICTED";
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      content: SummaryContent_REVIEW;
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      keySkills: string[];
      metadata: ContentMetadata_SUMMARY;
      publisher: SummaryPagePublisherInfo;
      relatedSummaries: ContentMetadata_SUMMARY[];
      restrictionMessage: string | null;
      restrictionReason: SummaryRestrictionReason;
      reviewContentTeaser: string;
      reviewInfoText: string;
      reviewTitle: string;
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    }
  | {
      summaryType: "DEFAULT_RESTRICTED";
      aboutAuthorHtml: string | null;
      aboutAuthorTitle: string;
      annotations: AnnotationView[];
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      comments: SummaryComment[];
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      keyInsights: string[];
      keySkills: string[];
      metadata: ContentMetadata_SUMMARY;
      otherLanguageEditions: SummaryLanguageEdition[];
      publisher: SummaryPagePublisherInfo;
      qualities: string[];
      regionalAvailability: string;
      relatedSummaries: ContentMetadata_SUMMARY[];
      restrictionMessage: string | null;
      restrictionReason: SummaryRestrictionReason;
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    }
  | {
      summaryType: "CLASSIC_RESTRICTED";
      aboutAuthorHtml: string | null;
      aboutAuthorTitle: string;
      annotations: AnnotationView[];
      breadCrumbsPath: BreadCrumbItem[];
      buyBookProps: BuyBookProps;
      comments: SummaryComment[];
      customerProps: SummaryPageCustomerProps;
      editionDetails: SummaryPageEditionDetails | null;
      firstEditionText: string;
      genre: string;
      metadata: ContentMetadata_SUMMARY;
      otherLanguageEditions: SummaryLanguageEdition[];
      period: string;
      publisher: SummaryPagePublisherInfo;
      relatedSummaries: ContentMetadata_SUMMARY[];
      restrictionMessage: string | null;
      restrictionReason: SummaryRestrictionReason;
      shareMediums: ShareMedium[];
      shareUrl: string;
      summaryExportFormats: SummaryExportFormat[];
      summaryPageStats: SummaryPageStats;
    };

export type SummaryPagePublisherInfo = {
  id: number;
  name: string;
  year: string;
};

export type SummaryPageReview = {
  aboutAuthorTitle: string;
  actionTypes: SummaryPageActionType[];
  annotations: AnnotationView[];
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  comments: SummaryComment[];
  content: SummaryContent_REVIEW;
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  keySkills: string[];
  metadata: ContentMetadata_SUMMARY;
  publisher: SummaryPagePublisherInfo;
  regionalAvailability: string;
  relatedSummaries: ContentMetadata_SUMMARY[];
  reviewInfoText: string;
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "REVIEW";
};

export type SummaryPageReviewRestricted = {
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  content: SummaryContent_REVIEW;
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  keySkills: string[];
  metadata: ContentMetadata_SUMMARY;
  publisher: SummaryPagePublisherInfo;
  relatedSummaries: ContentMetadata_SUMMARY[];
  restrictionMessage: string | null;
  restrictionReason: SummaryRestrictionReason;
  reviewContentTeaser: string;
  reviewInfoText: string;
  reviewTitle: string;
  shareMediums: ShareMedium[];
  shareUrl: string;
  summaryExportFormats: SummaryExportFormat[];
  summaryPageStats: SummaryPageStats;
  summaryType: "REVIEW_RESTRICTED";
};

export type SummaryPageStats = {
  amountComments: number | null;
  amountCompletions: number | null;
};

export type SummaryPageUpcoming = {
  aboutAuthorTitle: string;
  breadCrumbsPath: BreadCrumbItem[];
  buyBookProps: BuyBookProps;
  content: SummaryContent_UPCOMING;
  customerProps: SummaryPageCustomerProps;
  editionDetails: SummaryPageEditionDetails | null;
  metadata: ContentMetadata_SUMMARY;
  publisher: SummaryPagePublisherInfo;
  qualities: string[];
  relatedSummaries: ContentMetadata_SUMMARY[];
  summaryType: "UPCOMING";
  willBeNotified: boolean;
};

export type SummaryPrepareStepAiResponse = {
  point1: string;
  point2: string;
  point3: string;
  point4: string;
};

export type SummaryQrCodeRequest = {
  dataId: bigint;
};

export type SummaryQrCodeResponse = {
  base64: string;
};

export namespace SummaryRestrictionReason {
  const values = ["NONE", "UPGRADE_REQUIRED", "INVALID_B2B_PRODUCT", "REGION_RIGHTS", "UNPAID_INVOICE", "LOGIN_REQUIRED", "DOWNLOAD_BOT"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummaryRestrictionReason = SummaryRestrictionReason.Impl;

export type SummarySearchResultListView = {
  active: boolean | null;
  buyBookUrlObject: BuyBookUrlObject | null;
  dataId: number;
  deactivated: boolean | null;
  internalPriority: string | null;
  language: Language;
  publisher: string;
  title: string;
};

export type SummarySearchResultListViewList = {
  items: SummarySearchResultListView[];
};

export namespace SummarySort {
  const values = ["ACTIVATED_AT", "POPULARITY", "TITLE", "PUBLICATION_DATE", "RATING", "RELEVANCE"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummarySort = SummarySort.Impl;

export type SummarySource =
  | { sourceType: "BOOK"; duration: number; pages: number; sourceIsbn: string | null }
  | { sourceType: "VIDEO"; duration: number }
  | { sourceType: "ARTICLE"; duration: number; sectionPages: number }
  | { sourceType: "CHAPTER"; duration: number; sectionPages: number; sourceIsbn: string | null }
  | { sourceType: "REPORT"; duration: number; pages: number }
  | { sourceType: "PODCAST"; duration: number };

export type SummarySource_ARTICLE = {
  duration: number;
  sectionPages: number;
  sourceType: "ARTICLE";
};

export type SummarySource_BOOK = {
  duration: number;
  pages: number;
  sourceIsbn: string | null;
  sourceType: "BOOK";
};

export type SummarySource_CHAPTER = {
  duration: number;
  sectionPages: number;
  sourceIsbn: string | null;
  sourceType: "CHAPTER";
};

export type SummarySource_PODCAST = {
  duration: number;
  sourceType: "PODCAST";
};

export type SummarySource_REPORT = {
  duration: number;
  pages: number;
  sourceType: "REPORT";
};

export namespace SummarySourceType {
  const values = ["BOOK", "VIDEO", "ARTICLE", "CHAPTER", "REPORT", "PODCAST"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type SummarySourceType = SummarySourceType.Impl;

export type SummarySource_VIDEO = {
  duration: number;
  sourceType: "VIDEO";
};

export type SupadataAsyncJob = {
  jobId: string;
};

export type SupadataDirectResponse = {
  content: SupadataTranscriptChunk[];
  lang: Language;
};

export type SupadataJobStatusResponse = {
  content: SupadataTranscriptChunk[];
  lang: string;
  status: string;
};

export type SupadataTranscriptChunk = {
  duration: number | null;
  offset: number | null;
  text: string;
};

export type Tag =
  | { discriminator: "dynamicString"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicString; value: string }
  | { discriminator: "dynamicBoolean"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicBoolean; value: boolean }
  | { discriminator: "dynamicNumber"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicNumber; value: number }
  | { discriminator: "dynamicVoid"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindDynamicVoid }
  | { discriminator: "channel"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindChannel; value: string }
  | { discriminator: "primary-skill"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindPrimarySkill; value: string }
  | { discriminator: "skill"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindSkill; value: string }
  | { discriminator: "key-quality"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindKeyQuality; value: string }
  | { discriminator: "classification"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindClassification; value: string }
  | { discriminator: "parental-setting"; exclude: boolean; score: number; source: TagSourceType; tagKind: TagKindParentalSetting; value: ParentalSettingType };

export type TagAdminFilter = {
  query: string;
  tagKinds: TagKind[];
};

export type TagKind =
  | { discriminator: "classification"; id: bigint; kind: KindClassification }
  | { discriminator: "channel"; id: bigint; kind: KindChannel }
  | { discriminator: "primary-skill"; id: bigint; kind: KindPrimarySkill }
  | { discriminator: "skill"; id: bigint; kind: KindSkill }
  | { discriminator: "key-quality"; id: bigint; kind: KindKeyQuality }
  | { discriminator: "dynamicVoid"; id: bigint; kind: string }
  | { discriminator: "dynamicString"; id: bigint; kind: string }
  | { discriminator: "dynamicBoolean"; id: bigint; kind: string }
  | { discriminator: "dynamicNumber"; id: bigint; kind: string }
  | { discriminator: "parental-setting"; id: bigint; kind: KindParentalSetting };

export type TagKindChannel = {
  discriminator: KindChannel;
  id: bigint;
  kind: KindChannel;
};

export type TagKindClassification = {
  discriminator: KindClassification;
  id: bigint;
  kind: KindClassification;
};

export type TagKindDynamicBoolean = {
  discriminator: KindDynamicBoolean;
  id: bigint;
  kind: string;
};

export type TagKindDynamicNumber = {
  discriminator: KindDynamicNumber;
  id: bigint;
  kind: string;
};

export type TagKindDynamicString = {
  discriminator: KindDynamicString;
  id: bigint;
  kind: string;
};

export type TagKindDynamicVoid = {
  discriminator: KindDynamicVoid;
  id: bigint;
  kind: string;
};

export type TagKindKeyQuality = {
  discriminator: KindKeyQuality;
  id: bigint;
  kind: KindKeyQuality;
};

export type TagKindParentalSetting = {
  discriminator: KindParentalSetting;
  id: bigint;
  kind: KindParentalSetting;
};

export type TagKindPrimarySkill = {
  discriminator: KindPrimarySkill;
  id: bigint;
  kind: KindPrimarySkill;
};

export type TagKindSkill = {
  discriminator: KindSkill;
  id: bigint;
  kind: KindSkill;
};

export namespace TagSourceType {
  const values = ["USER", "AI", "VECTOR"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type TagSourceType = TagSourceType.Impl;

export type TagValidationResponse = { status: "success" } | { status: "error"; message: string };

export type TaggedItem = {
  item: ReferencedLearningItem;
  tag: Tag;
};

export type TakeawaysAiResponse = {
  takeaway1: string;
  takeaway2: string;
  takeaway3: string;
  takeaway4: string;
};

export type TakeawaysForm = {
  text: string;
};

export type TakeawaysUnparsedForm = {
  text: string;
};

export type TakeawaysValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: TakeawaysForm };

export type TeamSubscription = {
  teamPortalCorporateId: number;
  teamPortalUrl: string;
  teamPortalUsername: string;
};

export type TermsAndConditionsForm = {
  accept: boolean;
  acceptRequired: boolean;
};

export type TermsAndConditionsUnparsedForm = {
  accept: boolean;
  acceptRequired: boolean;
};

export type TermsAndConditionsValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: TermsAndConditionsForm };

export type TmsBulkKeyResult = {
  message: string;
  success: boolean;
  txid: string;
};

export type TmsBulkKeyResults = {
  results: TmsBulkKeyResult[];
};

export type TmsBulkKeys = {
  comment: string;
  requestTranslations: boolean;
  txids: string[];
};

export type ToastState = {
  message: string;
  title: string | null;
  uuid: string;
  variant: ToastVariant;
};

export namespace ToastVariant {
  const values = ["primary", "success", "warning", "danger"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type ToastVariant = ToastVariant.Impl;

export namespace TransferProtocol {
  const values = ["FTP", "SFTP", "FTPS"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type TransferProtocol = TransferProtocol.Impl;

export type TranslatedAuthor = {
  biography: string;
  firstName: string | null;
  lastName: string;
  middleName: string | null;
};

export type TranslatedSummaryFields = {
  aboutTheAuthor: string;
  rationale: string;
  recommendation: string;
  subtitle: string;
  takeaways: string[];
  title: string;
  whatYouWillLearn: string[];
};

export type TranslatedSummaryMarkdown = {
  summaryMarkdown: string;
};

export type TranslationCheckResult = {
  findings: string[];
  passed: boolean;
};

export type Translations = {
  keys: { additionalProperties: Record<string, string> };
};

export type UnidentifiedCorporateCustomerFilterForm = {
  nameId: string | null;
};

export type UnidentifiedCorporateCustomerForm = {
  nameIds: string[];
  op: "DELETE";
};

export type UnidentifiedCorporateCustomerListView = {
  createdAt: DateTime<true>;
  info: string | null;
  nameId: string;
};

export type UserContext = {
  loggedIn: boolean;
  u: string | null;
};

export type UserGroup = {
  name: string;
  userGroupId: number;
};

export type UserGroupEditForm = {
  corporateId: number;
  defaultUserGroup: boolean;
  description: string | null;
  externalId: string | null;
  name: string;
  systemUserGroup: boolean;
  userGroupId: number;
};

export type UserGroupListView = {
  corporateId: number;
  defaultUserGroup: boolean;
  description: string | null;
  name: string;
  systemUserGroup: boolean;
  userGroupId: number;
};

export type UserGroupListViewResponse = {
  items: UserGroupListView[];
};

export type UserListView = {
  customerId: number;
  email: string;
  gender: number;
  name: string;
};

export type UserReportView = {
  customer: CustomerReportView;
  lastMobileUsage: DateTime<true> | null;
  lastWebUsage: DateTime<true> | null;
  pull: number;
  push: number;
};

export namespace UserState {
  const values = ["APPLICATION", "PROBATION", "REGULAR", "VETERAN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type UserState = UserState.Impl;

export type ValidationErrorReasons = {
  fields: { additionalProperties: Record<string, string[]> };
  general: string[];
};

export type VectorSearchPlaygroundDiagnostics = {
  nearMisses: VectorSearchPlaygroundNearMiss[];
  rerankCostUsd: number;
  rewriteCostUsd: number;
  rewrittenQuery: string | null;
};

export type VectorSearchPlaygroundNearMiss = {
  key: string;
  reason: string;
  score: number;
};

export type VectorSearchPlaygroundResponse = { kind: "ERROR"; message: string } | { kind: "SUCCESS"; diagnostics: VectorSearchPlaygroundDiagnostics; result: VectorSearchPlaygroundResult[] };

export type VectorSearchPlaygroundResult = {
  chunkText: string;
  content: ContentMetadata;
  contentLabel: string;
  llmRating: {
    reason: string;
    score: number;
  } | null;
  score: number;
};

export type VectorSearchProps = {
  actionablesId: number | null;
  dataId: number | null;
  docLimit: number;
  embeddingModel: EmbeddingModel;
  expertVideoId: number | null;
  scoreThresold: number;
  searchTerm: string;
  verifaiContentId: bigint | null;
};

export type VectorSearchResult = {
  result: {
    content: ContentMetadata;
    score: number;
    skill: string | null;
  }[];
};

export type Verb = {
  display: { additionalProperties: Record<string, string> };
  id: string;
};

export type VerifyBulkLoadForm = {
  corporateId: number | null;
  includeInactive: boolean;
  verifyIds: number[];
};

export type VerifyMetadataArray = {
  items: ContentMetadata_ONLINE_CONTENT[];
};

export type VivaLearningConnectorForm = {
  auth: AuthConnectorForm;
  common: CommonConnectorForm;
  connectorType: "VIVA_LEARNING";
  learningProviderId: string;
  tenantId: string;
  userIdentifier: ConnectorUserIdentifier;
};

export type VueDemoProps = {
  language: Language;
  subtitle: string;
};

export type VwdBulkLoadForm = {
  corporateId: number | null;
  keys: string[];
};

export type VwdBulkLoadItem = {
  coverUri: string;
  description: string | null;
  id: bigint;
  title: string;
  uuid: string;
};

export type VwdBulkLoadItemArray = {
  items: VwdBulkLoadItem[];
};

export type WarmupEitherForm_B2B = {
  corporateId: bigint;
  countryCode: string;
  dynamicFields: DynamicFields;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2B";
  language: Language;
  lastName: string;
  password: string | null;
  termsAndConditions: boolean;
};

export type WarmupEitherUnparsedForm_B2B = {
  corporateId: bigint;
  countryCode: string;
  dynamicFields: DynamicFields;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency | null;
  kind: "B2B";
  language: Language | null;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupB2bValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherForm_B2B };

export type WarmupEitherForm_B2C = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "B2C";
  language: Language;
  lastName: string;
  password: string | null;
  termsAndConditions: boolean;
};

export type WarmupEitherUnparsedForm_B2C = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency | null;
  kind: "B2C";
  language: Language | null;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupB2cValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherForm_B2C };

export type WarmupEitherForm =
  | {
      kind: "SSO";
      corporateId: bigint;
      countryCode: string;
      dynamicFields: DynamicFields;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      function: string | null;
      language: Language;
      lastName: string;
      password: string | null;
      ssoNameId: string;
      termsAndConditions: boolean;
      title: bigint | null;
      town: string | null;
    }
  | {
      kind: "B2B";
      corporateId: bigint;
      countryCode: string;
      dynamicFields: DynamicFields;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      language: Language;
      lastName: string;
      password: string | null;
      termsAndConditions: boolean;
    }
  | { kind: "TRIAL"; corporateId: bigint; countryCode: string; email: string; evrUuid: string; firstName: string; frequency: WeeklyEmailPushFrequency; language: Language; lastName: string; password: string; termsAndConditions: boolean }
  | {
      kind: "B2C";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency;
      language: Language;
      lastName: string;
      password: string | null;
      termsAndConditions: boolean;
    };

export type WarmupEitherUnparsedForm =
  | {
      kind: "SSO";
      corporateId: bigint;
      countryCode: string;
      dynamicFields: DynamicFields;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency | null;
      function: string | null;
      language: Language | null;
      lastName: string;
      password: string;
      ssoNameId: string;
      termsAndConditions: boolean;
      title: bigint | null;
      town: string | null;
    }
  | {
      kind: "B2B";
      corporateId: bigint;
      countryCode: string;
      dynamicFields: DynamicFields;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency | null;
      language: Language | null;
      lastName: string;
      password: string;
      termsAndConditions: boolean;
    }
  | {
      kind: "TRIAL";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency | null;
      language: Language | null;
      lastName: string;
      password: string;
      termsAndConditions: boolean;
    }
  | {
      kind: "B2C";
      corporateId: bigint;
      countryCode: string;
      email: string;
      evrUuid: string;
      firstName: string;
      frequency: WeeklyEmailPushFrequency | null;
      language: Language | null;
      lastName: string;
      password: string;
      termsAndConditions: boolean;
    };

export type WarmupEitherValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherForm };

export type WarmupExtraField =
  | { kind: "OPTION"; mandatory: boolean; options: { additionalProperties: Record<string, string> }; title: string; value: string | null }
  | { kind: "TEXT"; mandatory: boolean; title: string; value: string | null };

export type WarmupExtraFieldWrapper = {
  extraField: WarmupExtraField;
};

export type WarmupForm = {
  either: WarmupEitherForm;
};

export type WarmupInitResult = { kind: "INVALID_EVR_ERROR" } | { kind: "EXPIRED_EVR_ERROR" } | { kind: "UNKNOWN_FLOW_ERROR" } | { kind: "RELOAD_REQUIRED" } | { kind: "SUCCESS"; data: WarmupWidgetProps };

export type WarmupRelayType = { kind: "DEFAULT"; relayState: string } | { kind: "CONTENT"; contentMetadata: ContentMetadata; relayState: string };

export type WarmupEitherForm_SSO = {
  corporateId: bigint;
  countryCode: string;
  dynamicFields: DynamicFields;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  function: string | null;
  kind: "SSO";
  language: Language;
  lastName: string;
  password: string | null;
  ssoNameId: string;
  termsAndConditions: boolean;
  title: bigint | null;
  town: string | null;
};

export type WarmupEitherUnparsedForm_SSO = {
  corporateId: bigint;
  countryCode: string;
  dynamicFields: DynamicFields;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency | null;
  function: string | null;
  kind: "SSO";
  language: Language | null;
  lastName: string;
  password: string;
  ssoNameId: string;
  termsAndConditions: boolean;
  title: bigint | null;
  town: string | null;
};

export type WarmupSsoValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherForm_SSO };

export type WarmupSubmitOutcome = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; errors: string[]; messages: string[]; validatedForm: WarmupForm };

export type WarmupEitherForm_TRIAL = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency;
  kind: "TRIAL";
  language: Language;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupEitherUnparsedForm_TRIAL = {
  corporateId: bigint;
  countryCode: string;
  email: string;
  evrUuid: string;
  firstName: string;
  frequency: WeeklyEmailPushFrequency | null;
  kind: "TRIAL";
  language: Language | null;
  lastName: string;
  password: string;
  termsAndConditions: boolean;
};

export type WarmupTrialValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupEitherForm_TRIAL };

export type WarmupUnparsedForm = {
  either: WarmupEitherUnparsedForm;
};

export type WarmupValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WarmupForm };

export type WarmupWidgetProps = {
  corporationName: string;
  customTermsHtml: string | null;
  enforceAcceptanceTermsAndConditions: boolean;
  enforcePassword: boolean;
  enforceWarmupPushFrequency: boolean;
  extraField1: WarmupExtraFieldWrapper | null;
  extraField2: WarmupExtraFieldWrapper | null;
  extraField3: WarmupExtraFieldWrapper | null;
  form: WarmupUnparsedForm;
  relayType: WarmupRelayType;
};

export namespace WeeklyEmailPushFrequency {
  const values = ["UNSPECIFIED", "NONE", "ONCE_A_WEEK", "TWICE_A_MONTH", "ONCE_A_MONTH"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type WeeklyEmailPushFrequency = WeeklyEmailPushFrequency.Impl;

export type WhatYouWillLearn = {
  whatYouWillLearn: string[];
};

export type WhatYouWillLearnForm = {
  text: string;
};

export type WhatYouWillLearnResults = {
  entries: string[];
};

export type WhatYouWillLearnUnparsedForm = {
  text: string;
};

export type WhatYouWillLearnValidationResult = { kind: "ERROR"; reasons: ValidationErrorReasons } | { kind: "SUCCESS"; validatedForm: WhatYouWillLearnForm };

export type WishlistManagementData = {
  editorOptions: DropdownEntry[];
  list: WishlistManagementItemList;
};

export type WishlistManagementEditorUpdate = {
  managingEditorCustomerId: number | null;
};

export type WishlistManagementItem = {
  coverUri: string | null;
  dataId: number | null;
  description: string;
  detailInfo: string;
  isbn: string | null;
  krecId: number;
  language: Language;
  managingEditorCustomerId: number | null;
  rightsGranted: boolean;
  suggestedAt: DateTime<true>;
  tinyUrl: string | null;
  title: string;
};

export type WishlistManagementItemList = {
  items: WishlistManagementItem[];
};

export type WishlistManagementLanguageUpdate = {
  language: Language;
};

export type WorkdayConnectorForm = {
  accountId: string;
  apiKey: string;
  categoryFallback: string;
  common: CommonConnectorForm;
  connectorType: "WORKDAY";
  userIdentifier: ConnectorUserIdentifier;
};

export type Writer = {
  biographyHtml: string | null;
  customerId: number;
  firstName: string | null;
  lastName: string | null;
  profilePictureUri: string;
  writerId: number;
};

export namespace WriterState {
  const values = ["APPLICATION", "PROBATION", "REGULAR", "VETERAN"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type WriterState = WriterState.Impl;

export namespace XApiActorId {
  const values = ["MBOX", "ACCOUNT", "OPENID"] as const;
  export type Impl = (typeof values)[number];
  export function getValues(): typeof values {
    return values;
  }
}
export type XApiActorId = XApiActorId.Impl;

export type XApiStatement = {
  actor: Actor;
  object: Activity;
  result: Result;
  timestamp: string;
  verb: Verb;
};

export type ZonedDateTime = {
  datetime: string;
  timezone: string;
};
